import { createContext, useContext, useState, useEffect } from "react";
import esTranslations from "./translations-es";
import caTranslations from "./translations-ca";
import itTranslations from "./translations-it";
import ptTranslations from "./translations-pt";
import nlTranslations from "./translations-nl";
import { LANGUAGES } from "./languages";

const translations = {
  fr: {
    // Common
    "app.name": "Kintsugi TCC",
    "common.save": "Enregistrer",
    "common.cancel": "Annuler",
    "common.less": "en moins",
    "common.loading": "Chargement...",
    "common.back": "Retour",
    "common.next": "Suivant",
    "common.previous": "Précédent",
    "common.delete": "Supprimer",
    "common.edit": "Modifier",
    "common.or": "ou",
    "common.yes": "Oui",
    "common.no": "Non",

    // Auth
    "auth.login": "Se connecter",
    "auth.register": "Créer un compte",
    "auth.logout": "Déconnexion",
    "auth.email": "Email",
    "auth.password": "Mot de passe",
    "auth.confirmPassword": "Confirmer le mot de passe",
    "auth.fullName": "Nom complet",
    "auth.iAm": "Je suis...",
    "auth.therapist": "Thérapeute",
    "auth.patient": "Patient",
    "auth.therapistEmail": "Email de votre thérapeute (optionnel)",
    "auth.therapistEmailHint": "Votre thérapeute recevra un email pour activer votre compte",
    "auth.patientNote": "Note: Un thérapeute devra activer votre compte pour accéder aux exercices.",
    "auth.loginWith": "Se connecter avec Google",
    "auth.registerWith": "S'inscrire avec Google",
    "auth.noAccount": "Pas encore de compte?",
    "auth.hasAccount": "Déjà un compte?",
    "auth.createAccount": "Créer mon compte",
    "auth.creating": "Création...",
    "auth.connecting": "Connexion...",
    "auth.startJourney": "Commencez votre parcours thérapeutique",
    "auth.welcome": "Bienvenue",
    "auth.invalidCredentials": "Identifiants invalides",
    "auth.passwordMismatch": "Les mots de passe ne correspondent pas",
    "auth.passwordMinLength": "Le mot de passe doit contenir au moins 6 caractères",
    "auth.accountCreated": "Compte créé avec succès!",
    "auth.loginSuccess": "Connexion réussie!",
    "auth.accountPending": "Votre compte doit être activé par votre thérapeute.",
    "auth.forgotPasswordLink": "Mot de passe oublié ?",
    "auth.forgotPassword": "Mot de passe oublié",
    "auth.forgotPasswordDesc": "Entrez votre adresse email et nous vous enverrons un lien pour reinitialiser votre mot de passe.",
    "auth.sendResetLink": "Envoyer le lien",
    "auth.resetEmailSent": "Email envoye !",
    "auth.resetEmailSentDesc": "Si un compte existe avec cette adresse email, vous recevrez un lien de reinitialisation dans quelques instants. Pensez a verifier vos spams.",
    "auth.resetEmailNotReceived": "Vous n'avez pas recu l'email ? Verifiez vos spams ou renvoyez le lien.",
    "auth.resendEmail": "Renvoyer l'email",
    "auth.backToLogin": "Retour a la connexion",
    "auth.newPassword": "Nouveau mot de passe",
    "auth.newPasswordDesc": "Choisissez un nouveau mot de passe pour votre compte.",
    "auth.passwordPolicy": "Le mot de passe doit contenir au moins 8 caracteres, une majuscule, une minuscule et un chiffre.",
    "auth.passwordReset": "Mot de passe reinitialise !",
    "auth.passwordResetDesc": "Votre mot de passe a ete reinitialise avec succes. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
    "auth.resetPassword": "Reinitialiser le mot de passe",

    // Navigation
    "nav.dashboard": "Tableau de bord",
    "nav.dersQuestionnaire": "Questionnaire DERS",
    "nav.sexualityTcc": "TCC Sexualité",
    "nav.myDoneExercises": "Mes exercices réalisés",
    "nav.statistics": "Statistiques",
    "nav.administration": "Administration",
    "nav.patients": "Patients",
    "nav.sessionReports": "Comptes rendus",
    "nav.exercises": "Exercices",
    "nav.reminders": "Rappels",
    "nav.profile": "Mon Profil",
    "app.refresh": "Actualiser l'application",
    "app.refresh_hint": "Recharge la dernière version de l'application sans vous déconnecter",
    "app.refresh_now": "Actualiser",
    "app.update_available": "Nouvelle version disponible",
    "nav.exercisesSection": "Exercices",
    "nav.cbtExercises": "Exercices TCC",
    "nav.barkleyParents": "Barkley — Parents",
    "nav.barkleyChildren": "Barkley — Enfants",
    "nav.barkleyTeens": "Barkley — Ados",
    "nav.myBadges": "Mes Badges",
    "nav.barkleyParent": "TDAH Enfant - Guide Parental",
    "nav.barkleyChild": "TDAH Enfant - Exercices (6-12 ans)",
    "nav.barkleyTeen": "Exercices Ados (12-17 ans)",
    "nav.tdahKidsWorkbook": "Carnet Galactique TDAH",
    "nav.psychoed": "Psychoéducation",
    "patient.privacyBanner": "Vos exercices sont privés par défaut. Après chaque exercice, vous pouvez choisir de le partager avec votre thérapeute.",
    "patient.manageSharing": "Gérer mes partages",
    "badge.premier_pas": "Premier Pas",
    "badge.explorateur": "Explorateur",
    "badge.super_heros_tdah": "Super-Héros TDAH",
    "badge.gardien_jardin": "Gardien du Jardin",
    "badge.quiz_master": "Quiz Master",
    "badge.regulier": "Régulier",
    "badge.champion": "Champion",
    "badge.collectionneur": "Collectionneur",
    "badge.genereux": "Généreux",
    "badge.earned": "Badges gagnés",
    "badge.toUnlock": "Badges à débloquer",
    "badge.noBadges": "Pas encore de badge",
    "badge.noBadgesDesc": "Complète des exercices pour débloquer tes premiers badges ! Chaque badge représente une nouvelle étape de ton parcours.",
    "parentTask.title": "Tableau de Missions",
    "parentTask.todayMissions": "Missions du jour",
    "parentTask.totalMissions": "Missions totales",
    "parentTask.badgesEarned": "Badges gagnés",
    "parentTask.streak": "Jours de suite",
    "parentTask.missionBadges": "Badges de Mission",
    "parentTask.nextBadge": "Prochain badge",
    "parentTask.apprentice": "Apprenti Astronaute",
    "parentTask.pilot": "Pilote Spatial",
    "parentTask.commander": "Commandant Galactique",
    "parentTask.hero": "Héros de l'Espace",
    "parentTask.legend": "Légende Cosmique",
    "parentTask.master": "Maître de l'Univers",
    "parentTask.subtitle": "Complète tes missions pour gagner des badges !",
    "parentTask.missionComplete": "Mission accomplie !",
    "parentTask.newBadge": "Nouveau Badge !",
    "parentTask.allDone": "Toutes les missions du jour sont terminées !",
    "parentTask.addMission": "Ajouter une mission...",
    "parentTask.thisWeek": "Cette semaine",
    "parentTask.missions": "missions",
    "parentTask.great": "Super !",
    "badge.new": "Nouveau !",
    "badge.newBadgeUnlocked": "Nouveau badge débloqué !",
    "badge.newBadgesUnlocked": "{count} nouveaux badges débloqués !",
    "common.viewAll": "Voir tout",
    "common.errorGeneric": "Erreur",
    "common.saving": "Sauvegarde...",
    "checklist.title": "Checklist du Matin",
    "checklist.myTasks": "Mes tâches du matin",
    "checklist.dailyNotes": "Notes du jour",
    "checklist.notesPlaceholder": "Comment s'est passé le matin ? (optionnel)",
    "checklist.saved": "Checklist sauvegardée !",
    "checklist.errorSaving": "Erreur lors de la sauvegarde",
    "checklist.addTask": "Ajouter une tâche...",
    "checklist.tasks": "tâches",
    "checklist.history": "Historique (14 derniers jours)",
    "checklist.noHistory": "Aucun historique pour le moment",
    "checklist.perfect": "Parfait !",
    "checklist.great": "Très bien !",
    "checklist.keepGoing": "Continue !",
    "checklist.courage": "Courage !",
    "install.title": "Installer Kintsugi TCC",
    "install.subtitle": "Accès rapide depuis votre écran d'accueil",
    "install.howTo": "Comment faire",
    "install.install": "Installer",
    "install.screenTitle": "Installer sur votre écran",
    "install.step1": "Appuyez sur le bouton",
    "install.share": "Partager",
    "install.step1b": "en bas de Safari",
    "install.step2": "Faites défiler et appuyez sur",
    "install.addToHome": "Sur l'écran d'accueil",
    "install.step3": "Appuyez sur",
    "install.add": "Ajouter",
    "install.step3b": "en haut à droite",
    "install.understood": "Compris",

    // Session Reports
    "sessions.title": "Comptes rendus de séance",
    "sessions.subtitle": "Gérez vos comptes rendus de séance par patient",
    "sessions.newReport": "Nouveau compte rendu",
    "sessions.editReport": "Modifier le compte rendu",
    "sessions.createFirst": "Créer votre premier compte rendu",
    "sessions.noReports": "Aucun compte rendu de séance",
    "sessions.backToList": "Retour",
    "sessions.patient": "Patient",
    "sessions.selectPatient": "Sélectionner un patient",
    "sessions.allPatients": "Tous les patients",
    "sessions.sessionType": "Type de séance",
    "sessions.typeInitial": "Entretien initial",
    "sessions.typeSuivi": "Séance de suivi TCC",
    "sessions.typeAdo": "Suivi TCC enfants & ados (12-17 ans)",
    "ado.schoolStress.title": "Mon journal de stress scolaire",
    "ado.schoolStress.desc": "Chaque jour de classe, 2 minutes : ton stress, ce qui s'est passé, ce que tu t'es dit, ce qui t'a aidé.",
    "ado.exposurePlan.title": "Mon plan de défis en classe",
    "ado.exposurePlan.desc": "Une échelle de défis du plus facile au plus difficile, avec ton anxiété avant / après.",
    "psychoed.recommendedExercises": "Exercices recommandés",
    "psychoed.recommendedExercisesHint": "Pour mettre en pratique ce module. Votre thérapeute peut aussi vous les assigner.",
    "psychoed.newTag": "spécifique",
    "nav.perfectionism": "Perfectionnisme et excellencisme",
    "perfScale.navTitle": "Questionnaire perfectionnisme",
    "perfScale.subtitle": "20 questions · 5 minutes · suivi de votre évolution dans le temps",
    "perfScale.history": "Mon évolution",
    "perfScale.pending": "Votre thérapeute vous invite à remplir ce questionnaire",
    "perfScale.dim1": "Excellencisme : viser très haut avec souplesse.",
    "perfScale.dim2": "Standards perfectionnistes : exiger le sans-faute.",
    "perfScale.dim3": "Préoccupations perfectionnistes : peur de l'erreur, doute, auto-critique.",
    "perfScale.start": "Commencer",
    "perfScale.startAgain": "Refaire le questionnaire",
    "perfScale.notePlaceholder": "Note facultative (contexte du moment : examens, surcharge, vacances…)",
    "perfScale.submit": "Voir mes résultats",
    "perfScale.saved": "Questionnaire enregistré",
    "perfScale.needTwo": "Remplissez le questionnaire au moins deux fois pour voir votre évolution.",
    "perfScale.lastResult": "Dernier résultat",
    "perfScale.allResults": "Toutes les passations",
    "perfScale.yourResult": "Votre résultat",
    "perfScale.concerns": "Préoccupations",
    "perfScale.disclaimer": "Outil de suivi non normé, inspiré du modèle de l'excellencisme et du perfectionnisme (Gaudreau) et de la FMPS (Frost). Il ne constitue pas un diagnostic ; parlez-en avec votre thérapeute.",
    "perfScale.trend": "Évolution dans le temps",
    "perfScale.trendHint": "Objectif : excellencisme stable ou en hausse, standards et préoccupations perfectionnistes en baisse.",
    "psychoed.scaleTag": "questionnaire",
    "perf.journal.title": "Journal du perfectionnisme",
    "perf.journal.desc": "Situation, standard exigé, pensée « je dois », comportement, coût, puis version excellenciste du standard.",
    "perf.goodEnough.title": "Expérience « suffisamment bon »",
    "perf.goodEnough.desc": "Fixez un critère à 80 %, prédisez, réalisez, comparez : une exposition à l'imperfection.",
    "perf.costBenefit.title": "Analyse coûts-bénéfices du perfectionnisme",
    "perf.costBenefit.desc": "Bénéfices et coûts à court et long terme, en quatre cases, puis conclusion personnelle.",
    "perf.continuum.title": "Continuum des standards",
    "perf.continuum.desc": "Placez vos exigences de 0 à 100 et délimitez la zone d'excellence et la zone de perfection.",
    "psychoed.perfectionism.title": "Perfectionnisme et excellencisme",
    "psychoed.perfectionismAdo.title": "Perfectionnisme et excellence (12-17 ans)",
    "psychoed.perfectionismAdo.subtitle": "Viser haut sans te mettre la pression : comprendre le perfectionnisme et devenir « excellenciste »",
    "nav.grief": "Deuil",
    "library.grief": "Deuil (TCC)",
    "library.psychoedGrief": "Psychoéducation Deuil",
    "psychoed.grief.title": "Le processus du deuil",
    "psychoed.grief.subtitle": "Comprendre le deuil, reconnaître quand il se complique et avancer avec l'approche TCC",
    "psychoed.griefAdo.title": "Vivre un deuil (12-17 ans)",
    "psychoed.griefAdo.subtitle": "Comprendre ce qui se passe en toi quand tu perds quelqu'un, et trouver comment avancer sans oublier",
    "grief.memoryJournal.title": "Journal des souvenirs",
    "grief.memoryJournal.desc": "Exposition graduée aux souvenirs : souvenir choisi, récit détaillé, émotion avant/après, ce que ce souvenir dit du lien.",
    "grief.letter.title": "Lettre à la personne disparue",
    "grief.letter.desc": "Écrire ce qui n'a pas été dit : regrets, gratitude, colère, nouvelles de votre vie, puis la réponse imaginée.",
    "grief.avoidance.title": "Hiérarchie des situations évitées",
    "grief.avoidance.desc": "Lister ce que vous évitez depuis le décès, coter la difficulté, planifier une confrontation graduée et noter le résultat.",
    "grief.reengagement.title": "Plan de réengagement",
    "grief.reengagement.desc": "Chaque semaine, une valeur, une activité concrète, les obstacles anticipés et le bilan.",
    "griefScale.navTitle": "Échelle de deuil",
    "griefScale.subtitle": "13 questions · 3 minutes · suivi de votre évolution dans le temps",
    "griefScale.history": "Mon évolution",
    "griefScale.pending": "Votre thérapeute vous invite à remplir cette échelle",
    "griefScale.dim1": "Détresse de séparation : manque, préoccupation, solitude.",
    "griefScale.dim2": "Détresse émotionnelle et évitement : acceptation, colère, culpabilité, évitement, perte de sens.",
    "griefScale.dim3": "Retentissement sur la vie : activités et relations.",
    "griefScale.monthsLabel": "Depuis combien de mois la perte a-t-elle eu lieu ? (facultatif)",
    "griefScale.start": "Commencer",
    "griefScale.startAgain": "Refaire l'échelle",
    "griefScale.submit": "Voir mes résultats",
    "griefScale.saved": "Échelle enregistrée",
    "griefScale.notePlaceholder": "Note facultative (contexte du moment : anniversaire, fêtes, démarches…)",
    "griefScale.yourResult": "Votre résultat",
    "griefScale.total": "Score total",
    "griefScale.level": "Niveau de chagrin",
    "griefScale.prolongedFlag": "Plus de six mois après la perte avec un chagrin intense qui retentit sur la vie : parlez-en avec votre thérapeute, un travail spécifique sur le deuil peut aider.",
    "griefScale.disclaimer": "Outil de suivi non normé, inspiré des critères du deuil prolongé (CIM-11, DSM-5-TR) et des échelles PG-13 / ICG (Prigerson). Il ne constitue pas un diagnostic ; un chagrin intense est normal, surtout dans les premiers mois.",
    "griefScale.trend": "Évolution dans le temps",
    "griefScale.trendHint": "Objectif : une baisse progressive des trois dimensions, en particulier du retentissement sur la vie.",
    "griefScale.needTwo": "Remplissez l'échelle au moins deux fois pour voir votre évolution.",
    "griefScale.lastResult": "Dernier résultat",
    "griefScale.allResults": "Toutes les passations",
    "griefScale.level_low": "faible",
    "griefScale.level_moderate": "modéré",
    "griefScale.level_high": "élevé",
    "psychoed.perfectionism.subtitle": "Viser haut sans s'épuiser : comprendre le perfectionnisme et cultiver l'excellence",
    "psychoed.perfectionism.desc": "Module en 10 chapitres : perfectionnisme adaptatif/inadapté, excellencisme (Gaudreau), cercle vicieux, distorsions, coûts-bénéfices, comportements de sécurité, auto-compassion, plan de maintien.",
    "library.adoTeen": "Suivi TCC Ados (12-17 ans)",
    "library.perfectionism": "Perfectionnisme et excellencisme",
    "library.psychoedPerfectionism": "Psychoéducation Perfectionnisme",
    "sessions.field.contexte_familial_scolaire": "Contexte familial & scolaire",
    "sessions.field.implication_parents": "Implication des parents",
    "sessions.field.developpement_motivation": "Développement / motivation",
    "cr.contexte_familial_scolaire": "Contexte familial & scolaire",
    "cr.implication_parents": "Implication des parents",
    "cr.developpement_motivation": "Développement / motivation",
    "auth.createPassword": "Créez votre mot de passe",
    "auth.createPasswordDesc": "Bienvenue sur Kintsugi TCC ! Choisissez un mot de passe pour activer votre accès personnel.",
    "auth.accountCreated": "Votre accès est prêt",
    "auth.accountCreatedDesc": "Votre mot de passe est enregistré. Connectez-vous avec votre adresse e-mail pour retrouver vos exercices et votre suivi.",
    "sessions.typeCouple": "Séance de thérapie de couple",
    "sessions.date": "Date de séance",
    "sessions.save": "Enregistrer",
    "sessions.update": "Mettre à jour",
    "sessions.edit": "Modifier",
    "sessions.delete": "Supprimer",
    "sessions.created": "Compte rendu créé !",
    "sessions.updated": "Compte rendu mis à jour !",
    "sessions.deleted": "Compte rendu supprimé",
    "sessions.confirmDelete": "Voulez-vous vraiment supprimer ce compte rendu ?",
    "sessions.search": "Rechercher...",
    "sessions.exportPdf": "Exporter en PDF",
    "sessions.pdfExported": "PDF exporté avec succès !",
    "sessions.selectPatientFirst": "Sélectionnez un patient pour gérer les exercices",
    "sessions.aiSuggestions": "Suggestions IA — cliquez pour valider",
    "sessions.pendingExercises": "Exercices en cours",
    "sessions.completedExercises": "Exercices complétés",
    "sessions.exercisesToAssign": "Exercices à assigner avec ce CR",
    "sessions.browseCatalog": "Parcourir le catalogue d'exercices",
    "sessions.searchExercises": "Rechercher un exercice...",

    // Document import for report generation
    "docImport.title": "Générer le CR par IA à partir d'un document",
    "docImport.upload": "Importer un fichier",
    "docImport.formats": "Formats acceptés : TXT, PDF (max 10 Mo)",
    "docImport.generate": "Générer le compte rendu",
    "docImport.generating": "Analyse et structuration du compte rendu par l'IA...",
    "docImport.generated": "Compte rendu généré avec succès !",
    "docImport.error": "Erreur",
    "docImport.patience": "Cela peut prendre quelques secondes",
    "docImport.invalidFormat": "Format non supporté. Formats acceptés : TXT, PDF",
    "docImport.fileTooLarge": "Le fichier dépasse la limite de 10 Mo",

    "sessions.field.informations_generales": "Informations générales",
    "sessions.field.anamnese": "Anamnèse",
    "sessions.field.evaluation_dsm5": "Évaluation psychopathologique selon le DSM-5",
    "sessions.field.hypothese_diagnostic_principal": "Hypothèse diagnostic principal suspecté",
    "sessions.field.hypotheses_diagnostics_differentiels": "Hypothèses diagnostics différentiels à explorer",
    "sessions.field.hypothese_clinique_generale": "Hypothèse clinique générale",
    "sessions.field.problematiques": "Problématiques identifiées",
    "sessions.field.protocole_therapeutique": "Protocole thérapeutique intégré TCC 2e et 3e vagues",
    "sessions.field.processus_cibles": "Processus ciblés",
    "sessions.field.strategies_therapeutiques": "Stratégies thérapeutiques",
    "sessions.field.evaluations_standardisees": "Évaluations standardisées à administrer",
    "sessions.field.objectifs_therapeutiques": "Objectifs thérapeutiques",
    "sessions.field.recommandations_suivi": "Recommandations pour le suivi",
    "sessions.field.notes_libres": "Notes libres (optionnel)",
    "sessions.field.exercices": "Exercices",
    "sessions.field.agenda_objectifs": "Agenda / objectifs de séance",
    "sessions.field.ameliorations_progres": "Améliorations / progrès",
    "sessions.field.revue_semaine": "Revue de la semaine / événements clés",
    "sessions.field.humeur_symptomes": "Humeur / symptômes",
    "sessions.field.echelles_administrees": "Échelles administrées",
    "sessions.field.revue_exercices": "Revue des exercices",
    "sessions.field.analyse_chaine": "Analyse en chaîne",
    "sessions.field.analyse_chaine_abc": "Analyse en chaîne (ABC)",
    "sessions.field.analyse_chaine_sorc": "Analyse en chaîne (SORC)",
    "sessions.field.situation_pensees_emotions": "Situation - Pensées - Émotions - Comportements - Conséquences",
    "sessions.field.pensees_automatiques": "Pensées automatiques / croyances",
    "sessions.field.analyse_tcc": "Analyse TCC / points clés",
    "sessions.field.interventions": "Interventions",
    "sessions.field.experiences_comportementales": "Expériences comportementales / exposition",
    "sessions.field.nouveaux_exercices": "Nouveaux exercices / tâches",
    "sessions.field.objectifs_smart": "Objectifs SMART / ajustement",
    "sessions.field.plan_prochaine_seance": "Plan / prochaine séance",

    // Dashboard - Therapist
    "therapist.hello": "Bonjour,",
    "therapist.overview": "Voici un aperçu de votre activité",
    "therapist.activePatients": "Patients actifs",
    "therapist.totalPatients": "Total patients",
    "therapist.pendingExercises": "Exercices en attente",
    "therapist.completedToday": "Complétés aujourd'hui",
    "therapist.quickActions": "Actions rapides",
    "therapist.managePatients": "Gérer les patients",
    "therapist.exerciseLibrary": "Bibliothèque d'exercices",
    "therapist.reminders": "Rappels",
    "therapist.recentPatients": "Patients récents",
    "therapist.viewAll": "Voir tous",
    "therapist.exerciseOverview": "Résumé des exercices par patient",
    "therapist.searchPatient": "Rechercher un patient...",
    "therapist.noPatientFound": "Aucun patient trouvé",
    "therapist.showMore": "Voir plus",
    "therapist.showLess": "Voir moins",
    "therapist.exerciseReview": "Revue des exercices",
    "therapist.totalEntries": "Total entrées",
    "therapist.activeTypes": "Types actifs",
    "therapist.completed": "Complétés",
    "therapist.pending": "En cours",
    "therapist.searchExerciseType": "Rechercher un type d'exercice...",
    "therapist.noExercisesFound": "Aucun exercice trouvé",
    "therapist.noExerciseData": "Ce patient n'a pas encore d'exercices",
    "therapist.allAssignments": "Tous les exercices assignés",
    "therapist.entries": "entrées",
    "therapist.types": "types",
    "therapist.pending": "En attente",

    // Dashboard - Patient
    "patient.hello": "Bonjour,",
    "patient.howAreYou": "Comment vous sentez-vous aujourd'hui?",
    "patient.consecutiveDays": "Jours consécutifs",
    "patient.moodEntries": "Entrées d'humeur",
    "patient.completedExercises": "Exercices complétés",
    "patient.pendingExercises": "Exercices en attente",
    "patient.moodEvolution": "Évolution de votre humeur",
    "patient.recentEmotions": "Émotions récentes:",
    "patient.yourProgress": "Votre progression",
    "patient.exercisesCompleted": "Exercices complétés",
    "patient.completedOf": "Vous avez complété {completed} exercices sur {total} assignés.",
    "patient.toDo": "À faire",
    "patient.allDone": "Tout est à jour!",
    "patient.yourExercises": "Vos exercices",
    "patient.noExercisesAssigned": "Aucun exercice ne vous a encore été assigné. Votre thérapeute vous en attribuera prochainement.",
    "patient.startExercise": "Commencer",
    "patient.exerciseProgress": "Progression par exercice",
    "patient.assigned": "Assignés",
    "patient.completed": "Complétés",
    "patient.standalone": "Entrées libres",
    "patient.noProgress": "Commencez vos exercices pour voir votre progression ici.",

    // Exercise names
    "exercise.mood": "Suivi d'humeur",
    "exercise.mood.desc": "Enregistrez votre état émotionnel",
    "exercise.emotion_register": "Émotions",
    "exercise.emotion_register.desc": "Régulation émotionnelle par le questionnement socratique",
    "exercise.gratitude": "Gratitude",
    "exercise.gratitude.desc": "Cultivez la reconnaissance",
    "exercise.pomodoro": "Pomodoro",
    "exercise.pomodoro.desc": "Restez concentré et productif",
    "exercise.meditation": "Méditation",
    "exercise.meditation.desc": "Pratiquez la pleine conscience",
    "exercise.cognitive_defusion": "Défusion cognitive",
    "exercise.cognitive_defusion.desc": "Détachez-vous de vos pensées",
    "exercise.self_compassion": "Auto-compassion",
    "exercise.self_compassion.desc": "Soyez bienveillant avec vous-même",
    "exercise.behavioral_activation": "Activation",
    "exercise.behavioral_activation.desc": "Planifiez des activités positives",
    "exercise.values_exploration": "Valeurs (ACT)",
    "exercise.values_exploration.desc": "Explorez vos valeurs fondamentales",
    "exercise.needs_exploration": "Besoins (Maslow)",
    "exercise.needs_exploration.desc": "Identifiez vos besoins essentiels",
    "exercise.problem_solving": "Résolution",
    "exercise.problem_solving.desc": "Résolvez vos difficultés",
    "exercise.qualities_exploration": "Qualités",
    "exercise.qualities_exploration.desc": "Renforcez votre estime de soi",
    "exercise.capabilities_exploration": "Capacités",
    "exercise.capabilities_exploration.desc": "Développez votre confiance",
    "exercise.assertiveness": "Affirmation de soi",
    "exercise.assertiveness.desc": "Améliorez votre communication",
    "exercise.self_reinforcement": "Auto-renforcement",
    "exercise.self_reinforcement.desc": "Célébrez vos accomplissements",

    // Profile
    "profile.title": "Mon Profil",
    "profile.subtitle": "Gérez vos informations personnelles",
    "profile.accountInfo": "Informations du compte",
    "profile.accountDesc": "Votre rôle et email",
    "profile.editProfile": "Modifier le profil",
    "profile.setPassword": "Définir un mot de passe",
    "profile.changePassword": "Modifier le mot de passe",
    "profile.setPasswordDesc": "Définissez un mot de passe pour vous connecter avec votre email en plus de Google",
    "profile.changePasswordDesc": "Changez votre mot de passe actuel",
    "profile.googleNotice": "Vous vous êtes inscrit(e) via Google. Définissez un mot de passe pour pouvoir aussi vous connecter avec votre email et mot de passe.",
    "profile.newPassword": "Nouveau mot de passe",
    "profile.confirmPassword": "Confirmer le mot de passe",
    "profile.passwordSet": "Mot de passe défini !",
    "profile.passwordChanged": "Mot de passe modifié !",
    "profile.profileUpdated": "Profil mis à jour !",
    "profile.role": "Rôle",
    "profile.appearance": "Apparence",
    "profile.language": "Langue",
    "profile.darkMode": "Mode sombre",
    "profile.lightMode": "Mode clair",
    "profile.autoMode": "Auto (système)",
    "profile.french": "Français",
    "profile.english": "English",
    "profile.spanish": "Español",

    // Landing
    "landing.hero": "Votre espace de thérapie cognitive et comportementale",
    "landing.heroSub": "Des exercices TCC guidés, un suivi personnalisé et un accompagnement adapté à votre parcours thérapeutique.",
    "landing.getStarted": "Commencer",
    "landing.learnMore": "En savoir plus",
    "landing.heroTitle": "Exercices TCC pour vos patients",
    "landing.heroDesc": "Une plateforme complète pour les thérapeutes praticiens des Thérapies Cognitives et Comportementales. Partagez des exercices personnalisés avec vos patients et suivez leur progression.",
    "landing.tryFree": "Essayer gratuitement",
    "landing.assignExercises": "Assignez des exercices à vos patients",
    "landing.trackProgress": "Suivez leur progression",
    "landing.evidenceBased": "Outils TCC basés sur les preuves",
    "landing.secureConfidential": "Sécurisé et confidentiel",
    "landing.moodTracking": "Suivi émotionnel",
    "landing.exerciseCount": "16 exercices TCC",
    "landing.completeTools": "Outils thérapeutiques complets",
    "landing.completeToolsDesc": "Une bibliothèque d'exercices basés sur les thérapies cognitives et comportementales, l'ACT et la pleine conscience.",
    "landing.cta": "Créez votre compte thérapeute",
    "landing.ctaDesc": "Commencez votre essai gratuit de 14 jours. Aucune carte bancaire requise.",
    "landing.ctaButton": "Commencer gratuitement",
    "landing.howItWorks": "Comment ça fonctionne",
    "landing.step1Title": "Créez votre compte thérapeute",
    "landing.step1Desc": "Inscrivez-vous en tant que thérapeute pour accéder à tous les outils.",
    "landing.step2Title": "Ajoutez vos patients",
    "landing.step2Desc": "Invitez vos patients et activez leur compte pour leur donner accès.",
    "landing.step3Title": "Assignez des exercices",
    "landing.step3Desc": "Choisissez parmi notre bibliothèque d'exercices TCC et personnalisez-les.",
    "landing.step4Title": "Suivez la progression",
    "landing.step4Desc": "Consultez les réponses de vos patients et leur évolution.",
    "landing.readyToStart": "Prêt à commencer?",
    "landing.readyDesc": "Rejoignez les thérapeutes qui utilisent déjà Kintsugi TCC pour accompagner leurs patients.",
    "landing.createFreeAccount": "Créer un compte gratuit",
    "landing.footer": "© 2026 Kintsugi TCC — VMLH-TCC EURL. Tous droits réservés.",
    "landing.privacy": "Politique de confidentialité",
    "landing.features": "Fonctionnalités",
    "landing.pricing": "Tarifs",
    "landing.soloTitle": "Vous n'êtes pas suivi par un thérapeute ?",
    "landing.soloDesc": "Accédez en autonomie à tous les exercices TCC et au programme Barkley TDAH. Sans engagement, résiliez quand vous voulez.",
    "landing.soloPrice": "9€/mois",
    "landing.soloCTA": "M'inscrire en autonomie",
    "landing.soloFeature1": "16+ exercices TCC guidés",
    "landing.soloFeature2": "Programme Barkley TDAH complet",
    "landing.soloFeature3": "Suivi de l'humeur et des progrès",
    "landing.soloFeature4": "Psychoéducation interactive",
    "landing.soloWarning": "Bien que les exercices soient accessibles en autonomie, un accompagnement par un praticien formé en TCC est vivement recommandé pour un suivi optimal et personnalisé. L'utilisation seule de ces outils ne remplace pas une prise en charge thérapeutique professionnelle.",

    // Charter
    "charter.acceptLabel": "J'ai lu et j'accepte la",
    "charter.linkLabel": "Charte du Secret Professionnel",
    "charter.acceptSuffix": " et m'engage à respecter la confidentialité des données de mes patients.",
    "charter.badgeTooltip": "Charte du secret professionnel acceptée",
    "charter.myTherapist": "Mon thérapeute",
    "charter.bannerText": "Veuillez accepter la Charte du Secret Professionnel pour afficher le badge de confiance à vos patients.",
    "charter.acceptBtn": "Accepter la charte",
    "charter.readCharter": "Lire la charte complète",

    // Owner invitation & Referral
    "owner.inviteTitle": "Inviter un(e) confrère/consoeur",
    "owner.inviteDesc": "Créez un lien d'invitation pour un(e) collègue thérapeute. Cette personne bénéficiera d'un accès gratuit sans abonnement. Chaque thérapeute gère ses propres patients de manière indépendante et confidentielle.",
    "owner.colleagueName": "Nom du confrère/consoeur",
    "owner.createInvite": "Créer l'invitation",
    "owner.inviteCopied": "Lien d'invitation copié !",
    "owner.invitationsList": "Invitations envoyées",
    "owner.used": "Utilisée",
    "owner.copyLink": "Copier",
    "referral.title": "Programme de parrainage",
    "referral.desc": "Parrainez des confrères et gagnez 1 mois gratuit pour chaque 10 thérapeutes parrainés qui s'abonnent (mensuel ou annuel avec engagement).",
    "referral.copyLink": "Copier le lien",
    "referral.copied": "Lien de parrainage copié !",
    "referral.totalReferred": "Parrainés",
    "referral.paying": "Abonnés",
    "referral.freeMonths": "Mois gratuits",
    "referral.progress": "Prochain bonus",
    "auth.ownerInvite": "Invitation confrère/consoeur",
    "auth.ownerInviteNote": "Vous êtes invité(e) par le propriétaire de la plateforme. Votre accès sera gratuit.",

    // Kintsugi & TCC section
    "landing.kintsugi.sectionTitle": "Pourquoi Kintsugi ?",
    "landing.kintsugi.subtitle": "L'art de se reconstruire, sublimé par la thérapie",
    "landing.kintsugi.paragraph1": "Le Kintsugi est un art japonais ancestral qui consiste à réparer les céramiques brisées en soulignant les fissures avec de l'or. Loin de cacher les blessures, cette philosophie les transforme en quelque chose de plus beau et de plus précieux qu'avant.",
    "landing.kintsugi.paragraph2": "C'est exactement la démarche de la Thérapie Cognitive et Comportementale. La TCC ne cherche pas à effacer vos difficultés, mais à vous aider à les comprendre, à les traverser, et à en ressortir plus fort. Chaque fissure réparée devient une ligne d'or — la preuve de votre résilience.",
    "landing.kintsugi.value1Title": "Accepter ses fissures",
    "landing.kintsugi.value1Desc": "Comme le Kintsugi, la TCC commence par reconnaître la souffrance sans la nier ni la minimiser.",
    "landing.kintsugi.value2Title": "Réparer avec soin",
    "landing.kintsugi.value2Desc": "Des exercices structurés et validés scientifiquement pour reconstruire, étape par étape, ses schémas de pensée.",
    "landing.kintsugi.value3Title": "Révéler sa force",
    "landing.kintsugi.value3Desc": "Le résultat n'est pas un retour à l'état initial, mais une version plus forte, plus consciente et plus résiliente de soi-même.",

    // Exercise pages common
    "exercise.newEntry": "Nouvelle entrée",
    "exercise.history": "Historique",
    "exercise.previousEntries": "Entrées précédentes",
    "exercise.previousExercises": "Exercices Précédents",
    "exercise.previousExplorations": "Explorations Précédentes",
    "exercise.noEntries": "Aucune entrée pour le moment",
    "exercise.save": "Enregistrer",
    "exercise.saving": "Enregistrement...",
    "exercise.saved": "Enregistré !",
    "exercise.completed": "Terminé",
    "exercise.pending": "En attente",
    "exercise.responses": "Réponse(s)",
    "exercise.noResponse": "Aucune réponse soumise",
    "exercise.entries": "entrée(s)",
    "exercise.downloadPDF": "Télécharger PDF",
    "auth.authError": "Erreur d'authentification",
    "auth.authenticating": "Authentification en cours...",
    "reinforcement.saved": "Accomplissement enregistré !",
    "psychoeducation.previous": "Précédent",
    "psychoeducation.next": "Suivant",
    "exercise.reflection": "Réflexion (optionnel)",
    "exercise.delete": "Supprimer",
    "exercise.deleted": "Entrée supprimée",

    // Mood tracker
    "mood.title": "Suivi de l'humeur",
    "mood.subtitle": "Enregistrez votre état émotionnel quotidien",
    "mood.howFeel": "Comment vous sentez-vous?",
    "mood.veryBad": "Très mal",
    "mood.veryGood": "Très bien",
    "mood.whatEmotions": "Quelles émotions ressentez-vous?",
    "mood.otherEmotion": "Autre émotion ou sentiment...",
    "mood.notes": "Notes (optionnel)",
    "mood.notesPlaceholder": "Comment s'est passée votre journée?",
    "mood.record": "Enregistrer mon humeur",
    "mood.recording": "Enregistrement...",
    "mood.recorded": "Humeur enregistrée!",

    // Thought record
    "thought.title": "Registre d'émotions",
    "thought.subtitle": "Régulation émotionnelle par le questionnement socratique",
    "thought.newRecord": "Nouvel enregistrement",
    "thought.situation": "Situation déclenchante",
    "thought.situationPlaceholder": "Décrivez la situation qui a provoqué cette émotion...",
    "thought.automaticThoughts": "Émotion ressentie",
    "thought.automaticThoughtsPlaceholder": "Décrivez ce que vous ressentez...",
    "thought.emotions": "Type d'émotion",
    "thought.basicEmotions": "Émotions de base",
    "thought.commonEmotions": "Autres émotions courantes",
    "thought.customEmotion": "Autre émotion ou sentiment...",
    "thought.intensity": "Intensité émotionnelle",
    "thought.evidenceFor": "Preuves pour",
    "thought.evidenceForPlaceholder": "Quelles preuves soutiennent cette pensée?",
    "thought.evidenceAgainst": "Preuves contre",
    "thought.evidenceAgainstPlaceholder": "Quelles preuves contredisent cette pensée?",
    "thought.balancedThought": "Pensée équilibrée",
    "thought.balancedThoughtPlaceholder": "Quelle pensée plus équilibrée pourriez-vous adopter?",
    "thought.newIntensity": "Nouvelle intensité émotionnelle",
    // Socratic questioning step
    "thought.socratic.title": "Questionnement socratique",
    "thought.socratic.desc": "Interrogez votre émotion pour déterminer si elle est proportionnelle à la situation",
    "thought.socratic.q1": "Cette émotion est-elle proportionnelle à la situation ?",
    "thought.socratic.q1placeholder": "Réfléchissez : l'intensité de votre émotion correspond-elle aux faits objectifs de la situation ?",
    "thought.socratic.q2": "Que dirais-je à un ami dans cette situation ?",
    "thought.socratic.q2placeholder": "Imaginez qu'un ami proche vive exactement la même situation. Que lui diriez-vous ?",
    "thought.socratic.q3": "Quels sont les faits objectifs vs. mes interprétations ?",
    "thought.socratic.q3placeholder": "Séparez les faits concrets de vos suppositions ou interprétations personnelles...",
    // Externalization step
    "thought.externalize.title": "Externalisation et réponse adaptée",
    "thought.externalize.desc": "Mettez votre émotion à distance et formulez une réponse constructive",
    "thought.externalize.letter": "Écrivez à votre émotion",
    "thought.externalize.letterPlaceholder": "Adressez-vous directement à votre émotion comme si c'était une entité séparée de vous. Ex: « Chère anxiété, je te reconnais, tu es là parce que... mais tu n'es pas moi et tu passeras... »",
    "thought.externalize.response": "Ma réponse adaptée",
    "thought.externalize.responsePlaceholder": "Comment souhaitez-vous réagir à cette situation de manière constructive ? Quel comportement choisissez-vous d'adopter ?",
    "thought.reeval.desc": "Après cette réflexion, à quel point ressentez-vous encore cette émotion ?",

    // Gratitude
    "gratitude.title": "Journal de gratitude",
    "gratitude.subtitle": "Cultivez la reconnaissance pour améliorer votre bien-être",
    "gratitude.item": "Gratitude",
    "gratitude.itemPlaceholder": "Quelque chose pour lequel vous êtes reconnaissant(e)...",

    // Behavioral activation
    "activation.title": "Activation Comportementale",
    "activation.subtitle": "Planifiez des activités positives",
    "activation.activity": "Activité",
    "activation.category": "Catégorie",
    "activation.timeOfDay": "Moment de la journée",
    "activation.plannedFor": "Planifié pour",
    "activation.enjoyment": "Plaisir attendu",
    "activation.mastery": "Maîtrise attendue",
    "activation.noActivities": "Aucune activité pour cette journée",
    "activation.addActivity": "Ajouter une activité",

    // Meditation
    "meditation.title": "Méditation guidée",
    "meditation.subtitle": "Pratiques de pleine conscience pour votre bien-être",
    "meditation.sessionsCompleted": "séances complétées",
    "meditation.completed": "Séance de méditation enregistrée !",
    "meditation.tips": "Conseils pour méditer",
    "meditation.tip1": "Créez un espace calme",
    "meditation.startSession": "Commencer la séance",
    "meditation.sessions": "Séances précédentes",

    // Cognitive defusion
    "defusion.title": "Défusion cognitive",
    "defusion.sessionsCompleted": "exercices complétés",
    "defusion.completed": "Exercice de défusion enregistré !",
    "defusion.subtitle": "Détachez-vous de vos pensées négatives",
    "defusion.yourThought": "Votre pensée:",
    "defusion.followSteps": "Suivez ces étapes:",

    // Self-compassion
    "compassion.title": "Auto-compassion",
    "compassion.sessionsCompleted": "exercices complétés",
    "compassion.completed": "Exercice d'auto-compassion enregistré !",
    "compassion.subtitle": "Développez la bienveillance envers vous-même",

    // Assertiveness
    "assertiveness.title": "Affirmation de soi",
    "assertiveness.subtitle": "Améliorez votre communication assertive",
    "assertiveness.descMethod": "La méthode DESC",
    "assertiveness.fullResponse": "Votre réponse DESC complète :",

    // Self-reinforcement
    "reinforcement.title": "Auto-Renforcement",
    "reinforcement.subtitle": "Célébrez vos accomplissements et récompensez-vous",
    "reinforcement.newAccomplishment": "Nouvel accomplissement",
    "reinforcement.whatAccomplished": "Qu'avez-vous accompli aujourd'hui ?",
    "reinforcement.accomplishmentPlaceholder": "Décrivez ce que vous avez réussi, même les petites choses...",
    "reinforcement.difficulty": "Niveau de difficulté",
    "reinforcement.easy": "Facile",
    "reinforcement.medium": "Moyen",
    "reinforcement.hard": "Difficile",
    "reinforcement.emotionsFelt": "Quelles émotions ressentez-vous ?",
    "reinforcement.emotionsPlaceholder": "Ex: fierté, satisfaction, soulagement...",
    "reinforcement.chooseReward": "Choisissez une récompense",
    "reinforcement.customReward": "Ou écrivez votre propre récompense...",
    "reinforcement.celebrate": "Célébrer cet accomplissement",
    "reinforcement.myAccomplishments": "Mes accomplissements",
    "reinforcement.noAccomplishments": "Aucun accomplissement enregistré",
    "reinforcement.startCelebrating": "Commencez à célébrer vos réussites",

    // Values exploration
    "values.title": "Exploration des valeurs",
    "values.subtitle": "Identifiez et explorez vos valeurs fondamentales",

    // Needs exploration
    "needs.title": "Exploration des besoins",
    "needs.subtitle": "Identifiez vos besoins essentiels selon Maslow",
    "needs.reflectionQuestions": "Questions de réflexion :",

    // Problem solving
    "problem.title": "Résolution de problèmes",
    "problem.subtitle": "Résolvez vos difficultés pas à pas",

    // Qualities exploration
    "qualities.title": "Exploration des qualités",
    "qualities.subtitle": "Renforcez votre estime de soi",

    // Capabilities exploration
    "capabilities.title": "Exploration des Capacités",
    "capabilities.subtitle": "Développez votre confiance en reconnaissant ce que vous savez faire",
    "capabilities.competenceAreas": "Domaines de compétences",

    // Patient exercises view
    "patientExercises.assigned": "Exercices assignés",
    "patientExercises.standalone": "Exercices autonomes",
    "patientExercises.myExercises": "Mes exercices",
    "patientExercises.downloadPdf": "Télécharger PDF",
    "psychoeducation.downloadPdf": "Télécharger en PDF",
    "psychoeducation.downloadPdfHint": "Télécharger ce module en PDF (chapitres, illustrations, quiz et corrigé)",
    "psychoeducation.pdfError": "PDF indisponible",
    "patientExercises.paperVersion": "Version papier",
    "patientExercises.paperVersionHint": "Télécharger la fiche à imprimer (PDF) pour faire cet exercice sur papier",
    "patientExercises.paperVersionError": "Fiche papier indisponible pour le moment",
    "patientExercises.entries": "entrée(s)",
    "patientExercises.responses": "Réponse(s)",
    "patientExercises.noResponse": "Aucune réponse soumise",
    "patientExercises.noExercises": "Aucun exercice enregistré",
    "patientExercises.completed": "Complété",
    "patientExercises.pending": "En attente",
    "patientExercises.entryRecorded": "Entrée enregistrée",

    // Patient management
    "patientMgmt.title": "Patients",
    "patientMgmt.subtitle": "Gérez vos patients et leurs accès",
    "patientMgmt.addNew": "Ajouter un nouveau patient",
    "patientMgmt.active": "Actif",
    "patientMgmt.inactive": "Inactif",
    "patientMgmt.activate": "Activer",
    "patientMgmt.deactivate": "Désactiver",
    "patientMgmt.viewProgress": "Voir la progression",
    "patientMgmt.viewExercises": "Voir les exercices",

    // Patient progress
    "progress.title": "Progression",
    "progress.notFound": "Patient non trouvé",
    "progress.moodEntries": "Entrées d'humeur",
    "progress.exercisesCompleted": "Exercices complétés",
    "progress.emotionsProcessed": "Émotions traitées",
    "progress.moodHistory": "Historique d'humeur",
    "progress.noMoodEntries": "Aucune entrée d'humeur",
    "progress.thoughtRecords": "Registres de pensées",
    "progress.thought": "Pensée:",
    "progress.balancedThought": "Pensée équilibrée:",
    "progress.noEmotionRegisters": "Aucun registre d'émotion",
    "progress.gratitudeEntries": "Entrées de gratitude",
    "progress.noGratitude": "Aucune entrée de gratitude",
    "progress.assignedExercises": "Exercices assignés",
    "progress.noAssignments": "Aucun exercice assigné",
    "progress.sessionReportsTitle": "Comptes rendus de séance",
    "progress.noReports": "Aucun compte rendu pour ce patient",
    "progress.noReportsHint": "Générez un compte rendu depuis l'onglet Comptes rendus",
    "cr.saved": "Compte rendu enregistré",
    "cr.saveError": "Erreur lors de la sauvegarde",
    "cr.initial": "Entretien initial",
    "cr.suivi": "Séance de suivi",
    "cr.fields": "champs",
    "cr.editHint": "Tous les champs remplis sont éditables ci-dessus.",
    "cr.informations_generales": "Informations générales",
    "cr.anamnese": "Anamnèse",
    "cr.evaluation_dsm5": "Évaluation DSM-5",
    "cr.hypothese_diagnostic_principal": "Diagnostic principal",
    "cr.hypotheses_diagnostics_differentiels": "Diagnostics différentiels",
    "cr.hypothese_clinique_generale": "Hypothèse clinique générale",
    "cr.problematiques": "Problématiques",
    "cr.protocole_therapeutique": "Protocole thérapeutique",
    "cr.processus_cibles": "Processus cibles",
    "cr.strategies_therapeutiques": "Stratégies thérapeutiques",
    "cr.evaluations_standardisees": "Évaluations standardisées",
    "cr.objectifs_therapeutiques": "Objectifs thérapeutiques",
    "cr.recommandations_suivi": "Recommandations de suivi",
    "cr.notes_libres": "Notes libres",
    "cr.exercices": "Exercices",
    "cr.agenda_objectifs": "Agenda / Objectifs",
    "cr.ameliorations_progres": "Améliorations / Progrès",
    "cr.revue_semaine": "Revue de la semaine",
    "cr.humeur_symptomes": "Humeur / Symptômes",
    "cr.echelles_administrees": "Échelles administrées",
    "cr.revue_exercices": "Revue des exercices",
    "cr.analyse_chaine": "Analyse en chaîne",
    "cr.analyse_chaine_abc": "Analyse ABC",
    "cr.analyse_chaine_sorc": "Analyse SORC",
    "cr.situation_pensees_emotions": "Situation-Pensées-Émotions",
    "cr.pensees_automatiques": "Pensées automatiques",
    "cr.analyse_tcc": "Analyse TCC",
    "cr.interventions": "Interventions",
    "cr.experiences_comportementales": "Expériences comportementales",
    "cr.nouveaux_exercices": "Nouveaux exercices",
    "cr.objectifs_smart": "Objectifs SMART",
    "cr.plan_prochaine_seance": "Plan prochaine séance",

    // Exercise library
    "library.title": "Bibliothèque d'exercices",
    "library.subtitle": "Parcourez et assignez des exercices à vos patients",
    "library.customTitle": "Titre personnalisé (optionnel)",
    "library.assign": "Assigner",
    "library.assigning": "Assignation...",
    "library.assigned": "Exercice assigné !",
    "library.currentAssignments": "Assignations actuelles",
    "library.remove": "Retirer",
    "library.pending": "En cours",
    "library.completed": "Terminé",
    "library.assignedToCount": "Assigné à {count} patient(s)",
    "library.exerciseUnassigned": "Exercice retiré",

    // Psychoeducation
    "psychoeducation.title": "Psychoéducation",
    "psychoeducation.subtitle": "Modules théoriques",
    "psychoeducation.modules": "Modules théoriques",
    "psychoeducation.assignModule": "Assigner un module",
    "psychoeducation.moduleAssigned": "Module assigné",
    "psychoeducation.moduleUnassigned": "Module retiré",
    "psychoeducation.noModulesAssigned": "Aucun module ne vous a encore été assigné. Votre thérapeute vous en attribuera prochainement.",

    // Reminders
    "reminders.title": "Rappels",
    "reminders.subtitle": "Historique et gestion des rappels d'exercices",

    // Pomodoro
    "pomodoro.title": "Pomodoro",
    "pomodoro.subtitle": "Restez concentré et productif",
    "pomodoro.startFocus": "Démarrer une session de concentration",
    "pomodoro.sessions": "Sessions précédentes",

    // Subscription
    "subscription.trial": "Essai gratuit",
    "subscription.daysLeft": "jours restants",

    // Navigation extras
    "nav.myExercises": "Mes exercices",
    "nav.psychoeducation": "Psychoéducation",
    "nav.theoryModules": "Modules théoriques",
    "nav.addPatient": "Ajouter un patient",
    "nav.meditations": "Vidéos de méditation",
    "nav.myMeditations": "Mes Méditations",

    // Video Meditations
    "meditations.title": "Méditations Vidéo",
    "meditations.subtitle": "Uploadez et gérez vos vidéos de méditation guidée pour vos patients",
    "meditations.uploadNew": "Uploader une vidéo",
    "meditations.dropzoneText": "Cliquez pour sélectionner une vidéo",
    "meditations.videoTitle": "Titre de la vidéo",
    "meditations.titlePlaceholder": "Ex: Respiration guidée 10min",
    "meditations.language": "Langue",
    "meditations.description": "Description (optionnel)",
    "meditations.descPlaceholder": "Description de la méditation...",
    "meditations.uploading": "Upload en cours...",
    "meditations.assembling": "Finalisation en cours...",
    "meditations.chunkInfo": "Envoi par morceaux pour supporter les gros fichiers",
    "meditations.uploadBtn": "Uploader la vidéo",
    "meditations.uploadSuccess": "Vidéo uploadée avec succès !",
    "meditations.uploadError": "Erreur lors de l'upload",
    "meditations.invalidFormat": "Format non supporté. Utilisez MP4, MOV, AVI, WebM ou MKV.",
    "meditations.fileTooLarge": "Fichier trop volumineux (max 800 Mo)",
    "meditations.library": "Bibliothèque de vidéos",
    "meditations.emptyLibrary": "Aucune vidéo uploadée",
    "meditations.emptyLibraryHint": "Uploadez votre première vidéo de méditation ci-dessus",
    "meditations.assign": "Assigner",
    "meditations.assignTo": "Assigner à des patients",
    "meditations.assignHint": "Sélectionnez les patients qui auront accès à",
    "meditations.assigned": "Méditation assignée",
    "meditations.unassigned": "Méditation retirée",
    "meditations.searchPatient": "Rechercher un patient...",
    "meditations.noPatients": "Aucun patient actif",
    "meditations.confirmDelete": "Supprimer cette méditation ?",
    "meditations.deleted": "Méditation supprimée",
    "meditations.noSupport": "Votre navigateur ne supporte pas la lecture vidéo.",
    "meditations.patientTitle": "Mes Méditations",
    "meditations.patientSubtitle": "Vidéos de méditation guidée assignées par votre thérapeute",
    "meditations.viewed": "Vue",
    "meditations.new": "Nouveau",
    "meditations.backToList": "Retour à la liste",
    "meditations.noAssignments": "Aucune méditation assignée",
    "meditations.noAssignmentsHint": "Votre thérapeute vous assignera des vidéos de méditation",

    // Common extras
    "common.error": "Erreur lors de l'enregistrement",
    "common.errorLoading": "Erreur lors du chargement",
    "common.continue": "Continuer",
    "common.finish": "Terminer",
    "common.start": "Démarrer",
    "common.pause": "Pause",
    "common.resume": "Reprendre",
    "common.restart": "Recommencer",
    "common.add": "Ajouter",
    "common.refresh": "Actualiser",
    "common.search": "Rechercher...",
    "common.select": "Sélectionner",
    "common.selected": "Sélectionnée",
    "common.fillRequired": "Veuillez remplir tous les champs obligatoires",
    "common.selectAtLeastOne": "Sélectionnez au moins un élément",
    "common.recentHistory": "Historique récent",
    "common.noEntriesYet": "Aucune entrée pour le moment",
    "common.completed": "Complété",
    "common.pending": "En attente",
    "common.active": "Actif",
    "common.inactive": "Inactif",
    "common.access": "Accès",
    "common.instructions": "Instructions",
    "common.tip": "Conseil",
    "common.optional": "optionnel",
    "common.examples": "Exemples",
    "common.domain": "Domaine",
    "common.of": "sur",

    // Therapist dashboard extras
    "therapist.noPatients": "Aucun patient pour le moment",
    "therapist.addFirstPatient": "Ajouter votre premier patient",

    // Patient management extras
    "patientMgmt.patientAdded": "Patient ajouté avec succès!",
    "patientMgmt.errorAdding": "Erreur lors de l'ajout",
    "patientMgmt.addPatient": "Ajouter le patient",
    "patientMgmt.addNewPatient": "Ajouter un nouveau patient",
    "patientMgmt.fullName": "Nom complet",
    "patientMgmt.immediateAccess": "Le patient recevra un accès immédiat à la plateforme.",
    "patientMgmt.patientList": "Liste des patients",
    "patientMgmt.newEmail": "Nouvel email",
    "patientMgmt.emailUpdated": "Email mis à jour avec succès!",
    "patientMgmt.errorUpdating": "Erreur lors de la mise à jour",
    "patientMgmt.searchPatient": "Rechercher un patient...",
    "patientMgmt.noPatientFound": "Aucun patient trouvé",
    "patientMgmt.activated": "Patient activé",
    "patientMgmt.deactivated": "Patient désactivé",
    "patientMgmt.tracking": "Suivi",
    "patientMgmt.pendingPatients": "Patients en attente",
    "patientMgmt.pendingPatientsDesc": "Ces patients se sont inscrits mais ne sont rattachés à aucun thérapeute.",
    "patientMgmt.claimPatient": "Rattacher",
    "patientMgmt.patientClaimed": "Patient rattaché avec succès",
    "patientMgmt.errorClaiming": "Erreur lors du rattachement",
    "patientMgmt.unnamed": "Sans nom",
    "patientMgmt.invitationCode": "Code d'invitation",
    "patientMgmt.invitationCodeDesc": "Partagez ce lien avec vos patients pour qu'ils puissent s'inscrire.",
    "patientMgmt.copyLink": "Copier le lien",
    "patientMgmt.linkCopied": "Lien d'invitation copié !",
    "patientMgmt.qrScanDesc": "Scannez ce QR code pour accéder au formulaire d'inscription.",
    "patientMgmt.downloadQR": "Télécharger le QR code",
    "auth.invitedBy": "Invitation de",
    "auth.invitationNote": "Votre compte sera activé après validation par votre thérapeute.",
    "auth.invalidInvitationCode": "Code d'invitation invalide",
    "auth.passwordPolicy": "Min. 8 caractères, 1 majuscule, 1 minuscule, 1 chiffre",
    "auth.healthDataConsent": "J'accepte le traitement de mes données de santé dans le cadre de mon suivi thérapeutique, conformément au RGPD.",
    "auth.privacyLink": "Politique de confidentialité",
    "auth.rateLimited": "Trop de tentatives. Réessayez dans 5 minutes.",
    "patientMgmt.refusePatient": "Refuser",
    "patientMgmt.confirmRefuse": "Voulez-vous vraiment refuser ce patient ?",
    "patientMgmt.patientRefused": "Patient refusé",
    "patientMgmt.errorRefusing": "Erreur lors du refus",
    "patientMgmt.confirmDelete": "Supprimer définitivement ce patient et toutes ses données ?",
    "patientMgmt.patientDeleted": "Patient supprimé définitivement",
    "patientMgmt.errorDeleting": "Erreur lors de la suppression",

    // Exercise library extras
    "library.cognitive": "Cognitif",
    "library.behavioral": "Comportemental",
    "library.monitoring": "Suivi",
    "library.act": "ACT",
    "library.mindfulness": "Pleine conscience",
    "library.positive": "Psychologie positive",
    "library.organization": "Organisation",
    "library.selfAwareness": "Connaissance de soi",
    "library.barkleyParents": "Barkley — Parents",
    "library.barkleyChildren": "Barkley — Enfants",
    "library.barkleyTeens": "Barkley — Ados",
    "library.tdahAdulte": "TDAH Adulte",
    "library.selectPatient": "Sélectionner un patient",
    "library.selectPatientError": "Veuillez sélectionner un patient",
    "library.assignExercise": "Assigner un exercice",
    "library.assignTheExercise": "Assigner l'exercice",
    "library.exerciseAssigned": "Exercice assigné avec succès!",
    "library.errorAssigning": "Erreur lors de l'assignation",
    "library.instructionsOptional": "Instructions (optionnel)",
    "library.instructionsPlaceholder": "Instructions spécifiques pour le patient...",
    "library.titleExample": "Ex: Exercice de pensée #1",

    // Mood extras
    "mood.selectEmotion": "Sélectionnez au moins une émotion",
    "mood.errorRecording": "Erreur lors de l'enregistrement",
    "mood.startTracking": "Commencez à suivre votre humeur",
    "mood.veryBadLabel": "Très mal",
    "mood.notGood": "Pas bien",
    "mood.okay": "Correct",
    "mood.good": "Bien",
    "mood.veryGoodLabel": "Très bien",

    // Emotion labels
    "emotion.happy": "Heureux",
    "emotion.calm": "Calme",
    "emotion.anxious": "Anxieux",
    "emotion.sad": "Triste",
    "emotion.angry": "En colère",
    "emotion.stressed": "Stressé",
    "emotion.hopeful": "Plein d'espoir",
    "emotion.tired": "Fatigué",
    "emotion.grateful": "Reconnaissant",
    "emotion.frustrated": "Frustré",
    "emotion.peaceful": "Paisible",
    "emotion.motivated": "Motivé",
    "emotion.joy": "Joie",
    "emotion.sadness": "Tristesse",
    "emotion.anger": "Colère",
    "emotion.fear": "Peur",
    "emotion.disgust": "Dégoût",
    "emotion.surprise": "Surprise",
    "emotion.ashamed": "Honteux",
    "emotion.guilty": "Coupable",
    "emotion.scared": "Effrayé",
    "emotion.hopeless": "Désespéré",
    "emotion.irritated": "Irrité",
    "emotion.worried": "Inquiet",

    // Thought record extras
    "thought.recorded": "Émotion enregistrée !",
    "thought.errorRecording": "Erreur lors de l'enregistrement",
    "thought.yourThought": "Votre émotion :",
    "thought.tipTitle": "Conseil",
    "thought.tipText": "En écrivant à votre émotion, vous créez une distance qui permet de la réguler. L'émotion n'est pas vous — c'est une réaction temporaire.",
    "thought.afterReflection": "Après cette réflexion, à quel point ressentez-vous encore ces émotions ?",
    "thought.noRecords": "Aucun enregistrement",
    "thought.evidenceForTitle": "Preuves en faveur de cette pensée",
    "thought.evidenceAgainstTitle": "Preuves contre cette pensée",
    "thought.evidenceForPrompt": "Quels faits soutiennent cette pensée?",
    "thought.evidenceAgainstPrompt": "Quels faits contredisent cette pensée?",
    "thought.balancedThoughtPrompt": "Formulez une pensée plus nuancée et réaliste...",

    // Gratitude extras
    "gratitude.todayGrateful": "Aujourd'hui, je suis reconnaissant pour...",
    "gratitude.addGratitude": "Ajouter une gratitude",
    "gratitude.reflectionLabel": "Réflexion (optionnel)",
    "gratitude.reflectionPlaceholder": "Qu'est-ce que ces gratitudes m'apprennent sur ma vie?",
    "gratitude.saveGratitudes": "Enregistrer mes gratitudes",
    "gratitude.pastGratitudes": "Mes gratitudes passées",
    "gratitude.errorSaving": "Erreur lors de l'enregistrement",
    "gratitude.saved": "Gratitudes enregistrées!",
    "gratitude.addAtLeastOne": "Ajoutez au moins une gratitude",
    "gratitude.startNoting": "Commencez à noter vos gratitudes",

    // Behavioral activation extras
    "activation.pageTitle": "Activation comportementale",
    "activation.daysPlanned": "jours planifiés",
    "activation.saved": "Journée enregistrée !",
    "activation.saveDay": "Enregistrer la journée",
    "activation.pageSubtitle": "Planifiez des activités positives pour améliorer votre humeur",
    "activation.completedCount": "complétées",
    "activation.addImproveHint": "Ajoutez des activités pour améliorer votre humeur",
    "activation.social": "Social",
    "activation.physical": "Physique",
    "activation.creative": "Créatif",
    "activation.wellbeing": "Bien-être",
    "activation.productive": "Productif",
    "activation.relaxation": "Détente",
    "activation.morning": "Matin",
    "activation.afternoon": "Après-midi",
    "activation.evening": "Soir",
    "activation.activityPlaceholder": "Ex: Marcher 20 minutes",

    // Meditation extras
    "meditation.breathing": "Respiration consciente",
    "meditation.breathingDesc": "Exercice de base pour calmer l'esprit",
    "meditation.bodyScan": "Scan corporel",
    "meditation.bodyScanDesc": "Prenez conscience de chaque partie de votre corps",
    "meditation.gratitudeMed": "Méditation de gratitude",
    "meditation.gratitudeMedDesc": "Cultivez un sentiment de reconnaissance",
    "meditation.innerCalm": "Calme intérieur",
    "meditation.innerCalmDesc": "Trouvez la paix et la sérénité",
    "meditation.sleepPrep": "Préparation au sommeil",
    "meditation.sleepPrepDesc": "Relaxation profonde avant de dormir",
    "meditation.minutes": "minutes",
    "meditation.backToMeditations": "Retour aux méditations",
    "meditation.tipFindTime": "Trouvez le bon moment",
    "meditation.tipFindTimeDesc": "Le matin au réveil ou le soir avant de dormir sont des moments idéaux.",
    "meditation.tipCreateSpace": "Créez un espace calme",
    "meditation.tipCreateSpaceDesc": "Choisissez un endroit tranquille où vous ne serez pas dérangé.",
    "meditation.tipBePatient": "Soyez patient",
    "meditation.tipBePatientDesc": "La méditation s'améliore avec la pratique. Commencez par 5 minutes.",

    // Cognitive defusion extras
    "defusion.pageSubtitle": "Techniques ACT pour prendre de la distance avec vos pensées",
    "defusion.writeThought": "Écrivez une pensée qui vous préoccupe",
    "defusion.thoughtPlaceholder": "Ex: Je ne suis pas assez bon...",
    "defusion.startExercise": "Commencer l'exercice",
    "defusion.backToExercises": "Retour aux exercices",
    "defusion.leaves": "Feuilles sur un ruisseau",
    "defusion.leavesDesc": "Visualisez vos pensées comme des feuilles flottant sur l'eau",
    "defusion.silly": "Voix comique",
    "defusion.sillyDesc": "Répétez la pensée avec une voix drôle pour réduire son impact",
    "defusion.prefix": "'J'ai la pensée que...'",
    "defusion.prefixDesc": "Ajoutez un préfixe pour créer une distance avec vos pensées",
    "defusion.clouds": "Nuages dans le ciel",
    "defusion.cloudsDesc": "Vos pensées sont des nuages qui passent dans le ciel de votre esprit",
    "defusion.thankMind": "Merci, esprit",
    "defusion.thankMindDesc": "Remerciez votre esprit pour ses pensées, puis recentrez-vous",

    // Self-compassion extras
    "compassion.pageSubtitle": "Apprenez à être bienveillant envers vous-même",
    "compassion.letter": "Lettre de compassion",
    "compassion.letterDesc": "Écrivez-vous une lettre comme vous le feriez pour un ami cher",
    "compassion.touch": "Auto-apaisement physique",
    "compassion.touchDesc": "Utilisez le toucher pour vous réconforter",
    "compassion.phrases": "Phrases de bienveillance",
    "compassion.phrasesDesc": "Répétez des phrases de compassion pour vous-même",
    "compassion.finishExercise": "Terminer l'exercice",
    "compassion.nextStep": "Étape suivante",
    "compassion.repeatInstruction": "Répétez chaque phrase 3 fois, lentement et avec intention. Cliquez sur chaque phrase une fois que vous l'avez répétée.",
    "compassion.writeHere": "Écrivez ici...",
    "compassion.backToExercises": "Retour aux exercices",

    // Assertiveness extras
    "assertiveness.situationLabel": "Décrivez une situation où vous avez eu du mal à vous affirmer",
    "assertiveness.situationPlaceholder": "Exemple : Lors d'une réunion de travail, mon collègue m'a coupé la parole plusieurs fois...",
    "assertiveness.emotionsLabel": "Quelles émotions avez-vous ressenties ?",
    "assertiveness.emotionsPlaceholder": "Frustration, colère, honte, anxiété...",
    "assertiveness.styleQuestion": "Quel style de communication avez-vous adopté dans cette situation ?",
    "assertiveness.passive": "Passif",
    "assertiveness.aggressive": "Agressif",
    "assertiveness.passiveAggressive": "Passif-Agressif",
    "assertiveness.assertive": "Assertif",
    "assertiveness.descTechnique": "Une technique structurée pour s'exprimer de manière assertive.",
    "assertiveness.describe": "Décrire",
    "assertiveness.describeDetail": "Décrivez les faits objectivement",
    "assertiveness.express": "Exprimer",
    "assertiveness.expressDetail": "Exprimez vos émotions avec 'Je'",
    "assertiveness.specify": "Spécifier",
    "assertiveness.specifyDetail": "Spécifiez ce que vous souhaitez",
    "assertiveness.consequences": "Conséquences",
    "assertiveness.consequencesDetail": "Indiquez les conséquences positives",
    "assertiveness.reformulate": "Reformulez avec vos propres mots",
    "assertiveness.reformulatePlaceholder": "Écrivez comment vous formuleriez votre message assertif...",
    "assertiveness.reflectionLabel": "Réflexion : Comment vous sentez-vous après cet exercice ?",
    "assertiveness.reflectionPlaceholder": "Qu'avez-vous appris ? Vous sentez-vous plus confiant(e) pour utiliser cette technique ?",
    "assertiveness.saved": "Exercice d'affirmation de soi enregistré !",
    "assertiveness.steps.situation": "La Situation",
    "assertiveness.steps.style": "Style Actuel",
    "assertiveness.steps.desc": "Méthode DESC",
    "assertiveness.steps.practice": "Mise en Pratique",

    // Values exploration extras
    "values.pageSubtitle": "Découvrez ce qui compte vraiment pour vous (ACT - Steven C. Hayes)",
    "values.domainOf": "Domaine {current} sur {total}",
    "values.whatImportant": "Qu'est-ce qui est important pour vous dans ce domaine ?",
    "values.importancePlaceholder": "Décrivez ce qui compte vraiment pour vous...",
    "values.howToAct": "Comment aimeriez-vous agir en accord avec cette valeur ?",
    "values.actionsPlaceholder": "Quelles actions concrètes reflèteraient cette valeur ?",
    "values.ratingQuestion": "Sur une échelle de 1 à 10, à quel point vivez-vous actuellement selon cette valeur ?",
    "values.saved": "Exploration des valeurs enregistrée !",
    "values.domainsExplored": "domaines explorés",

    // Needs exploration extras
    "needs.pageSubtitle": "Pyramide de Maslow - Identifiez vos besoins fondamentaux",
    "needs.howSatisfied": "À quel point ce besoin est-il satisfait ?",
    "needs.notSatisfied": "Pas du tout satisfait",
    "needs.fullySatisfied": "Pleinement satisfait",
    "needs.yourReflections": "Vos réflexions sur ce besoin",
    "needs.reflectionsPlaceholder": "Qu'est-ce qui manque ? Qu'est-ce qui fonctionne bien ?",
    "needs.saveExploration": "Enregistrer l'exploration",
    "needs.saved": "Exploration des besoins enregistrée !",
    "needs.physiological": "Besoins Physiologiques",
    "needs.safety": "Besoins de Sécurité",
    "needs.belonging": "Besoins d'Appartenance",
    "needs.esteem": "Besoins d'Estime",
    "needs.selfActualization": "Besoins d'Accomplissement",

    // Problem solving extras
    "problem.pageSubtitle": "Une approche structurée pour surmonter les difficultés",
    "problem.step.problem": "Définir le Problème",
    "problem.step.problemDesc": "Décrivez clairement le problème à résoudre",
    "problem.step.goals": "Objectifs",
    "problem.step.goalsDesc": "Que souhaitez-vous accomplir ?",
    "problem.step.solutions": "Générer des Solutions",
    "problem.step.solutionsDesc": "Brainstorming sans jugement",
    "problem.step.evaluate": "Évaluer les Solutions",
    "problem.step.evaluateDesc": "Peser le pour et le contre",
    "problem.step.plan": "Plan d'Action",
    "problem.step.planDesc": "Étapes concrètes à suivre",
    "problem.whatProblem": "Quel est le problème que vous rencontrez ?",
    "problem.problemPlaceholder": "Décrivez le problème de manière claire et spécifique...",
    "problem.problemTip": "Soyez précis. Au lieu de \"Je suis stressé\", essayez \"Je me sens dépassé par la charge de travail au bureau cette semaine\".",
    "problem.whatGoal": "Que souhaitez-vous accomplir ?",
    "problem.goalPlaceholder": "Décrivez votre objectif idéal...",
    "problem.smartTip": "Objectif Spécifique, Mesurable, Atteignable, Réaliste, Temporel.",
    "problem.generateSolutions": "Générez autant de solutions que possible, même les plus farfelues. Ne jugez pas encore !",
    "problem.solution": "Solution",
    "problem.solutionPlaceholder": "Décrivez la solution...",
    "problem.addSolution": "Ajouter une solution",
    "problem.evaluateIntro": "Évaluez chaque solution en listant les avantages et inconvénients.",
    "problem.pros": "Avantages (+)",
    "problem.prosPlaceholder": "Points positifs...",
    "problem.cons": "Inconvénients (-)",
    "problem.consPlaceholder": "Points négatifs...",
    "problem.chosenSolution": "Solution choisie :",
    "problem.none": "Aucune",
    "problem.actionSteps": "Étapes d'action :",
    "problem.stepPlaceholder": "Décrivez l'étape...",
    "problem.addStep": "Ajouter une étape",
    "problem.saved": "Résolution de problème enregistrée !",

    // Qualities exploration extras
    "qualities.pageSubtitle": "Renforcez votre estime de soi en reconnaissant vos forces",
    "qualities.suggestions": "Suggestions de qualités",
    "qualities.myQualities": "Mes Qualités",
    "qualities.quality": "Qualité",
    "qualities.qualityPlaceholder": "Nommez une qualité que vous possédez...",
    "qualities.examplePlaceholder": "Donnez un exemple concret où vous avez démontré cette qualité...",
    "qualities.sourcePlaceholder": "Qui vous a reconnu cette qualité ? (ami, famille, collègue...)",
    "qualities.addQuality": "Ajouter une qualité",
    "qualities.reflection": "Réflexion",
    "qualities.reflectionPlaceholder": "Comment vous sentez-vous après avoir listé ces qualités ? Qu'avez-vous appris sur vous-même ?",
    "qualities.saveQualities": "Enregistrer mes qualités",
    "qualities.saved": "Exploration des qualités enregistrée !",
    "qualities.addAtLeastOne": "Ajoutez au moins une qualité",
    "qualities.interpersonal": "Qualités Relationnelles",
    "qualities.intellectual": "Qualités Intellectuelles",
    "qualities.emotional": "Qualités Émotionnelles",
    "qualities.moral": "Qualités Morales",
    "qualities.practical": "Qualités Pratiques",

    // Capabilities exploration extras
    "capabilities.pageSubtitle": "Développez votre confiance en reconnaissant ce que vous savez faire",
    "capabilities.myCapabilities": "Mes Capacités",
    "capabilities.capability": "Capacité",
    "capabilities.capabilityPlaceholder": "Nommez une capacité ou compétence...",
    "capabilities.currentLevel": "Niveau actuel",
    "capabilities.learning": "En apprentissage",
    "capabilities.competent": "Compétent",
    "capabilities.proficient": "Maîtrisé",
    "capabilities.expert": "Expert",
    "capabilities.evidencePlaceholder": "Preuve : décrivez une situation où vous avez utilisé cette capacité avec succès...",
    "capabilities.goalPlaceholder": "Objectif de développement (optionnel) : comment améliorer cette capacité ?",
    "capabilities.addCapability": "Ajouter une capacité",
    "capabilities.saveCapabilities": "Enregistrer mes capacités",
    "capabilities.saved": "Exploration des capacités enregistrée !",
    "capabilities.addAtLeastOne": "Ajoutez au moins une capacité",
    "capabilities.professional": "Compétences Professionnelles",
    "capabilities.social": "Compétences Sociales",
    "capabilities.personal": "Compétences Personnelles",
    "capabilities.creative": "Compétences Créatives",
    "capabilities.physicalSkills": "Compétences Physiques",

    // Pomodoro extras
    "pomodoro.pageSubtitle": "Technique de productivite et de concentration",
    "pomodoro.taskName": "Nom de la tache",
    "pomodoro.taskPlaceholder": "Ex: Revision du cours...",
    "pomodoro.duration": "Duree (minutes)",
    "pomodoro.started": "Pomodoro demarre!",
    "pomodoro.completed": "Pomodoro complete! Prenez une pause.",
    "pomodoro.enterTask": "Entrez un nom de tache",
    "pomodoro.errorStarting": "Erreur lors du demarrage",
    "pomodoro.todayCount": "Pomodoros aujourd'hui",
    "pomodoro.recentSessions": "Sessions recentes",
    "pomodoro.noSessions": "Aucune session pour le moment",
    "pomodoro.dayPlannerSubtitle": "Planifiez et executez vos pomodori pour la journee",
    "pomodoro.settings": "Parametres",
    "pomodoro.workDuration": "Travail (min)",
    "pomodoro.shortBreak": "Pause courte (min)",
    "pomodoro.longBreak": "Pause longue (min)",
    "pomodoro.longBreakHint": "La pause longue se declenche automatiquement apres 4 pomodori",
    "pomodoro.longBreakLabel": "Pause longue",
    "pomodoro.planTasks": "Taches de la journee",
    "pomodoro.objectivePh": "Objectif : que voulez-vous accomplir ?",
    "pomodoro.addTask": "Ajouter une tache",
    "pomodoro.startDay": "Demarrer la journee",
    "pomodoro.planCreated": "Plan cree !",
    "pomodoro.maxReached": "Maximum atteint (8h de travail effectif)",
    "pomodoro.taskList": "Liste des taches",
    "pomodoro.deferTask": "Decaler",
    "pomodoro.taskDeferred": "Tache decalee a plus tard",
    "pomodoro.deferred": "Decale",
    "pomodoro.reviewTitle": "Bilan du pomodoro",
    "pomodoro.reviewPh": "Qu'avez-vous accompli ? Comment cela s'est-il passe ?",
    "pomodoro.submitReview": "Valider et continuer",
    "pomodoro.startBreak": "Demarrer la pause",
    "pomodoro.skipBreak": "Passer la pause",
    "pomodoro.resume": "Reprendre",
    "pomodoro.allDone": "Journee terminee !",
    "pomodoro.effectiveWork": "de travail effectif",
    "pomodoro.newPlan": "Nouveau plan",

    // Patient progress extras
    "progress.averageMood": "Humeur moyenne",
    "progress.moodEvolution": "Évolution de l'humeur (30 derniers jours)",
    "progress.notEnoughData": "Pas assez de données",
    "progress.topEmotions": "Émotions les plus fréquentes",
    "progress.mood": "Humeur",
    "progress.thoughts": "Pensées",
    "progress.gratitude": "Gratitude",
    "progress.exercises": "Exercices",
    "progress.moodHistoryTitle": "Historique d'humeur",
    "progress.thoughtRecordsTitle": "Registres de pensées",
    "progress.gratitudeJournal": "Journal de gratitude",
    "progress.assignedExercisesTitle": "Exercices assignés",
    "progress.situation": "Situation:",
    "progress.balancedThoughtLabel": "Pensée équilibrée:",
    "progress.viewExercises": "Voir exercices",
    "progress.reportGenerated": "Rapport généré le",

    // Reminders extras
    "reminders.totalSent": "Rappels envoyés (total)",
    "reminders.today": "Rappels aujourd'hui",
    "reminders.pendingReminders": "En attente de rappel",
    "reminders.autoReminders": "Rappels automatiques",
    "reminders.autoRemindersDesc": "Les rappels sont envoyés automatiquement aux patients qui n'ont pas complété leurs exercices après 3 jours. Maximum un rappel par jour par exercice.",
    "reminders.history": "Historique des rappels",
    "reminders.assigned": "Assigné:",
    "reminders.lastReminder": "Dernier rappel:",
    "reminders.resend": "Renvoyer",
    "reminders.sentSuccess": "Rappel envoyé avec succès!",
    "reminders.errorSending": "Erreur lors de l'envoi du rappel",
    "reminders.noReminders": "Aucun rappel envoyé pour le moment",
    "reminders.autoSendDesc": "Les rappels seront envoyés automatiquement aux patients après 3 jours d'inactivité",

    // Psychoeducation extras
    "psychoeducation.pageSubtitle": "Comprendre les bases théoriques de la thérapie cognitive et comportementale",
    "psychoeducation.aboutModule": "À propos de ce module",
    "psychoeducation.aboutModuleDesc": "La psychoéducation est une étape essentielle du parcours thérapeutique. Comprendre les mécanismes de vos difficultés vous donne un pouvoir d'action. Chaque module présente un modèle théorique fondamental, suivi d'un quiz pour tester votre compréhension.",
    "psychoeducation.sections": "sections",
    "psychoeducation.quiz": "Quiz",
    "psychoeducation.quizTitle": "Quiz de compréhension",
    "psychoeducation.checkAnswers": "Vérifier mes réponses",
    "psychoeducation.backToModules": "Retour aux modules",
    "psychoeducation.correct": "correct",
    "psychoeducation.section": "Section",
    "psychoeducation.goToQuiz": "Passer au Quiz",
    "psychoeducation.newerModulesTitle": "Modules approfondis",
    "psychoeducation.classicModulesTitle": "Fondements theoriques",
    "psychoeducation.currentAssignments": "Patients assignes",
    "psychoeducation.addPatient": "Ajouter un patient",

    // AI suggestions extras
    "ai.title": "Suggestions IA personnalisées",
    "ai.history": "Historique",
    "ai.description": "L'IA analysera vos données d'humeur et vos exercices récents pour vous suggérer des exercices TCC adaptés à votre situation actuelle.",
    "ai.contextLabel": "Contexte additionnel (optionnel)",
    "ai.contextPlaceholder": "Décrivez ce que vous ressentez aujourd'hui ou une situation particulière...",
    "ai.analyzing": "Analyse en cours...",
    "ai.getSuggestions": "Obtenir des suggestions",
    "ai.generated": "Suggestions générées!",
    "ai.errorGenerating": "Erreur lors de la génération",
    "ai.basedOn": "Basé sur",
    "ai.entriesAnalyzed": "entrées analysées",
    "ai.averageMood": "Humeur moyenne:",
    "ai.newSuggestions": "Nouvelles suggestions",
    "ai.noHistory": "Aucun historique de suggestions",
    "ai.avg": "Moy:",

    // MoodChart extras
    "chart.mood": "Humeur:",
    "chart.notEnoughData": "Pas assez de données pour afficher le graphique",
    "chart.avg": "Moy:",

    // ExerciseIntro extras
    "exerciseIntro.objectives": "Objectifs de l'exercice",
    "exerciseIntro.understand": "Comprendre l'exercice",
    "exerciseIntro.foundation": "Fondement :",

    // Exercise Data - Thought Record
    "exerciseData.emotion_register.title": "Registre d'émotions",
    "exerciseData.emotion_register.obj1": "Identifier et nommer précisément les émotions ressenties",
    "exerciseData.emotion_register.obj2": "Questionner la proportionnalité de l'émotion face à la situation",
    "exerciseData.emotion_register.obj3": "Externaliser l'émotion pour créer une distance thérapeutique",
    "exerciseData.emotion_register.obj4": "Développer des réponses émotionnelles plus adaptées",
    "exerciseData.emotion_register.explanation": "Le registre d'émotions est un outil de régulation émotionnelle basé sur le questionnement socratique. Plutôt que de subir vos émotions, vous apprenez à les observer, les questionner et les externaliser. En écrivant directement à votre émotion comme si c'était une entité séparée, vous créez une distance qui permet de ne pas tomber dans une attitude négative et de choisir une réponse plus adaptée.",
    "exerciseData.emotion_register.foundation": "Méthode socratique — Régulation émotionnelle (Beck, Greenberger & Padesky)",
    // Exercise Data - Behavioral Activation
    "exerciseData.behavioral_activation.title": "Activation comportementale",
    "exerciseData.behavioral_activation.obj1": "Augmenter les activités agréables et valorisantes",
    "exerciseData.behavioral_activation.obj2": "Briser le cycle de l'évitement et de l'inactivité",
    "exerciseData.behavioral_activation.obj3": "Améliorer l'humeur par l'action",
    "exerciseData.behavioral_activation.obj4": "Développer une routine quotidienne équilibrée",
    "exerciseData.behavioral_activation.explanation": "L'activation comportementale repose sur le principe que nos comportements influencent directement notre humeur. Quand on se sent déprimé, on a tendance à s'isoler et à éviter les activités, ce qui aggrave l'état émotionnel. En planifiant progressivement des activités significatives, on peut inverser ce cycle négatif.",
    "exerciseData.behavioral_activation.foundation": "Jacobson et al. — Activation comportementale (1996)",
    // Exercise Data - Mood Tracking
    "exerciseData.mood_tracking.title": "Suivi de l'humeur",
    "exerciseData.mood_tracking.obj1": "Prendre conscience de ses états émotionnels",
    "exerciseData.mood_tracking.obj2": "Identifier les patterns et déclencheurs émotionnels",
    "exerciseData.mood_tracking.obj3": "Mesurer l'évolution de l'humeur au fil du temps",
    "exerciseData.mood_tracking.obj4": "Faciliter la communication avec le thérapeute",
    "exerciseData.mood_tracking.explanation": "Le suivi quotidien de l'humeur est un outil essentiel de la TCC. En notant régulièrement votre niveau d'humeur et vos émotions, vous développez une meilleure conscience émotionnelle. Cela permet d'identifier des patterns, des déclencheurs récurrents et de mesurer objectivement vos progrès au cours de la thérapie.",
    "exerciseData.mood_tracking.foundation": "Beck et al. — Auto-surveillance en TCC (1979)",
    // Exercise Data - Pomodoro
    "exerciseData.pomodoro.title": "Technique Pomodoro",
    "exerciseData.pomodoro.obj1": "Améliorer la concentration et la productivité",
    "exerciseData.pomodoro.obj2": "Réduire la procrastination et l'anxiété liée aux tâches",
    "exerciseData.pomodoro.obj3": "Apprendre à gérer son temps efficacement",
    "exerciseData.pomodoro.obj4": "Intégrer des pauses régulières pour le bien-être",
    "exerciseData.pomodoro.explanation": "La technique Pomodoro divise le travail en intervalles de concentration (25 minutes) suivis de courtes pauses. Cette méthode est particulièrement utile en TCC pour les patients souffrant d'anxiété de performance ou de procrastination, car elle rend les tâches moins intimidantes en les découpant en segments gérables.",
    "exerciseData.pomodoro.foundation": "Francesco Cirillo — Technique Pomodoro (1980s)",
    // Exercise Data - Cognitive Defusion
    "exerciseData.cognitive_defusion.title": "Défusion cognitive",
    "exerciseData.cognitive_defusion.obj1": "Se distancier de ses pensées négatives",
    "exerciseData.cognitive_defusion.obj2": "Réduire l'impact émotionnel des pensées",
    "exerciseData.cognitive_defusion.obj3": "Observer ses pensées sans s'y identifier",
    "exerciseData.cognitive_defusion.obj4": "Développer une flexibilité psychologique",
    "exerciseData.cognitive_defusion.explanation": "La défusion cognitive est une technique issue de la thérapie d'acceptation et d'engagement (ACT). Plutôt que de chercher à changer le contenu des pensées, elle nous apprend à modifier notre relation avec elles. L'objectif est de voir les pensées comme de simples événements mentaux, et non comme des vérités absolues.",
    "exerciseData.cognitive_defusion.foundation": "Steven Hayes — ACT et défusion cognitive (1999)",
    // Exercise Data - Meditation
    "exerciseData.meditation.title": "Méditation pleine conscience",
    "exerciseData.meditation.obj1": "Développer l'attention au moment présent",
    "exerciseData.meditation.obj2": "Réduire le stress et l'anxiété",
    "exerciseData.meditation.obj3": "Améliorer la régulation émotionnelle",
    "exerciseData.meditation.obj4": "Cultiver une attitude de non-jugement",
    "exerciseData.meditation.explanation": "La méditation de pleine conscience consiste à porter une attention intentionnelle au moment présent, sans jugement. Intégrée à la TCC sous forme de MBCT (Mindfulness-Based Cognitive Therapy), elle aide à prévenir les rechutes dépressives et à mieux gérer l'anxiété en développant une conscience détachée de ses pensées et émotions.",
    "exerciseData.meditation.foundation": "Jon Kabat-Zinn — MBSR (1979) / Segal, Williams, Teasdale — MBCT (2002)",
    // Exercise Data - Gratitude
    "exerciseData.gratitude.title": "Journal de gratitude",
    "exerciseData.gratitude.obj1": "Orienter l'attention vers les aspects positifs",
    "exerciseData.gratitude.obj2": "Contrebalancer le biais de négativité naturel",
    "exerciseData.gratitude.obj3": "Augmenter le bien-être subjectif",
    "exerciseData.gratitude.obj4": "Renforcer les relations sociales positives",
    "exerciseData.gratitude.explanation": "Le journal de gratitude est un exercice de psychologie positive intégré à la TCC. En notant régulièrement les choses pour lesquelles vous êtes reconnaissant, vous entraînez votre cerveau à remarquer davantage les aspects positifs de votre vie. Cela aide à contrebalancer la tendance naturelle du cerveau à se focaliser sur le négatif.",
    "exerciseData.gratitude.foundation": "Robert Emmons — Recherche sur la gratitude (2003)",
    // Exercise Data - Self Compassion
    "exerciseData.self_compassion.title": "Auto-compassion",
    "exerciseData.self_compassion.obj1": "Développer une attitude bienveillante envers soi-même",
    "exerciseData.self_compassion.obj2": "Réduire l'auto-critique excessive",
    "exerciseData.self_compassion.obj3": "Renforcer la résilience émotionnelle",
    "exerciseData.self_compassion.obj4": "Accepter ses imperfections avec humanité",
    "exerciseData.self_compassion.explanation": "L'auto-compassion implique de se traiter avec la même bienveillance que l'on offrirait à un ami cher traversant une difficulté. Elle comprend trois composantes : la bienveillance envers soi, la reconnaissance de notre humanité commune, et la pleine conscience de nos souffrances sans s'y identifier excessivement.",
    "exerciseData.self_compassion.foundation": "Kristin Neff — Auto-compassion (2003)",
    // Exercise Data - Values Exploration
    "exerciseData.values_exploration.title": "Exploration des valeurs",
    "exerciseData.values_exploration.obj1": "Identifier ses valeurs fondamentales",
    "exerciseData.values_exploration.obj2": "Évaluer l'alignement entre ses actions et ses valeurs",
    "exerciseData.values_exploration.obj3": "Définir des actions concrètes guidées par ses valeurs",
    "exerciseData.values_exploration.obj4": "Donner un sens et une direction à sa vie",
    "exerciseData.values_exploration.explanation": "L'exploration des valeurs est un exercice clé de l'ACT. Les valeurs sont comme une boussole qui guide nos choix et nos actions. Contrairement aux objectifs, elles ne sont jamais atteintes mais nous donnent une direction. En clarifiant ce qui compte vraiment pour vous, vous pouvez prendre des décisions plus alignées avec votre être profond.",
    "exerciseData.values_exploration.foundation": "Russ Harris — ACT Made Simple (2009)",
    // Exercise Data - Needs Exploration
    "exerciseData.needs_exploration.title": "Exploration des besoins",
    "exerciseData.needs_exploration.obj1": "Identifier ses besoins fondamentaux non satisfaits",
    "exerciseData.needs_exploration.obj2": "Comprendre la hiérarchie des besoins humains",
    "exerciseData.needs_exploration.obj3": "Évaluer le niveau de satisfaction de chaque besoin",
    "exerciseData.needs_exploration.obj4": "Planifier des actions pour mieux répondre à ses besoins",
    "exerciseData.needs_exploration.explanation": "Basé sur la pyramide de Maslow, cet exercice vous aide à évaluer dans quelle mesure vos besoins fondamentaux sont satisfaits. En TCC, comprendre ses besoins non satisfaits permet d'identifier les sources de mal-être et de définir des actions concrètes pour améliorer sa qualité de vie à chaque niveau.",
    "exerciseData.needs_exploration.foundation": "Abraham Maslow — Hiérarchie des besoins (1943)",
    // Exercise Data - Problem Solving
    "exerciseData.problem_solving.title": "Résolution de problèmes",
    "exerciseData.problem_solving.obj1": "Structurer l'approche face aux problèmes",
    "exerciseData.problem_solving.obj2": "Générer plusieurs solutions alternatives",
    "exerciseData.problem_solving.obj3": "Évaluer objectivement les avantages et inconvénients",
    "exerciseData.problem_solving.obj4": "Élaborer un plan d'action concret",
    "exerciseData.problem_solving.explanation": "La thérapie de résolution de problèmes est une approche structurée qui aide à faire face aux difficultés de manière systématique. Elle est particulièrement utile quand on se sent dépassé ou bloqué. En décomposant un problème complexe en étapes gérables, on retrouve un sentiment de contrôle et de compétence.",
    "exerciseData.problem_solving.foundation": "D'Zurilla & Goldfried — Problem-Solving Therapy (1971)",
    // Exercise Data - Qualities Exploration
    "exerciseData.qualities_exploration.title": "Exploration des qualités",
    "exerciseData.qualities_exploration.obj1": "Reconnaître et valoriser ses qualités personnelles",
    "exerciseData.qualities_exploration.obj2": "Contrebalancer la tendance à se focaliser sur ses défauts",
    "exerciseData.qualities_exploration.obj3": "Renforcer l'estime de soi de manière réaliste",
    "exerciseData.qualities_exploration.obj4": "Identifier les qualités à développer davantage",
    "exerciseData.qualities_exploration.explanation": "Cet exercice vous aide à identifier et reconnaître vos qualités personnelles. En TCC, les personnes souffrant de faible estime de soi ont tendance à minimiser leurs qualités et à amplifier leurs défauts. En documentant concrètement vos qualités avec des exemples, vous construisez une image de soi plus équilibrée et réaliste.",
    "exerciseData.qualities_exploration.foundation": "Christine Padesky — Modèle de résilience basé sur les forces (2012)",
    // Exercise Data - Capabilities Exploration
    "exerciseData.capabilities_exploration.title": "Exploration des compétences",
    "exerciseData.capabilities_exploration.obj1": "Inventorier ses compétences dans différents domaines",
    "exerciseData.capabilities_exploration.obj2": "Reconnaître ses niveaux de maîtrise",
    "exerciseData.capabilities_exploration.obj3": "Identifier les compétences à développer",
    "exerciseData.capabilities_exploration.obj4": "Renforcer le sentiment d'auto-efficacité",
    "exerciseData.capabilities_exploration.explanation": "L'exploration des compétences aide à dresser un inventaire réaliste de ce que vous savez faire. Le sentiment d'auto-efficacité — la croyance en sa capacité à accomplir des tâches — est un facteur clé du bien-être psychologique. En identifiant vos compétences et en planifiant leur développement, vous renforcez votre confiance en vous.",
    "exerciseData.capabilities_exploration.foundation": "Albert Bandura — Auto-efficacité (1977)",
    // Exercise Data - Assertiveness
    "exerciseData.assertiveness.title": "Communication assertive",
    "exerciseData.assertiveness.obj1": "Identifier son style de communication habituel",
    "exerciseData.assertiveness.obj2": "Apprendre la technique DESC pour s'affirmer",
    "exerciseData.assertiveness.obj3": "Exprimer ses besoins tout en respectant les autres",
    "exerciseData.assertiveness.obj4": "Développer des relations interpersonnelles plus saines",
    "exerciseData.assertiveness.explanation": "L'assertivité est la capacité à exprimer ses besoins, opinions et émotions de manière directe et respectueuse. La technique DESC (Décrire, Exprimer, Spécifier, Conséquences) est un outil structuré pour formuler des messages assertifs. Elle est particulièrement utile dans les situations de conflit ou quand on a du mal à poser ses limites.",
    "exerciseData.assertiveness.foundation": "Sharon Anthony Bower — Asserting Yourself (1976)",
    // Exercise Data - Self Reinforcement
    "exerciseData.self_reinforcement.title": "Auto-renforcement",
    "exerciseData.self_reinforcement.obj1": "Reconnaître et célébrer ses accomplissements",
    "exerciseData.self_reinforcement.obj2": "Développer un système de récompenses saines",
    "exerciseData.self_reinforcement.obj3": "Renforcer les comportements positifs",
    "exerciseData.self_reinforcement.obj4": "Augmenter la motivation intrinsèque",
    "exerciseData.self_reinforcement.explanation": "L'auto-renforcement est basé sur les principes du conditionnement opérant. En vous récompensant après avoir accompli une tâche difficile ou adopté un comportement positif, vous augmentez la probabilité de reproduire ce comportement. C'est un outil puissant pour maintenir la motivation et construire de nouvelles habitudes.",
    "exerciseData.self_reinforcement.foundation": "B.F. Skinner — Renforcement positif (1938)",
    // Exercise Data - Cognitive Restructuring
    "exerciseData.cognitive_restructuring.title": "Restructuration cognitive",
    "exerciseData.cognitive_restructuring.obj1": "Identifier les schémas de pensée dysfonctionnels",
    "exerciseData.cognitive_restructuring.obj2": "Remettre en question les croyances irrationnelles",
    "exerciseData.cognitive_restructuring.obj3": "Développer des pensées plus réalistes et adaptées",
    "exerciseData.cognitive_restructuring.obj4": "Réduire les symptômes anxieux et dépressifs",
    "exerciseData.cognitive_restructuring.explanation": "La restructuration cognitive est au cœur de la TCC. Elle consiste à identifier les pensées automatiques négatives et les schémas cognitifs dysfonctionnels, puis à les examiner de manière critique pour développer des alternatives plus réalistes et adaptées. Ce processus modifie progressivement les croyances fondamentales qui maintiennent la souffrance psychologique.",
    "exerciseData.cognitive_restructuring.foundation": "Aaron Beck — Triade cognitive et schémas (1979)",

    // Psychoeducation - CBT Model
    "psycho.cbt.title": "Le Modèle de la TCC",
    "psycho.cbt.subtitle": "Thérapies Cognitives et Comportementales",
    "psycho.cbt.desc": "Comprendre le lien entre pensées, émotions et comportements",
    "psycho.cbt.s1t": "Introduction aux TCC",
    "psycho.cbt.s1c": "Les Thérapies Cognitives et Comportementales (TCC) sont un ensemble de traitements psychologiques validés scientifiquement, recommandés par la Haute Autorité de Santé (HAS) et l'Organisation Mondiale de la Santé (OMS). Développées dans les années 1960 par Aaron Beck et Albert Ellis, elles reposent sur un principe fondamental : nos pensées influencent nos émotions, qui à leur tour influencent nos comportements. Ce cercle peut être vertueux ou vicieux. Contrairement aux thérapies analytiques, les TCC se concentrent sur le présent, sont structurées, et visent des objectifs concrets. Le thérapeute et le patient travaillent en collaboration active, comme une équipe. Les TCC ont prouvé leur efficacité dans le traitement de la dépression, des troubles anxieux, des phobies, du TOC, du SSPT, des troubles alimentaires et de nombreuses autres pathologies.",
    "psycho.cbt.s2t": "Le triangle cognitif",
    "psycho.cbt.s2c": "Le modèle central de la TCC est le « triangle cognitif » qui illustre l'interaction entre trois composantes : les pensées (cognitions), les émotions et les comportements. Voici comment il fonctionne : une situation déclenche des pensées automatiques, qui génèrent des émotions, qui entraînent des comportements. Exemple concret : votre ami ne répond pas à votre message (situation) -> « Il m'en veut, je ne compte pas pour lui » (pensée automatique) -> anxiété, tristesse (émotions) -> vous envoyez 10 messages de suite ou vous ne le recontactez plus (comportement). En TCC, on apprend à intervenir à chaque sommet de ce triangle. On peut modifier la pensée (restructuration cognitive), réguler l'émotion (techniques de relaxation) ou changer le comportement (activation comportementale). Modifier un seul élément impacte les deux autres.",
    "psycho.cbt.s3t": "Les distorsions cognitives",
    "psycho.cbt.s3c": "Aaron Beck a identifié des erreurs de raisonnement récurrentes appelées « distorsions cognitives ». Ce ne sont pas des mensonges délibérés, mais des filtres automatiques qui déforment notre perception de la réalité. Les principales distorsions sont : (1) Pensée tout-ou-rien : voir les choses en noir ou blanc, sans nuance (« Si ce n'est pas parfait, c'est nul »). (2) Catastrophisation : imaginer le pire scénario possible (« Si j'échoue à cet examen, ma vie est foutue »). (3) Surgénéralisation : tirer une conclusion générale d'un seul événement (« J'ai raté ce rendez-vous, je rate toujours tout »). (4) Lecture de pensée : supposer ce que les autres pensent sans preuve (« Il pense que je suis incompétent »). (5) Disqualification du positif : rejeter les expériences positives (« Ils m'ont complimenté par politesse »). Reconnaître ces biais est la première étape pour les corriger.",
    "psycho.cbt.s4t": "Les trois vagues de la TCC",
    "psycho.cbt.s4c": "La TCC a évolué en trois vagues successives, chacune enrichissant la précédente. La 1ère vague (années 1950-60) est comportementale : elle se concentre sur les comportements observables, le conditionnement (Pavlov, Skinner) et les techniques d'exposition. La 2ème vague (années 1960-80) intègre la cognition grâce aux travaux de Beck et Ellis : on s'intéresse aux pensées automatiques, aux schémas cognitifs et à la restructuration cognitive. La 3ème vague (années 2000 à aujourd'hui) ajoute l'acceptation, la pleine conscience et les valeurs. Elle inclut l'ACT (Thérapie d'Acceptation et d'Engagement de Hayes), la MBCT (Thérapie Cognitive basée sur la Pleine Conscience), et la thérapie des schémas de Young. Cette dernière vague ne cherche plus à contrôler les pensées négatives mais à modifier notre relation avec elles.",
    "psycho.cbt.q1q": "Quel est le principe fondamental de la TCC ?",
    "psycho.cbt.q1a": "Nos comportements sont uniquement déterminés par notre enfance",
    "psycho.cbt.q1b": "Nos pensées influencent nos émotions et nos comportements",
    "psycho.cbt.q1c": "Les émotions sont incontrôlables",
    "psycho.cbt.q1d": "Seuls les médicaments peuvent traiter les troubles psychologiques",
    "psycho.cbt.q1e": "La TCC repose sur l'idée que nos pensées influencent nos émotions et nos comportements, et qu'en modifiant nos pensées, on peut changer nos réactions.",
    "psycho.cbt.q2q": "Qu'est-ce qu'une distorsion cognitive ?",
    "psycho.cbt.q2a": "Un trouble neurologique",
    "psycho.cbt.q2b": "Une erreur de raisonnement récurrente qui déforme la réalité",
    "psycho.cbt.q2c": "Un mensonge délibéré",
    "psycho.cbt.q2d": "Un symptôme de schizophrénie",
    "psycho.cbt.q2e": "Les distorsions cognitives sont des biais systématiques dans le traitement de l'information, pas des mensonges volontaires.",
    // Psychoeducation - Anxiety
    "psycho.anx.title": "Modèle Cognitif de l'Anxiété",
    "psycho.anx.subtitle": "Aaron Beck",
    "psycho.anx.desc": "Comprendre les mécanismes cognitifs de l'anxiété selon Beck",
    "psycho.anx.s1t": "Le modèle de Beck pour l'anxiété",
    "psycho.anx.s1c": "Selon Aaron Beck (1976), l'anxiété n'est pas causée directement par les situations elles-mêmes, mais par notre interprétation de ces situations. Le modèle cognitif de l'anxiété repose sur un double biais : une surestimation systématique du danger (« C'est très dangereux ») et une sous-estimation de ses propres capacités à y faire face (« Je ne vais pas y arriver »). Ce déséquilibre entre menace perçue et ressources perçues crée l'état anxieux. Par exemple, pour un examen : une personne anxieuse pensera « Je vais tout rater, les questions seront impossibles » (surestimation) tout en pensant « Je suis incapable de me préparer correctement » (sous-estimation).",
    "psycho.anx.s2t": "Les schémas de danger",
    "psycho.anx.s2c": "Les personnes anxieuses développent des « schémas de danger » : des structures cognitives profondes centrées sur la vulnérabilité et la menace, formées souvent durant l'enfance (surprotection parentale, expériences traumatiques, apprentissage vicariant). Ces schémas fonctionnent comme des filtres : un événement neutre est automatiquement interprété comme menaçant. Par exemple, un regard d'un collègue sera perçu comme du jugement, un petit retard sera interprété comme un signe de catastrophe imminente. Ces schémas sont sélectifs : ils captent uniquement les informations qui confirment le danger et ignorent les signaux de sécurité. C'est ce qu'on appelle le biais attentionnel vers la menace.",
    "psycho.anx.s3t": "Le cercle vicieux de l'anxiété",
    "psycho.anx.s3c": "L'anxiété s'auto-entretient par un cercle vicieux en 5 étapes : (1) Perception d'une menace (réelle ou imaginée). (2) Activation physiologique : le corps se prépare au danger (accélération cardiaque, transpiration, tensions musculaires, hyperventilation). (3) Interprétation catastrophique des sensations : « Mon coeur bat vite, je fais un infarctus ! ». (4) Augmentation de l'anxiété et renforcement du schéma de danger. (5) Comportements d'évitement ou de sécurité qui procurent un soulagement temporaire mais empêchent de vérifier que le danger n'était pas réel. Ce cercle vicieux est au coeur du trouble panique, de l'agoraphobie et du trouble anxieux généralisé.",
    "psycho.anx.s4t": "Les pensées automatiques anxieuses",
    "psycho.anx.s4c": "Les pensées automatiques dans l'anxiété ont des caractéristiques spécifiques. Elles commencent souvent par « Et si... ? » (et si je fais une crise de panique ? et si on me juge ?). Elles surestiment à la fois la probabilité et la gravité du scénario redouté. Elles sont rapides, involontaires et paraissent totalement crédibles sur le moment. Les techniques de la TCC pour les combattre incluent : le questionnement socratique (« Quelle est la probabilité réelle ? »), la décentration (« Que dirais-je à un ami ? »), la technique de la flèche descendante pour identifier les croyances profondes, et l'exposition graduée pour tester les prédictions anxieuses dans la réalité.",
    "psycho.anx.q1q": "Selon Beck, l'anxiété est maintenue par :",
    "psycho.anx.q1a": "Un excès de sommeil",
    "psycho.anx.q1b": "Une surestimation du danger et une sous-estimation de ses capacités",
    "psycho.anx.q1c": "Un manque de vitamines",
    "psycho.anx.q1d": "Une personnalité faible",
    "psycho.anx.q1e": "Le modèle cognitif de Beck montre que l'anxiété repose sur un double biais : surestimer les menaces et sous-estimer sa capacité à y faire face.",
    "psycho.anx.q2q": "Pourquoi les comportements d'évitement renforcent-ils l'anxiété ?",
    "psycho.anx.q2a": "Parce qu'ils fatiguent physiquement la personne",
    "psycho.anx.q2b": "Parce qu'ils empêchent de vérifier que le danger n'est pas réel",
    "psycho.anx.q2c": "Parce qu'ils provoquent des effets secondaires",
    "psycho.anx.q2d": "Parce qu'ils n'ont aucun effet",
    "psycho.anx.q2e": "L'évitement procure un soulagement temporaire mais empêche le cerveau d'apprendre que la situation n'est pas dangereuse, ce qui maintient et renforce l'anxiété.",
    // Psychoeducation - Depression
    "psycho.dep.title": "Modèle Cognitif de la Dépression",
    "psycho.dep.subtitle": "Aaron Beck",
    "psycho.dep.desc": "La triade cognitive et les schémas dépressogènes de Beck",
    "psycho.dep.s1t": "La triade cognitive de Beck",
    "psycho.dep.s1c": "Aaron Beck (1967) a identifié trois types de pensées négatives caractéristiques de la dépression, qu'il a nommé la « triade cognitive ». (1) Vision négative de soi : « Je suis nul, je ne vaux rien, je suis un poids pour les autres ». (2) Vision négative du monde : « Le monde est injuste, rien ne va, personne ne peut m'aider ». (3) Vision négative de l'avenir : « Rien ne s'améliorera jamais, c'est sans espoir, à quoi bon essayer ». Ces trois composantes se renforcent mutuellement et créent un filtre négatif global à travers lequel la personne déprimée interprète toute son expérience. La triade cognitive est au coeur de la compréhension et du traitement de la dépression en TCC.",
    "psycho.dep.s2t": "Les schémas dépressogènes",
    "psycho.dep.s2c": "Les schémas dépressogènes sont des croyances profondes et rigides formées dès l'enfance à partir des expériences précoces (critiques parentales, rejet, négligence, perte). Ils restent latents jusqu'à être activés par un événement stressant (perte d'emploi, rupture, échec). Exemples de schémas : « Je ne suis pas digne d'être aimé » (schéma d'incompétence), « Si les autres me connaissaient vraiment, ils me rejetteraient » (schéma de défectuosité), « Je dois être parfait pour être acceptable » (schéma de standards élevés). Une fois activés, ces schémas provoquent un flux de pensées automatiques négatives qui confirment la triade cognitive. La thérapie des schémas de Jeffrey Young (1990) a développé des techniques spécifiques pour modifier ces structures profondes.",
    "psycho.dep.s3t": "Le cercle vicieux de la dépression",
    "psycho.dep.s3c": "La dépression s'auto-entretient par un cercle vicieux puissant : les pensées négatives (« Je suis nul, rien n'a de sens ») entraînent une baisse de motivation et d'énergie, qui provoque une réduction des activités (arrêt du sport, isolement social, négligence des loisirs). Cette inactivité prive la personne de sources de plaisir et d'accomplissement, ce qui « confirme » les pensées négatives (« Tu vois, je ne suis capable de rien »). Le retrait social réduit aussi le soutien des proches. Ce cercle peut être brisé par l'activation comportementale : reprendre des activités agréables, même sans motivation initiale, pour recréer des expériences positives qui contredisent les pensées négatives.",
    "psycho.dep.s4t": "Le traitement cognitif de la dépression",
    "psycho.dep.s4c": "Le traitement cognitif de la dépression suit une progression structurée. Phase 1 : Psychoéducation et activation comportementale - le patient apprend le modèle et reprend des activités gratifiantes. Phase 2 : Identification des pensées automatiques - le patient utilise un tableau de pensées pour repérer les pensées négatives récurrentes. Phase 3 : Restructuration cognitive - les pensées automatiques sont examinées comme des hypothèses, pas des faits. On cherche les preuves pour et contre, et on formule des pensées alternatives. Phase 4 : Modification des schémas profonds - le travail porte sur les croyances fondamentales. Les méta-analyses montrent que la TCC est aussi efficace que les antidépresseurs pour les dépressions légères à modérées, avec un taux de rechute plus faible à long terme.",
    "psycho.dep.q1q": "Quels sont les trois éléments de la triade cognitive de Beck ?",
    "psycho.dep.q1a": "Passé, présent, futur",
    "psycho.dep.q1b": "Vision négative de soi, du monde et de l'avenir",
    "psycho.dep.q1c": "Tristesse, colère, peur",
    "psycho.dep.q1d": "Pensée, émotion, comportement",
    "psycho.dep.q1e": "La triade cognitive de Beck décrit les trois domaines de pensées négatives caractéristiques de la dépression.",
    "psycho.dep.q2q": "Pourquoi l'activation comportementale est-elle efficace contre la dépression ?",
    "psycho.dep.q2a": "Elle élimine les pensées négatives instantanément",
    "psycho.dep.q2b": "Elle recrée des expériences positives qui contredisent les pensées négatives",
    "psycho.dep.q2c": "Elle remplace les médicaments",
    "psycho.dep.q2d": "Elle supprime la fatigue",
    "psycho.dep.q2e": "En reprenant des activités agréables, la personne génère des expériences positives qui brisent le cercle vicieux pensées négatives -> inactivité -> confirmation des pensées.",
    // Psychoeducation - Grey
    "psycho.grey.title": "Neurobiologie de Grey",
    "psycho.grey.subtitle": "Jeffrey Alan Grey",
    "psycho.grey.desc": "Les systèmes d'activation et d'inhibition comportementale",
    "psycho.grey.s1t": "La Théorie de la Sensibilité au Renforcement",
    "psycho.grey.s1c": "Jeffrey Alan Grey (1982) a proposé une théorie neurobiologique de la personnalité basée sur deux systèmes cérébraux fondamentaux qui régulent notre comportement face aux récompenses et aux punitions. Le BAS (Behavioral Activation System / Système d'Activation Comportementale) nous pousse vers les récompenses, tandis que le BIS (Behavioral Inhibition System / Système d'Inhibition Comportementale) nous freine face aux menaces. Chaque individu possède un équilibre unique entre ces deux systèmes, ce qui explique les différences de tempérament et de vulnérabilité aux troubles psychologiques. Cette théorie est l'un des modèles les plus influents en psychologie de la personnalité.",
    "psycho.grey.s2t": "Le BAS : Système d'Activation Comportementale",
    "psycho.grey.s2c": "Le BAS est activé par les signaux de récompense, de non-punition et d'évasion de la punition. Neurobiologiquement, il est associé aux voies dopaminergiques (circuit de la récompense). Quand le BAS est activé, il génère des émotions positives (espoir, enthousiasme, anticipation du plaisir) et des comportements d'approche et de recherche active. Un BAS très réactif est associé à l'extraversion, l'optimisme et la prise de risque. En revanche, un BAS sous-actif peut contribuer à la dépression (anhédonie, perte de motivation, absence de plaisir). L'activation comportementale en TCC vise justement à restimler le BAS chez les personnes déprimées.",
    "psycho.grey.s3t": "Le BIS : Système d'Inhibition Comportementale",
    "psycho.grey.s3c": "Le BIS est activé par les signaux de punition, de non-récompense, de nouveauté et de stimuli innés de peur. Il est associé au circuit septohippocampique et à la sérotonine. Quand le BIS est activé, il produit une inhibition du comportement en cours, une augmentation de la vigilance et de l'attention, et une activation physiologique (arousal). Émotionnellement, cela se traduit par de l'anxiété et de l'appréhension. Un BIS hyperactif est fortement associé au névrosisme, à l'anxiété chronique, aux phobies et au trouble anxieux généralisé. Les anxiolytiques (benzodiazépines) agissent en réduisant l'activité du BIS, ce qui confirme le modèle de Grey.",
    "psycho.grey.s4t": "Implications cliniques et thérapeutiques",
    "psycho.grey.s4c": "Le modèle de Grey a des implications thérapeutiques directes. Pour la dépression (BAS sous-actif) : l'activation comportementale vise à augmenter les comportements d'approche et les expériences de récompense pour restimler le BAS. Pour l'anxiété (BIS hyperactif) : l'exposition graduée vise à réduire la sensibilité du BIS aux stimuli menaçants. Pour les addictions (BAS hyperactif) : le travail porte sur la régulation de l'impulsivité et la recherche de récompenses saines. Comprendre son profil BAS/BIS aide le patient à mieux comprendre ses réactions automatiques et à développer des stratégies adaptées. Un questionnaire (BIS/BAS Scales de Carver & White, 1994) permet d'évaluer son propre profil.",
    "psycho.grey.q1q": "Quel système de Grey est associé à l'anxiété ?",
    "psycho.grey.q1a": "Le BAS (Behavioral Activation System)",
    "psycho.grey.q1b": "Le BIS (Behavioral Inhibition System)",
    "psycho.grey.q1c": "Le système nerveux parasympathique",
    "psycho.grey.q1d": "Le cortex préfrontal",
    "psycho.grey.q1e": "Le BIS est le système d'inhibition qui, lorsqu'il est hyperactif, est associé à l'anxiété et l'hypervigilance.",
    "psycho.grey.q2q": "Quel trouble est associé à un BAS sous-actif ?",
    "psycho.grey.q2a": "Le trouble anxieux généralisé",
    "psycho.grey.q2b": "La dépression (anhédonie et perte de motivation)",
    "psycho.grey.q2c": "Le trouble obsessionnel-compulsif",
    "psycho.grey.q2d": "La phobie sociale",
    "psycho.grey.q2e": "Un BAS sous-actif signifie que le système de récompense fonctionne moins bien, ce qui entraîne une perte de motivation, d'intérêt et de plaisir - symptômes centraux de la dépression.",
    // Psychoeducation - Conditioning
    "psycho.cond.title": "Conditionnement",
    "psycho.cond.subtitle": "Pavlov & Skinner",
    "psycho.cond.desc": "Les bases du conditionnement classique et opérant",
    "psycho.cond.s1t": "Le conditionnement classique (Pavlov)",
    "psycho.cond.s1c": "Ivan Pavlov (1849-1936) a découvert le conditionnement classique en étudiant la salivation des chiens. Principe : lorsqu'un stimulus neutre (une cloche) est associé de manière répétée à un stimulus inconditionnel (de la nourriture), le stimulus neutre finit par déclencher seul la réponse (salivation). En psychopathologie, ce mécanisme explique l'acquisition des phobies et du SSPT : un événement traumatisant (accident de voiture) associe la peur à des stimuli neutres (voitures, routes). Par la suite, ces stimuli déclenchent seuls la réponse de peur. Le cas du « Petit Albert » (Watson, 1920) a démontré expérimentalement que les phobies pouvaient être acquises par conditionnement classique, ouvrant la voie aux traitements par exposition.",
    "psycho.cond.s2t": "Le conditionnement opérant (Skinner)",
    "psycho.cond.s2c": "B.F. Skinner (1904-1990) a montré que les comportements sont façonnés par leurs conséquences. Quatre mécanismes clés : (1) Renforcement positif : ajouter quelque chose d'agréable pour augmenter un comportement (compliment apres un effort). (2) Renforcement négatif : retirer quelque chose de désagréable pour augmenter un comportement (l'évitement soulage l'anxiété, donc on évite davantage). (3) Punition positive : ajouter quelque chose de désagréable pour diminuer un comportement (critique après une erreur). (4) Punition négative : retirer quelque chose d'agréable pour diminuer un comportement (retirer un privilège). Le renforcement négatif est le mécanisme le plus important en psychopathologie car il explique le maintien des comportements d'évitement dans l'anxiété.",
    "psycho.cond.s3t": "Applications en TCC : l'exposition",
    "psycho.cond.s3c": "Le traitement des phobies et du SSPT par exposition progressive est directement issu des principes de Pavlov. Le principe : lorsqu'on expose répétitivement une personne au stimulus redouté sans que la conséquence négative ne se produise, l'association peur-stimulus s'affaiblit progressivement. C'est l'extinction. L'exposition peut être : in vivo (situation réelle), en imagination, en réalité virtuelle, ou intéroceptive (pour le trouble panique - on provoque les sensations physiques redoutées). La clé du succès est de rester dans la situation assez longtemps pour que l'anxiété diminue naturellement (habituation). La fuite ou l'évitement pendant l'exposition renforce la peur. L'exposition est l'un des traitements les plus efficaces en psychothérapie, avec des taux de réussite de 70-90% pour les phobies spécifiques.",
    "psycho.cond.s4t": "L'évitement : le piège du renforcement négatif",
    "psycho.cond.s4c": "L'évitement est le mécanisme central qui maintient l'anxiété, et il fonctionne par renforcement négatif. Voici le piège : quand on évite une situation anxiogène, on ressent un soulagement immédiat (l'anxiété baisse). Ce soulagement « récompense » le cerveau et renforce le comportement d'évitement. Le problème : on n'a jamais l'occasion de vérifier que la situation n'est pas réellement dangereuse. L'évitement s'étend progressivement : d'abord on évite l'ascenseur, puis les étages élevés, puis les immeubles, puis on ne sort plus de chez soi. Ce phénomène de « généralisation de l'évitement » peut mener à l'agoraphobie sévère. En TCC, on inverse ce mécanisme par l'exposition graduée, en affrontant progressivement les situations évitées.",
    "psycho.cond.q1q": "Pourquoi l'évitement maintient-il l'anxiété ?",
    "psycho.cond.q1a": "Parce qu'il demande beaucoup d'énergie",
    "psycho.cond.q1b": "Parce que le soulagement temporaire renforce le comportement d'évitement (renforcement négatif)",
    "psycho.cond.q1c": "Parce qu'il cause des maux de tête",
    "psycho.cond.q1d": "Parce qu'il n'a aucun effet",
    "psycho.cond.q1e": "L'évitement procure un soulagement immédiat qui renforce la tendance à éviter, mais empêche de découvrir que la situation n'est pas dangereuse.",
    "psycho.cond.q2q": "Qu'est-ce que l'extinction dans le conditionnement classique ?",
    "psycho.cond.q2a": "La disparition d'une espèce animale",
    "psycho.cond.q2b": "L'affaiblissement progressif de la réponse conditionnée quand le stimulus n'est plus associé à la conséquence",
    "psycho.cond.q2c": "L'augmentation de la peur face au stimulus",
    "psycho.cond.q2d": "L'oubli total du stimulus conditionné",
    "psycho.cond.q2e": "L'extinction se produit quand le stimulus conditionné (ex: ascenseur) est présenté répétitivement sans le stimulus inconditionnel (danger). La réponse de peur s'affaiblit progressivement. C'est le principe de l'exposition thérapeutique.",
    // Psychoeducation - Darwin
    "psycho.darwin.title": "Les Émotions selon Darwin",
    "psycho.darwin.subtitle": "Charles Darwin",
    "psycho.darwin.desc": "L'approche évolutionniste des émotions humaines",
    "psycho.darwin.s1t": "L'universalité des émotions",
    "psycho.darwin.s1c": "Dans son ouvrage « L'Expression des émotions chez l'homme et les animaux » (1872), Charles Darwin a proposé que les émotions de base sont universelles, biologiquement programmées et partagées par tous les êtres humains, quelle que soit leur culture. Les expressions faciales de la joie, de la tristesse, de la colère, de la peur, du dégoût et de la surprise sont identiques dans le monde entier. Paul Ekman (1971) a confirmé cette théorie en étudiant des peuples isolés de Nouvelle-Guinée qui n'avaient jamais eu de contact avec la civilisation occidentale : ils exprimaient et reconnaissaient les mêmes émotions de base. Cette universalité indique que les émotions ont des racines biologiques profondes, héritées de nos ancêtres, et ne sont pas de simples constructions culturelles.",
    "psycho.darwin.s2t": "La fonction adaptative des émotions",
    "psycho.darwin.s2c": "Pour Darwin, chaque émotion a évolué pour remplir une fonction spécifique de survie. La peur mobilise l'organisme pour fuir ou se figer face au danger (réponse fight-flight-freeze). La colère donne l'énergie de se défendre et de protéger son territoire. Le dégoût protège de l'ingestion de substances toxiques ou de la contamination par des maladies. La tristesse signale la perte et attire le soutien social des proches. La joie renforce les comportements bénéfiques et favorise les liens sociaux. La surprise prépare l'organisme à traiter rapidement un événement inattendu. Même les émotions que nous considérons comme « négatives » sont en réalité des outils d'adaptation essentiels. Le problème n'est pas l'émotion elle-même, mais son intensité ou sa durée quand elle devient disproportionnée par rapport à la situation.",
    "psycho.darwin.s3t": "Les émotions primaires et secondaires",
    "psycho.darwin.s3c": "On distingue 6 émotions primaires (ou de base) : joie, tristesse, colère, peur, dégoût et surprise. Elles sont innées, rapides, automatiques et universelles. Les émotions secondaires (ou complexes) sont des combinaisons d'émotions primaires enrichies par la cognition et l'apprentissage social. Exemples : la honte (peur + tristesse + cognition sociale), la culpabilité (tristesse + colère envers soi), la fierté (joie + estime de soi), la jalousie (peur + colère + attachement), la nostalgie (joie + tristesse). Robert Plutchik (1980) a proposé la « roue des émotions » qui illustre comment les émotions primaires se combinent pour former des émotions plus complexes, comme des couleurs primaires créent de nouvelles teintes. En TCC, il est crucial d'apprendre à identifier et nommer précisément ses émotions (granularité émotionnelle) car cela permet de mieux les réguler.",
    "psycho.darwin.s4t": "Implications pour la TCC",
    "psycho.darwin.s4c": "L'approche évolutionniste de Darwin a des implications thérapeutiques fondamentales. (1) Normaliser les émotions : l'objectif de la TCC n'est pas d'éliminer les émotions « négatives » (qui sont normales et utiles) mais de réduire leur intensité quand elles deviennent disproportionnées. Avoir peur dans un avion est normal ; ne plus pouvoir prendre l'avion est un problème. (2) Distinguer émotion et pensée : les émotions sont des réponses automatiques du corps (battements de coeur, tension musculaire). Les pensées sont l'interprétation que nous en faisons. En TCC, on travaille sur les pensées pour modifier la réponse émotionnelle. (3) Développer l'intelligence émotionnelle : apprendre à identifier, nommer, accepter et réguler ses émotions est une compétence clé enseignée en TCC. La recherche montre que simplement nommer une émotion (« affect labeling ») réduit son intensité.",
    "psycho.darwin.q1q": "Selon l'approche de Darwin, les émotions « négatives » sont :",
    "psycho.darwin.q1a": "Des maladies à éliminer",
    "psycho.darwin.q1b": "Des signes de faiblesse",
    "psycho.darwin.q1c": "Des mécanismes de survie ayant une fonction adaptative",
    "psycho.darwin.q1d": "Uniquement d'origine culturelle",
    "psycho.darwin.q1e": "Darwin a montré que toutes les émotions ont une fonction adaptative pour la survie.",
    "psycho.darwin.q2q": "Combien d'émotions primaires universelles ont été identifiées ?",
    "psycho.darwin.q2a": "3 (joie, tristesse, colère)",
    "psycho.darwin.q2b": "4 (joie, tristesse, colère, peur)",
    "psycho.darwin.q2c": "6 (joie, tristesse, colère, peur, dégoût, surprise)",
    "psycho.darwin.q2d": "10 ou plus",
    "psycho.darwin.q2e": "Darwin et Ekman ont identifié 6 émotions primaires universelles : joie, tristesse, colère, peur, dégoût et surprise. Elles sont reconnues dans toutes les cultures humaines.",

    // Barkley TDAH Module
    "psycho.barkley.title": "Programme Barkley — TDAH Enfant",
    "psycho.barkley.subtitle": "Accompagnement parental pour le TDAH",
    "psycho.barkley.desc": "Un programme en 8 séances pour aider les parents d'enfants avec TDAH à adapter leur cadre éducatif et restaurer une relation positive.",

    "psycho.barkley.s1t": "Séance 1 — Comprendre le TDAH & le Programme Barkley",
    "psycho.barkley.s1c": "Le TDAH (Trouble Déficit de l'Attention avec ou sans Hyperactivité) est un trouble neurodéveloppemental fréquent (environ 1 enfant par classe). Ce n'est pas dû à une « mauvaise éducation ». Les difficultés viennent en grande partie de fonctions exécutives fragiles :\n\n• Mémoire de travail : le « post-it dans la tête » qui retient la consigne.\n• Inhibition : le « frein » qui empêche de dire ou faire tout de suite ce qui vient.\n• Flexibilité : la capacité à « changer de voie » quand on doit passer d'une activité à une autre.\n\nLe programme Barkley est un coaching parental qui vous donne des stratégies concrètes pour diminuer les conflits et restaurer une relation positive. Il ne vous juge pas, il vous entraîne.\n\nExercice : Carte d'identité du TDAH de votre enfant — Listez 3 à 5 situations fréquentes difficiles (devoirs, repas, coucher…). Pour chaque situation, identifiez le comportement observé, les fonctions exécutives en jeu, et ce que vous ressentez.",

    "psycho.barkley.s2t": "Séance 2 — Rétablir la communication : les « Moments Spéciaux »",
    "psycho.barkley.s2c": "Avec un enfant TDAH, on finit souvent par ne communiquer qu'au travers des problèmes. L'objectif est de réinstaller des moments où vous êtes juste bien ensemble.\n\n10 à 20 minutes de qualité tous les jours, c'est plus puissant qu'une grande sortie de temps en temps.\n\nRègles du Moment Spécial :\n• Un seul enfant à la fois (exclusivité)\n• Pas de téléphone / télé / tâches ménagères en même temps\n• C'est l'enfant qui choisit l'activité (dans un cadre acceptable)\n• Le parent décrit, commente, félicite, mais donne le moins d'ordres possible\n• On évite les sujets qui fâchent (école, comportement…)\n\nEn fin de moment, concluez par une phrase positive : « J'ai beaucoup aimé jouer avec toi. »\n\nSuivi : Notez chaque jour si le moment a eu lieu, l'activité, et le niveau d'agrément (0-10) pour vous et pour l'enfant.",

    "psycho.barkley.s3t": "Séance 3 — Apprendre à donner des consignes efficaces",
    "psycho.barkley.s3c": "Beaucoup de conflits viennent d'une inadéquation entre la façon de donner les consignes et les capacités attentionnelles de l'enfant TDAH.\n\nRègles de base :\n1. Se rapprocher physiquement, se mettre à hauteur d'enfant\n2. Capter l'attention : yeux dans les yeux, moins de distractions (écran coupé…)\n3. Formuler la consigne : claire, courte, positive (« range tes chaussures » plutôt que « arrête de laisser traîner tes chaussures »)\n4. Une seule consigne à la fois ; si la tâche est complexe, la découper en étapes\n5. Demander à l'enfant de répéter la consigne (« Tu peux me redire ce que tu dois faire ? »)\n6. Rester sur place quelques secondes pour voir s'il commence\n\nExemple de transformation :\n« Tu peux arrêter de faire n'importe quoi le matin ! » → « Quand le réveil sonne, tu t'habilles d'abord, puis tu viens prendre le petit-déjeuner. »\n\nExercice : Notez 5 phrases que vous dites souvent et retravaillez-les pour les rendre plus courtes, positives et spécifiques.",

    "psycho.barkley.s4t": "Séance 4 — Aider l'enfant à rester seul et respecter la place de chacun",
    "psycho.barkley.s4c": "L'objectif est de diminuer la sensation « il ne nous laisse jamais tranquilles » et de développer l'autonomie de l'enfant.\n\nExercice — Les « Temps où papa/maman est occupé(e) » :\n1. Annoncez à l'enfant ce que vous allez faire (« Je vais téléphoner »)\n2. Précisez ce qu'il ne doit pas faire (venir parler, interrompre)\n3. Proposez ce qu'il peut faire à la place (coloriage, legos, lecture…)\n4. Au début, revenez au bout d'1 minute pour féliciter : « Merci, tu ne m'as pas dérangé, tu t'occupes bien tout seul. »\n5. Puis repartez et revenez au bout de 3-5 minutes, etc.\n\nOutil pratique : Un petit sablier ou timer visuel — « Quand le sable est en bas, tu peux revenir me parler. »\n\nChaque jour, choisissez 1 situation pour vous entraîner et rallongez progressivement la durée.",

    "psycho.barkley.s5t": "Séance 5 — Mettre en place un système de points / jetons",
    "psycho.barkley.s5c": "L'objectif est de passer des « rappels et cris » à un système de renforcement positif structuré.\n\nÉtapes :\n1. Construire un tableau avec les jours en colonnes et les comportements ciblés en lignes (+ une ligne BONUS)\n2. Choisir maximum 3 comportements pour commencer, formulés positivement :\n   • « Je viens à table dès qu'on m'appelle »\n   • « Je range mes affaires d'école dans mon cartable le soir »\n   • « Je respecte le temps d'écran décidé avec mes parents »\n3. Définir les récompenses avec l'enfant :\n   • Petites récompenses quotidiennes (choisir le dessin animé, 10 min de jeu avec papa/maman…)\n   • Grosses récompenses hebdomadaires (film en famille, petit objet, activité choisie)\n4. Taux de change : 1 bonne case = 1 point, 10 points = 1 petite récompense, 30 points = 1 grosse\n5. En cas d'échec : ne pas humilier, simplement ne pas donner le point, puis rappeler la règle\n\nTester le système pendant 7 jours sans modifier les règles en cours de route.",

    "psycho.barkley.s6t": "Séance 6 — Gérer la colère & l'opposition : Time-out / Mise à l'écart",
    "psycho.barkley.s6c": "L'opposition est en partie normale dans le développement, mais exacerbée dans le TDAH. L'enfant TDAH est souvent débordé émotionnellement : le but du time-out est de créer un espace de retour au calme, pas de punir.\n\nProtocole en 5 étapes :\n1. Prévention : consignes simples, cadre stable, humour, éviter les paroles humiliantes\n2. Si l'opposition démarre : se rapprocher, contact visuel, rappeler la règle, proposer une alternative\n3. Si la crise monte : annoncer le temps de retrait (« là tu es très en colère, on va faire un temps calme »), accompagner sans trop parler\n4. Pendant le time-out : ne pas discuter à travers la porte, ne revenir que pour raisons de sécurité\n5. Après la crise : parler à froid, privilégier la réparation plutôt que la punition (ranger, nettoyer, mettre la table…)\n\nExercice : Construire un « Plan de gestion de crise » personnalisé en 3 colonnes — signaux d'alerte, ce que je ferai systématiquement, ce que je ne ferai plus.\n\nAuto-observation : après chaque crise, notez le déclencheur, ce que vous avez fait différemment, et ce que vous aimeriez améliorer.",

    "psycho.barkley.s7t": "Séance 7 — Gérer les comportements en lieux publics",
    "psycho.barkley.s7c": "L'objectif est de généraliser les progrès faits à la maison vers les magasins, restaurants, transports, etc.\n\nLa « Sortie Entraînement » :\n\nAvant la sortie :\n1. Choisir un lieu simple pour commencer (petite course, courte promenade)\n2. S'arrêter devant le lieu et rappeler 3 règles maximum (« je reste près de papa/maman, je ne touche pas, je ne demande pas d'acheter des choses »)\n3. Demander à l'enfant de redire les règles\n4. Annoncer la récompense si les règles sont respectées et la conséquence sinon\n\nPendant la sortie :\n• Repérer dès l'entrée un endroit possible de time-out (coin calme, banc…)\n• Multiplier les compliments discrets (« merci de rester près de moi »)\n\nAprès la sortie :\n• Débriefer : « qu'est-ce qui a été facile ? difficile ? »\n• Donner la récompense rapidement si les règles ont été globalement respectées\n\nFaites 1 à 2 sorties entraînement par semaine, même très courtes.",

    "psycho.barkley.s8t": "Séance 8 — Anticiper les rechutes & Plan d'auto-guidance",
    "psycho.barkley.s8c": "L'objectif est d'apprendre à analyser vous-même les nouveaux problèmes qui surgiront (nouvelle classe, adolescence, nouveaux contextes…).\n\nL'outil clé est l'analyse ABC :\n• A — Antécédents : ce qui se passe juste avant (contexte, consigne, fatigue…)\n• B — Comportement : ce que l'enfant fait précisément\n• C — Conséquences : ce que font les adultes / ce que l'enfant obtient (attention, évitement, gain d'un écran…)\n\nPour chaque problème :\n1. Remplir 3 lignes ABC sur des épisodes similaires\n2. Repérer quels antécédents peuvent être modifiés\n3. Identifier quelles conséquences renforcent involontairement le comportement\n4. Construire un mini plan d'action combinant les outils des séances précédentes\n\nMessages finaux :\n• Le comportement de l'enfant dépend beaucoup des antécédents et des conséquences immédiates\n• L'objectif n'est pas un enfant « parfait » mais de réduire les crises, améliorer la relation, et apaiser le climat familial\n• Vous progressez pas à pas — chaque petit changement compte.",

    "psycho.barkley.q1q": "Le TDAH est principalement causé par :",
    "psycho.barkley.q1a": "Une mauvaise éducation parentale",
    "psycho.barkley.q1b": "Un trouble neurodéveloppemental affectant les fonctions exécutives",
    "psycho.barkley.q1c": "Un excès de sucre dans l'alimentation",
    "psycho.barkley.q1d": "Un manque de discipline à l'école",
    "psycho.barkley.q1e": "Le TDAH est un trouble neurodéveloppemental. Les difficultés viennent de fonctions exécutives fragiles (attention, inhibition, mémoire de travail), pas de l'éducation.",

    "psycho.barkley.q2q": "Quelle est la durée recommandée pour le « Moment Spécial » quotidien ?",
    "psycho.barkley.q2a": "1 à 2 minutes",
    "psycho.barkley.q2b": "10 à 20 minutes",
    "psycho.barkley.q2c": "1 heure minimum",
    "psycho.barkley.q2d": "Il n'y a pas de durée recommandée",
    "psycho.barkley.q2e": "Le programme Barkley recommande 10 à 20 minutes de qualité par jour, de manière régulière. C'est plus efficace qu'une grande sortie occasionnelle.",

    "psycho.barkley.q3q": "Quelle est la meilleure façon de donner une consigne à un enfant TDAH ?",
    "psycho.barkley.q3a": "Crier depuis une autre pièce pour qu'il entende bien",
    "psycho.barkley.q3b": "Donner plusieurs consignes en même temps pour gagner du temps",
    "psycho.barkley.q3c": "Se rapprocher, capter l'attention, donner une consigne courte et positive",
    "psycho.barkley.q3d": "Écrire toutes les consignes sur un papier et le donner à l'enfant",
    "psycho.barkley.q3e": "Il faut se rapprocher physiquement, capter l'attention (yeux dans les yeux), formuler une consigne claire, courte et positive, puis demander à l'enfant de la répéter.",

    "psycho.barkley.q4q": "Quel est le but principal du time-out dans le programme Barkley ?",
    "psycho.barkley.q4a": "Punir l'enfant pour qu'il comprenne sa faute",
    "psycho.barkley.q4b": "Créer un espace de retour au calme, pas de punition",
    "psycho.barkley.q4c": "Isoler l'enfant le plus longtemps possible",
    "psycho.barkley.q4d": "Faire peur à l'enfant pour qu'il obéisse",
    "psycho.barkley.q4e": "Le time-out est un espace de retour au calme. L'enfant TDAH est souvent débordé émotionnellement. Après la crise, on privilégie la réparation (ranger, nettoyer) plutôt que la punition.",

    // Barkley exercise names
    "barkley.identityCard.title": "Carte d'identité TDAH",
    "barkley.identityCard.desc": "Identifier les situations difficiles et les fonctions exécutives en jeu",
    "barkley.specialMoment.title": "Moment Spécial",
    "barkley.specialMoment.desc": "Suivi quotidien du moment spécial parent-enfant",
    "barkley.instructions.title": "Consignes Efficaces",
    "barkley.instructions.desc": "Transformer vos consignes pour les rendre positives et claires",
    "barkley.tokenSystem.title": "Système de Points",
    "barkley.tokenSystem.desc": "Mettre en place et suivre un système de renforcement positif",
    "barkley.crisisPlan.title": "Plan Gestion de Crise",
    "barkley.crisisPlan.desc": "Construire un protocole personnalisé pour gérer les crises",
    "barkley.abcAnalysis.title": "Analyse ABC Barkley",
    "barkley.abcAnalysis.desc": "Analyser les antécédents, comportements et conséquences",
    "barkley.saved": "Exercice enregistré !",
    "barkley.history": "Historique",

    // Barkley - Enfants
    "barkley.childVolcano.title": "Mon Volcan Intérieur",
    "barkley.childVolcano.desc": "Identifie les signaux de colère et choisis une stratégie pour te calmer",
    "barkley.childAutonomy.title": "Mon Timer d'Autonomie",
    "barkley.childAutonomy.desc": "Entraîne-toi à rester seul(e) sur une activité choisie",
    "barkley.childSuperpowers.title": "Mes Super Pouvoirs",
    "barkley.childSuperpowers.desc": "Découvre tes forces et qualités",
    "barkley.childSolutions.title": "La Roue des Solutions",
    "barkley.childSolutions.desc": "Choisis une stratégie face à un problème",
    "barkley.childSuccess.title": "Mon Tableau de Réussites",
    "barkley.childSuccess.desc": "Note chaque jour tes réussites, même petites",
    "barkley.childDetective.title": "Le Détective des Consignes",
    "barkley.childDetective.desc": "Reformule une consigne dans tes propres mots",

    // Barkley - Adolescents
    "barkley.teenSituation.title": "Mon Analyse de Situation",
    "barkley.teenSituation.desc": "Analyse une situation avec tes pensées et émotions",
    "barkley.teenPlanner.title": "Mon Planning de la Semaine",
    "barkley.teenPlanner.desc": "Construis et suis ton planning hebdomadaire",
    "barkley.teenEmotionJournal.title": "Journal des Émotions Express",
    "barkley.teenEmotionJournal.desc": "Note une émotion par jour avec son déclencheur",
    "barkley.teenSmartGoals.title": "Mes Objectifs SMART",
    "barkley.teenSmartGoals.desc": "Définis et suis des objectifs concrets",
    "barkley.teenCNV.title": "Communication Non-Violente",
    "barkley.teenCNV.desc": "Formule des demandes en 4 étapes (CNV)",
    "barkley.teenWeeklyReview.title": "Mon Bilan Hebdo",
    "barkley.teenWeeklyReview.desc": "Auto-bilan rapide de la semaine",

    // ACT Psychoeducation module (Russ Harris)
    "psycho.act.title": "L'ACT — Thérapie d'Acceptation et d'Engagement",
    "psycho.act.subtitle": "Basé sur l'ouvrage de Russ Harris",
    "psycho.act.desc": "Module complet sur la Thérapie d'Acceptation et d'Engagement (ACT), basé sur 'Passez à l'ACT' de Russ Harris. Apprenez les 6 processus de la flexibilité psychologique.",

    "psycho.act.s1t": "1. Qu'est-ce que l'ACT ?",
    "psycho.act.s1c": "L'ACT (Thérapie d'Acceptation et d'Engagement) est une thérapie comportementale de 3e vague développée par Steven C. Hayes. Contrairement aux approches traditionnelles qui visent à réduire les symptômes, l'ACT ne vise pas à se sentir mieux, mais à vivre mieux.\n\nL'ACT repose sur un constat : la souffrance fait partie de la vie. Ce n'est pas la présence de pensées ou émotions difficiles qui cause nos problèmes, mais notre façon de les gérer. Quand nous luttons contre nos expériences internes (évitement expérientiel), nous restons piégés dans un cercle vicieux.\n\nL'objectif de l'ACT est de développer la flexibilité psychologique : la capacité à être en contact avec le moment présent et, selon ce que la situation permet, à maintenir ou changer son comportement au service de ses valeurs.",

    "psycho.act.s2t": "2. Le modèle Hexaflex : les 6 processus",
    "psycho.act.s2c": "L'ACT s'organise autour de 6 processus interconnectés, représentés dans un hexagone (Hexaflex) :\n\n1. DÉFUSION COGNITIVE — Se distancier de ses pensées au lieu de les croire littéralement. Les pensées sont des mots et des images, pas des faits.\n\n2. ACCEPTATION (Faire de la place) — Accueillir les émotions et sensations difficiles sans lutter contre elles. L'opposé de l'évitement expérientiel.\n\n3. CONTACT AVEC LE MOMENT PRÉSENT — Être pleinement attentif à l'ici et maintenant, avec ouverture et curiosité.\n\n4. LE SOI OBSERVATEUR (Soi comme contexte) — Se connecter à cette partie de nous qui observe sans juger. Nous ne sommes pas nos pensées ni nos émotions — nous sommes le contexte dans lequel elles apparaissent.\n\n5. VALEURS — Clarifier ce qui compte vraiment pour nous. Les valeurs sont comme une boussole qui guide nos actions.\n\n6. ACTION ENGAGÉE — Passer à l'action en alignement avec nos valeurs, même en présence de pensées et émotions difficiles.",

    "psycho.act.s3t": "3. L'évitement expérientiel : le piège",
    "psycho.act.s3c": "L'évitement expérientiel est la tentative de supprimer, contrôler ou fuir les expériences internes désagréables (pensées, émotions, souvenirs, sensations). C'est le processus central de la souffrance psychologique selon l'ACT.\n\nExemples d'évitement :\n• Consommer de l'alcool pour ne pas ressentir l'anxiété\n• Éviter les situations sociales par peur du jugement\n• Ruminer pour tenter de résoudre un problème émotionnel\n• Se distraire constamment pour ne pas penser\n\nParadoxe du contrôle : Plus on lutte contre une émotion, plus elle s'intensifie. Russ Harris utilise la métaphore du 'monstre dans le sable mouvant' : plus vous vous débattez, plus vous vous enfoncez.\n\nLa solution ACT n'est pas de combattre, mais d'observer et d'accueillir ces expériences tout en agissant vers ce qui compte.",

    "psycho.act.s4t": "4. La Défusion cognitive : se détacher de ses pensées",
    "psycho.act.s4c": "Nous avons environ 60 000 à 80 000 pensées par jour. La fusion cognitive, c'est quand nous prenons nos pensées pour la réalité absolue. 'Je suis nul' devient un fait plutôt qu'une simple pensée.\n\nTechniques de défusion (Russ Harris) :\n\n• 'J'AI LA PENSÉE QUE...' — Ajoutez ce préfixe devant toute pensée difficile. 'Je suis incompétent' devient 'J'ai la pensée que je suis incompétent'. Cela crée une distance instantanée.\n\n• FEUILLES SUR UN RUISSEAU — Visualisez chaque pensée comme une feuille qui flotte sur un ruisseau et s'éloigne.\n\n• VOIX COMIQUE — Répétez la pensée avec une voix de dessin animé. Cela réduit son impact émotionnel.\n\n• MERCI ESPRIT — Quand votre mental produit une pensée anxiogène, dites simplement 'Merci esprit, c'est très intéressant'.\n\nLa défusion ne signifie pas que les pensées disparaissent. Elle signifie qu'elles perdent leur emprise sur nos comportements.",

    "psycho.act.s5t": "5. L'Acceptation et le Contact avec le moment présent",
    "psycho.act.s5c": "L'acceptation en ACT ne signifie pas la résignation ou la tolérance passive. C'est une ouverture active aux expériences internes, même désagréables.\n\nLA TECHNIQUE D'EXPANSION (Faire de la place) :\n1. Observez la sensation dans votre corps\n2. Respirez vers cette zone\n3. Faites de la place autour de la sensation\n4. Permettez-lui d'être là, sans lutter\n\nJETER L'ANCRE (technique ACE) :\nQuand une tempête émotionnelle survient :\n• A — Accepter : 'Je remarque que je ressens de l'anxiété'\n• C — Connecter : Poussez vos pieds dans le sol, étirez-vous, regardez autour de vous\n• E — S'Engager : Choisissez une action alignée sur vos valeurs\n\nJeter l'ancre ne calme pas la tempête — mais vous empêche d'être emporté. Le contact avec le moment présent s'entraîne par des exercices de pleine conscience : remarquer 5 choses que vous voyez, 4 que vous touchez, 3 que vous entendez.",

    "psycho.act.s6t": "6. Les Valeurs : votre boussole intérieure",
    "psycho.act.s6c": "Les valeurs sont les directions de vie qui comptent pour vous. Elles sont différentes des objectifs :\n\n• Un OBJECTIF peut être atteint (courir un marathon)\n• Une VALEUR est une direction continue (prendre soin de sa santé)\n\nRuss Harris propose d'explorer les valeurs dans différents domaines :\n• Relations (famille, couple, amitié)\n• Travail / études\n• Loisirs / créativité\n• Santé / bien-être\n• Développement personnel\n• Communauté / spiritualité\n\nPour identifier vos valeurs, demandez-vous :\n- 'Quel genre de personne je veux être ?'\n- 'Si je pouvais agir sans peur, que ferais-je ?'\n- 'Qu'est-ce que je défendrais même si c'était difficile ?'\n\nLe POINT DE CHOIX est un outil visuel : face à une situation difficile, vous choisissez entre des mouvements d'ÉLOIGNEMENT (fuir, éviter) ou de RAPPROCHEMENT (avancer vers vos valeurs).",

    "psycho.act.s7t": "7. Le Soi Observateur et la Matrice ACT",
    "psycho.act.s7c": "LE SOI OBSERVATEUR :\nImaginez que vous êtes le ciel. Les pensées et émotions sont comme les nuages — elles vont et viennent, parfois sombres, parfois légères. Mais le ciel reste toujours intact derrière les nuages.\n\nVous n'êtes pas vos pensées, ni vos émotions, ni votre histoire. Vous êtes le contexte dans lequel tout cela apparaît.\n\nLA MATRICE ACT :\nOutil simple et puissant qui divise l'expérience en 4 quadrants :\n\n       VERS ce qui compte ↑\n  Expériences internes ← → Comportements\n       LOIN de ce qui compte ↓\n\n• En haut à droite : Actions vers mes valeurs\n• En haut à gauche : Ce qui compte (personnes, valeurs)\n• En bas à gauche : Obstacles internes (peurs, pensées, émotions)\n• En bas à droite : Comportements d'évitement\n\nLa matrice aide à prendre conscience de nos patterns et à faire des choix plus alignés avec nos valeurs.",

    "psycho.act.s8t": "8. L'Action Engagée : vivre selon ses valeurs",
    "psycho.act.s8c": "L'ACT aboutit toujours à l'action. Connaître ses valeurs sans agir n'a pas de sens. L'action engagée, c'est :\n\n1. IDENTIFIER une valeur importante\n2. DÉFINIR un objectif SMART aligné\n3. TROUVER le plus petit pas possible\n4. ANTICIPER les obstacles internes\n5. S'ENGAGER à agir AVEC les difficultés, pas SANS elles\n\nLa volonté (willingness) est cruciale en ACT : êtes-vous prêt(e) à accueillir les pensées et émotions difficiles pour avancer vers ce qui compte ? Ce n'est pas du courage — c'est un choix conscient.\n\nRuss Harris résume l'ACT en une phrase : 'Accepter ce qui est hors de votre contrôle personnel, et s'engager dans des actions qui enrichissent votre vie.'\n\nL'ACT est un voyage, pas une destination. Chaque jour offre des points de choix. À chaque point, vous pouvez choisir d'avancer vers la personne que vous voulez être.",

    "psycho.act.q1q": "Selon l'ACT, quel est l'objectif principal de la thérapie ?",
    "psycho.act.q1a": "Éliminer les pensées négatives",
    "psycho.act.q1b": "Développer la flexibilité psychologique",
    "psycho.act.q1c": "Se sentir mieux en toutes circonstances",
    "psycho.act.q1d": "Contrôler ses émotions",
    "psycho.act.q1e": "L'ACT ne vise pas à éliminer les pensées ou émotions négatives, mais à développer la flexibilité psychologique : la capacité à être présent et à agir selon ses valeurs, même en présence de difficultés internes.",

    "psycho.act.q2q": "Qu'est-ce que l'évitement expérientiel ?",
    "psycho.act.q2a": "Éviter les situations dangereuses",
    "psycho.act.q2b": "Ignorer les conseils de son thérapeute",
    "psycho.act.q2c": "Tenter de supprimer ou fuir les expériences internes désagréables",
    "psycho.act.q2d": "Refuser de prendre des médicaments",
    "psycho.act.q2e": "L'évitement expérientiel est la tentative de contrôler, supprimer ou fuir nos expériences internes (pensées, émotions, sensations). Paradoxalement, plus on lutte, plus la souffrance s'intensifie.",

    "psycho.act.q3q": "Dans la technique de défusion 'J'ai la pensée que...', quel est l'objectif ?",
    "psycho.act.q3a": "Créer une distance avec la pensée pour réduire son emprise",
    "psycho.act.q3b": "Prouver que la pensée est fausse",
    "psycho.act.q3c": "Remplacer la pensée par une pensée positive",
    "psycho.act.q3d": "Analyser la pensée en profondeur",
    "psycho.act.q3e": "Le préfixe 'J'ai la pensée que...' crée une distance entre vous et la pensée. Elle reste présente, mais perd son pouvoir de dicter vos actions. C'est de la défusion, pas de la restructuration cognitive.",

    "psycho.act.q4q": "Quelle est la différence entre une valeur et un objectif selon l'ACT ?",
    "psycho.act.q4a": "Les valeurs sont plus faciles à atteindre que les objectifs",
    "psycho.act.q4b": "Les objectifs sont plus importants que les valeurs",
    "psycho.act.q4c": "Une valeur est une direction de vie continue, un objectif peut être atteint",
    "psycho.act.q4d": "Il n'y a pas de différence",
    "psycho.act.q4e": "Une valeur est comme une direction sur une boussole — on ne l'atteint jamais, on avance vers elle (ex : être un parent aimant). Un objectif est un point d'arrivée concret (ex : lire un livre de parentalité ce mois-ci). Les valeurs guident les objectifs.",

    // ACT Exercise names
    "nav.actExercises": "L'ACT",
    "act.droppingAnchor.title": "Jeter l'Ancre",
    "act.droppingAnchor.desc": "Technique d'ancrage ACT pour revenir au moment présent",
    "act.expansion.title": "Faire de la Place",
    "act.expansion.desc": "Accueillir les émotions difficiles sans lutter",
    "act.observerSelf.title": "Le Soi Observateur",
    "act.observerSelf.desc": "Se connecter à la partie de soi qui observe sans juger",
    "act.choicePoint.title": "Le Point de Choix",
    "act.choicePoint.desc": "Identifier ce qui vous éloigne ou rapproche de vos valeurs",
    "act.matrix.title": "La Matrice ACT",
    "act.matrix.desc": "Analyser ses comportements : vers ou loin de ce qui compte",
    "act.committedAction.title": "Action Engagée",
    "act.committedAction.desc": "Définir et suivre des actions alignées sur vos valeurs",
    "act.realityWitness.title": "Soyez témoin de la réalité",
    "act.realityWitness.desc": "Sortir de la rumination en ancrant l'attention dans le réel via les 5 sens",
    "act.choiceQuestion.title": "La question du choix",
    "act.choiceQuestion.desc": "M'éloigne ou me rapproche de la personne que je veux être ?",
    "act.controlMyth.title": "Le mythe du contrôle",
    "act.controlMyth.desc": "Distinguer ce que je ne contrôle pas de ce que je peux contrôler partiellement",

    // TDAH Adulte Psychoeducation (Safren / TCCMontréal)
    "psycho.tdah.title": "Le TDAH chez l'adulte — Approche TCC",
    "psycho.tdah.subtitle": "Basé sur le programme de Safren et le guide TCCMontréal",
    "psycho.tdah.desc": "Module complet sur la compréhension et la gestion du TDAH chez l'adulte par la Thérapie Cognitivo-Comportementale. Apprenez les stratégies d'organisation, d'attention et de restructuration cognitive.",

    "psycho.tdah.s1t": "1. Qu'est-ce que le TDAH chez l'adulte ?",
    "psycho.tdah.s1c": "Le Trouble du Déficit de l'Attention avec ou sans Hyperactivité (TDAH) touche environ 4 à 5 % des adultes. Contrairement aux croyances populaires, le TDAH ne disparaît pas à l'âge adulte — il évolue.\n\nLes trois présentations du TDAH :\n• INATTENTION PRÉDOMINANTE : difficultés à maintenir l'attention, distractibilité, oublis fréquents, difficultés d'organisation\n• HYPERACTIVITÉ-IMPULSIVITÉ PRÉDOMINANTE : agitation interne ou externe, impatience, interruption des autres, prises de décision impulsives\n• PRÉSENTATION COMBINÉE : les deux types de symptômes\n\nLe TDAH adulte s'accompagne souvent de :\n• Dysrégulation émotionnelle (réactions émotionnelles intenses et rapides)\n• Difficultés avec le sens du temps (retards chroniques, sous-estimation des durées)\n• Hyperfocalisation (absorption totale dans une tâche intéressante, difficulté à en sortir)\n• Faible mémoire de travail (oublier ce qu'on allait faire en changeant de pièce)",

    "psycho.tdah.s2t": "2. Le modèle cognitivo-comportemental du TDAH (Safren)",
    "psycho.tdah.s2c": "Le programme de Safren et al. (2006) est le traitement TCC du TDAH adulte le plus étudié et validé scientifiquement. Il propose un modèle en trois niveaux :\n\nNIVEAU 1 — DÉFICITS NEUROBIOLOGIQUES :\nLe TDAH est un trouble neurodéveloppemental. Le cerveau TDAH présente un déficit en dopamine et noradrénaline, affectant les fonctions exécutives (planification, inhibition, mémoire de travail).\n\nNIVEAU 2 — DIFFICULTÉS FONCTIONNELLES :\nCes déficits neurobiologiques créent des difficultés concrètes : retards, oublis, désorganisation, procrastination, difficultés à terminer les tâches.\n\nNIVEAU 3 — CONSÉQUENCES PSYCHOLOGIQUES :\nLes échecs répétés depuis l'enfance génèrent des pensées automatiques négatives : 'Je suis paresseux', 'Je n'y arriverai jamais', 'Tous les autres y arrivent sauf moi'. Ces pensées alimentent l'anxiété, la dépression et la baisse d'estime de soi.\n\nLa TCC agit sur les 3 niveaux : stratégies compensatoires (niveau 2) + restructuration cognitive (niveau 3), en complément du traitement pharmacologique si nécessaire (niveau 1).",

    "psycho.tdah.s3t": "3. L'impact du TDAH sur la vie quotidienne",
    "psycho.tdah.s3c": "Le TDAH affecte tous les domaines de la vie adulte :\n\nTRAVAIL :\n• Retards et absences\n• Difficultés à respecter les échéances\n• Projets inachevés\n• Conflits avec la hiérarchie\n• Sous-performance par rapport au potentiel\n\nRELATIONS :\n• Oubli des anniversaires et engagements\n• Interruption des conversations\n• Réactions émotionnelles disproportionnées\n• Partenaire perçu comme « irresponsable »\n\nFINANCES :\n• Achats impulsifs\n• Factures en retard ou oubliées\n• Difficulté à tenir un budget\n\nSANTÉ :\n• Rendez-vous médicaux oubliés\n• Difficulté à maintenir une hygiène de vie régulière\n• Troubles du sommeil fréquents\n• Comorbidités : anxiété (50%), dépression (30-40%), addictions\n\nImportant : ces difficultés ne sont PAS des défauts de caractère. Ce sont les conséquences fonctionnelles d'un trouble neurobiologique. La TCC vous apprend des stratégies compensatoires adaptées.",

    "psycho.tdah.s4t": "4. Hygiène de vie et TDAH",
    "psycho.tdah.s4c": "L'hygiène de vie est le socle du traitement du TDAH. Sans fondations solides, les stratégies cognitives et comportementales perdent en efficacité.\n\nSOMMEIL (le plus impactant) :\n• Le TDAH perturbe le rythme circadien — 75% des adultes TDAH ont des troubles du sommeil\n• Couchez-vous et levez-vous à la même heure, même le week-end\n• Pas d'écrans 1h avant le coucher (la lumière bleue aggrave les symptômes TDAH)\n• La privation de sommeil aggrave TOUS les symptômes du TDAH\n\nEXERCICE PHYSIQUE :\n• L'exercice augmente la dopamine et la noradrénaline — les mêmes neurotransmetteurs ciblés par les médicaments TDAH\n• 30 minutes d'exercice modéré ont un effet comparable à une petite dose de stimulant\n• Privilégiez le matin : l'exercice matinal améliore l'attention pour les 4-6h suivantes\n\nALIMENTATION :\n• Repas réguliers (la glycémie instable aggrave l'inattention)\n• Protéines au petit-déjeuner (soutiennent l'attention)\n• Limiter le sucre et la caféine excessive\n\nPLEINE CONSCIENCE :\n• La méditation de pleine conscience est validée scientifiquement pour le TDAH adulte\n• Elle entraîne l'attention soutenue et la capacité à remarquer les distractions",

    "psycho.tdah.s5t": "5. Organisation et planification",
    "psycho.tdah.s5c": "L'organisation est le talon d'Achille du TDAH. Le programme de Safren propose des stratégies concrètes :\n\nLE SYSTÈME ABC DE PRIORISATION :\n• A = Urgent ET important (à faire aujourd'hui)\n• B = Important, pas urgent (à planifier cette semaine)\n• C = Secondaire (peut attendre ou être délégué)\nRègle : ne commencez jamais une tâche B si une tâche A n'est pas terminée.\n\nL'AGENDA UNIQUE :\n• UN SEUL endroit pour TOUT noter (pas de post-its éparpillés)\n• Consultez-le matin, midi et soir\n• Notez rendez-vous, tâches, rappels, et idées au même endroit\n• Numérique ou papier — l'important est la constance\n\nLE SYSTÈME DE GESTION UNIQUE :\n• UN endroit pour le courrier entrant\n• UN moment fixe par semaine pour le traitement\n• Trois actions possibles : traiter maintenant, planifier, jeter\n\nRÈGLE DES 2 MINUTES :\nSi une tâche prend moins de 2 minutes, faites-la immédiatement. Si elle prend plus, notez-la dans votre agenda.\n\nLES RAPPELS :\n• Alarmes téléphone pour les rendez-vous importants\n• Rappel la veille ET le jour même\n• Routine du soir : préparer le lendemain (vêtements, sac, documents)",

    "psycho.tdah.s6t": "6. La gestion de la distractibilité",
    "psycho.tdah.s6c": "La distractibilité est le symptôme le plus visible du TDAH adulte. Elle est causée par un déficit du filtre attentionnel : le cerveau TDAH a du mal à ignorer les stimuli non pertinents.\n\nSTRATÉGIES ENVIRONNEMENTALES :\n• Travaillez dos au mouvement et aux fenêtres\n• Téléphone en mode avion ou dans une autre pièce\n• Utilisez un casque anti-bruit ou de la musique sans paroles\n• Bureau dégagé : ne gardez que ce qui concerne la tâche en cours\n• Signalez aux collègues/famille quand vous avez besoin de vous concentrer\n\nSTRATÉGIES TEMPORELLES :\n• Connaissez votre « capacité attentionnelle » (souvent 15-25 min pour le TDAH)\n• Travaillez en blocs courts avec pauses (technique Pomodoro adaptée)\n• Planifiez les tâches difficiles à votre pic d'attention (souvent le matin)\n• Utilisez un timer visible (le temps devient concret)\n\nSTRATÉGIES COGNITIVES :\n• Quand une distraction apparaît, notez-la sur un « parking à idées » et revenez à votre tâche\n• Pratiquez le « recadrage attentionnel » : « Je remarque que je suis distrait. Je choisis de revenir à ma tâche. »\n• Utilisez la technique « juste 5 minutes » : engagez-vous à travailler seulement 5 min (l'inertie au démarrage est le vrai obstacle)",

    "psycho.tdah.s7t": "7. La restructuration cognitive dans le TDAH",
    "psycho.tdah.s7c": "Les adultes TDAH développent des schémas de pensée négatifs après des années d'échecs et de critiques. Le programme de Safren cible spécifiquement ces pensées automatiques.\n\nPENSÉES AUTOMATIQUES TYPIQUES DU TDAH :\n• « Je suis paresseux / fainéant » → En réalité : déficit de dopamine, pas de volonté\n• « Tout le monde y arrive sauf moi » → Surgénéralisation + comparaison injuste\n• « Ça ne sert à rien d'essayer, je vais encore échouer » → Prédiction négative\n• « Si je ne peux pas le faire parfaitement, autant ne rien faire » → Pensée tout-ou-rien\n• « Je devrais être capable de faire ça sans aide » → Exigence irréaliste\n\nLE TABLEAU D'ENREGISTREMENT DES PENSÉES :\n1. Situation déclencheuse\n2. Pensée automatique\n3. Émotion et intensité (0-10)\n4. Type d'erreur de pensée\n5. Preuves pour / contre\n6. Pensée alternative réaliste (intégrant le TDAH comme facteur)\n7. Nouvelle émotion et intensité\n\nClé : La pensée alternative doit RECONNAÎTRE le TDAH comme facteur explicatif tout en préservant l'agentivité. Ex : « Mon TDAH rend l'organisation difficile, ET je peux apprendre des stratégies compensatoires. »",

    "psycho.tdah.s8t": "8. Procrastination et prévention des rechutes",
    "psycho.tdah.s8c": "LA PROCRASTINATION DANS LE TDAH :\nLa procrastination TDAH est différente de la procrastination « ordinaire ». Elle est liée à :\n• Le déficit de dopamine (le cerveau ne produit pas assez de motivation pour les tâches non stimulantes)\n• La difficulté à initier (« paralysie de démarrage »)\n• L'aversion pour le délai de gratification\n• La sous-estimation du temps nécessaire\n\nTECHNIQUES ANTI-PROCRASTINATION :\n• Décomposer en micro-étapes (5 min maximum chacune)\n• La technique du « premier pas » : ne vous engagez qu'à faire la première micro-étape\n• Récompense immédiate après chaque micro-étape\n• Le « body doubling » : travailler en présence de quelqu'un (même en visio)\n• Rendre la tâche plus stimulante (musique, défi personnel, gamification)\n\nPRÉVENTION DES RECHUTES :\nLe TDAH est un trouble CHRONIQUE. Les stratégies fonctionnent tant qu'elles sont maintenues.\n• Attendez-vous à des rechutes — elles font partie du processus\n• Quand une stratégie échoue, ne l'abandonnez pas : ajustez-la\n• Maintenez vos rendez-vous de suivi même quand « tout va bien »\n• Entourez-vous : groupe de soutien TDAH, coach, thérapeute\n• Soyez bienveillant avec vous-même : le TDAH est un marathon, pas un sprint",

    "psycho.tdah.q1q": "Selon le modèle de Safren, quel est le troisième niveau des conséquences du TDAH ?",
    "psycho.tdah.q1a": "Les difficultés d'organisation",
    "psycho.tdah.q1b": "Les déficits neurobiologiques",
    "psycho.tdah.q1c": "Les conséquences psychologiques (pensées négatives, anxiété, dépression)",
    "psycho.tdah.q1d": "Les troubles du sommeil",
    "psycho.tdah.q1e": "Le modèle de Safren décrit 3 niveaux : 1) Déficits neurobiologiques, 2) Difficultés fonctionnelles, 3) Conséquences psychologiques. Les échecs répétés génèrent des pensées automatiques négatives qui alimentent l'anxiété et la dépression.",

    "psycho.tdah.q2q": "Dans le système ABC de priorisation, que représente une tâche A ?",
    "psycho.tdah.q2a": "Une tâche secondaire",
    "psycho.tdah.q2b": "Une tâche urgente ET importante à faire aujourd'hui",
    "psycho.tdah.q2c": "Une tâche importante mais pas urgente",
    "psycho.tdah.q2d": "Une tâche pouvant être déléguée",
    "psycho.tdah.q2e": "Le système ABC classe les tâches : A = urgent + important (aujourd'hui), B = important mais pas urgent (cette semaine), C = secondaire. On ne commence jamais une tâche B si une tâche A n'est pas terminée.",

    "psycho.tdah.q3q": "Pourquoi l'exercice physique est-il particulièrement bénéfique pour le TDAH ?",
    "psycho.tdah.q3a": "Il augmente la dopamine et la noradrénaline, les mêmes neurotransmetteurs ciblés par les médicaments TDAH",
    "psycho.tdah.q3b": "Il fatigue le corps et réduit l'hyperactivité",
    "psycho.tdah.q3c": "Il améliore uniquement le sommeil",
    "psycho.tdah.q3d": "Il n'a pas d'effet prouvé sur le TDAH",
    "psycho.tdah.q3e": "L'exercice physique augmente la dopamine et la noradrénaline — les mêmes neurotransmetteurs que les médicaments TDAH ciblent. 30 minutes d'exercice modéré ont un effet comparable à une petite dose de stimulant.",

    "psycho.tdah.q4q": "Quelle est la différence entre la procrastination TDAH et la procrastination « ordinaire » ?",
    "psycho.tdah.q4a": "Il n'y a pas de différence",
    "psycho.tdah.q4b": "La procrastination TDAH est un choix conscient",
    "psycho.tdah.q4c": "La procrastination TDAH est uniquement liée au stress",
    "psycho.tdah.q4d": "La procrastination TDAH est liée au déficit de dopamine et à la difficulté à initier les tâches non stimulantes",
    "psycho.tdah.q4e": "La procrastination dans le TDAH est neurobiologique : le cerveau ne produit pas assez de dopamine pour motiver les tâches non stimulantes, créant une 'paralysie de démarrage'. Ce n'est pas de la paresse.",

    // TDAH Adulte Exercise names
    "nav.tdahAdulte": "TDAH Adulte",
    "tdah.priorities.title": "Priorités ABC",
    "tdah.priorities.desc": "Système de priorisation des tâches A/B/C",
    "tdah.documents.title": "Gestion des Documents",
    "tdah.documents.desc": "Système de gestion unique du courrier et factures",
    "tdah.attention.title": "Capacité Attentionnelle",
    "tdah.attention.desc": "Évaluer et entraîner sa capacité d'attention",
    "tdah.thoughts.title": "Pensées Automatiques TDAH",
    "tdah.thoughts.desc": "Enregistrement et restructuration cognitive",
    "tdah.procrastination.title": "Anti-Procrastination",
    "tdah.procrastination.desc": "Décomposition des tâches en micro-étapes",
    "tdah.goals.title": "Objectifs & Motivation",
    "tdah.goals.desc": "Fixer des objectifs réalistes et maintenir la motivation",

    // TDAH Kids Workbook
    "exercise.tdahKids.superpowers.title": "Super-Pouvoirs Cosmiques",
    "exercise.tdahKids.superpowers.desc": "Découvre tes forces secrètes",
    "exercise.tdahKids.safetyBubble.title": "Bulle Spatiale",
    "exercise.tdahKids.safetyBubble.desc": "Crée ton espace de pause",
    "exercise.tdahKids.relaxation.title": "Retour en Orbite",
    "exercise.tdahKids.relaxation.desc": "Relaxation musculaire",
    "exercise.tdahKids.radar.title": "Radar Galactique",
    "exercise.tdahKids.radar.desc": "Journal des sensations",
    "exercise.tdahKids.recharge.title": "Station de Recharge",
    "exercise.tdahKids.recharge.desc": "Moments calmes et ressourçants",
    "exercise.tdahKids.turbo.title": "Vaisseau Turbo",
    "exercise.tdahKids.turbo.desc": "Comprends ton cerveau turbo",
    "exercise.tdahKids.mission.title": "Mission Spatiale",
    "exercise.tdahKids.mission.desc": "Défis de self-control",
    "exercise.tdahKids.superheroDay.title": "Journée d'Astronaute",
    "exercise.tdahKids.superheroDay.desc": "Suivi d'énergie quotidien",
    "exercise.tdahKids.victories.title": "Étoiles de Victoire",
    "exercise.tdahKids.victories.desc": "Célèbre tes réussites",
    "exercise.tdahKids.questions.title": "Message Centre Contrôle",
    "exercise.tdahKids.questions.desc": "Questions pour le thérapeute",
    // Parent Task Board & Psychoeducation
    "exercise.parentTaskBoard.title": "Tableau de Missions",
    "exercise.parentTaskBoard.desc": "Missions quotidiennes avec badges à gagner",
    "exercise.morningChecklist.title": "Checklist du Matin",
    "exercise.morningChecklist.desc": "Liste de tâches pour structurer ta routine matinale",
    "psychoed.tdahChild.title": "Comprendre mon TDAH",
    "psychoed.tdahChild.desc": "Module ludique pour comprendre le TDAH",
    "psychoed.limitsChild.title": "Mon Jardin Secret - Les Limites",
    "psychoed.limitsChild.desc": "Apprendre ses limites de manière ludique",
    "psychoed.limitsAdult.title": "Communiquer ses Limites",
    "psychoed.limitsAdult.desc": "Techniques assertives pour défendre ses limites",
    "psychoed.schemaTherapy.title": "Thérapie des Schémas",
    "psychoed.tcd.title": "TCD (Linehan)",
    "psychoed.dissociationTrauma.title": "Dissociation et Trauma",
    "psychoed.toc.title": "Comprendre le TOC",
    "psychoed.toc.subtitle": "Trouble Obsessionnel Compulsif : comprendre et agir",
    "psychoed.borderline.title": "Comprendre le Trouble Borderline",
    "psychoed.borderline.subtitle": "Trouble de la Personnalite Borderline : comprendre pour mieux vivre",
    "psychoed.erectileDysfunction.title": "Dysfonction érectile et anxiété de performance",
    "psychoed.erectileDysfunction.subtitle": "Comprendre l'anxiété de performance et pratiquer la focalisation sensorielle",
    "exercices.schemaTherapy.title": "Exercices Schémas",
    "exercices.tcd.title": "Exercices TCD",
    "exercices.dissociationTrauma.title": "Exercices Dissociation",
    "nav.exercicesAvances": "Exercices Avancés",

    // Schema Exercises Page
    "schemaEx.pageTitle": "Exercices : Thérapie des Schémas",
    "schemaEx.pageSubtitle": "Exercices pratiques pour identifier et transformer vos schémas",
    "schemaEx.journal.name": "Journal de schémas",
    "schemaEx.journal.desc": "Identifiez vos schémas au quotidien en repérant les situations qui activent des réactions émotionnelles intenses.",
    "schemaEx.journal.situation": "Situation déclenchante",
    "schemaEx.journal.situationPh": "Décrivez la situation (qui, quoi, quand, où)",
    "schemaEx.journal.emotion": "Émotion ressentie et intensité (0-10)",
    "schemaEx.journal.emotionPh": "Ex: Tristesse (8/10), colère (5/10)",
    "schemaEx.journal.thoughts": "Pensées automatiques",
    "schemaEx.journal.thoughtsPh": "Quelles pensées ont surgi ? Ex: 'Personne ne tient à moi'",
    "schemaEx.journal.schema": "Schéma probablement activé",
    "schemaEx.journal.schemaPh": "Ex: Abandon, Imperfection, Échec, Manque affectif...",
    "schemaEx.journal.reaction": "Réaction comportementale",
    "schemaEx.journal.reactionPh": "Qu'avez-vous fait ? (soumission, évitement ou compensation)",
    "schemaEx.memo.name": "Fiche mémo-flash",
    "schemaEx.memo.desc": "Créez une carte portable pour contrer votre schéma quand il s'active.",
    "schemaEx.memo.voice": "Ce que dit le schéma",
    "schemaEx.memo.voicePh": "Ex: 'Personne ne m'aime vraiment'",
    "schemaEx.memo.why": "Pourquoi c'est un schéma, pas la réalité",
    "schemaEx.memo.whyPh": "Listez les preuves contre cette croyance",
    "schemaEx.memo.realistic": "Ce qui est plus réaliste",
    "schemaEx.memo.realisticPh": "Ex: 'Certaines personnes m'aiment sincèrement'",
    "schemaEx.memo.action": "Ce que je peux faire maintenant",
    "schemaEx.memo.actionPh": "Un comportement sain et concret",
    "schemaEx.dialogue.name": "Dialogue intérieur",
    "schemaEx.dialogue.desc": "Faites dialoguer votre côté schéma et votre côté sain pour contester les croyances automatiques.",
    "schemaEx.dialogue.schemaSide": "Côté schéma (ce qu'il dit)",
    "schemaEx.dialogue.schemaSidePh": "Laissez le schéma s'exprimer pleinement...",
    "schemaEx.dialogue.healthySide": "Côté sain (réponse de l'Adulte Sain)",
    "schemaEx.dialogue.healthySidePh": "Répondez avec empathie et fermeté...",
    "schemaEx.dialogue.schemaReply": "Schéma (réponse)",
    "schemaEx.dialogue.schemaReplyPh": "Que répond le schéma ?",
    "schemaEx.dialogue.healthyFinal": "Côté sain (mot final)",
    "schemaEx.dialogue.healthyFinalPh": "Concluez avec une perspective équilibrée",
    "schemaEx.letter.name": "Lettre à l'Enfant Vulnérable",
    "schemaEx.letter.desc": "Écrivez à la partie enfant de vous-même pour un acte de re-parentage intérieur.",
    "schemaEx.letter.letter": "Votre lettre à l'enfant intérieur",
    "schemaEx.letter.letterPh": "Cher(e) petit(e) [prénom]...\nReconnaissez sa souffrance, validez ses émotions, rassurez-le/la...",
    "schemaEx.letter.response": "Réponse de l'enfant (optionnel)",
    "schemaEx.letter.responsePh": "Qu'est-ce que l'enfant répondrait ?",
    "schemaEx.imagery.name": "Imagerie et re-parentage",
    "schemaEx.imagery.desc": "Accédez à un souvenir d'enfance et transformez-le en y apportant une expérience réparatrice.",
    "schemaEx.imagery.scene": "Scène d'enfance (décrivez l'image)",
    "schemaEx.imagery.scenePh": "Le lieu, les personnes, ce que vous ressentez dans votre corps d'enfant...",
    "schemaEx.imagery.intervention": "Intervention de l'Adulte Sain",
    "schemaEx.imagery.interventionPh": "Comment votre vous adulte intervient pour protéger l'enfant ?",
    "schemaEx.imagery.transformed": "Scène transformée",
    "schemaEx.imagery.transformedPh": "Comment la scène change quand le besoin de l'enfant est satisfait ?",
    "schemaEx.imagery.feeling": "Ressenti après l'exercice",
    "schemaEx.imagery.feelingPh": "Qu'avez-vous ressenti en ouvrant les yeux ?",

    // TCD Exercises Page
    "tcdEx.pageTitle": "Exercices : TCD (Thérapie Comportementale Dialectique)",
    "tcdEx.pageSubtitle": "Compétences de régulation émotionnelle et de tolérance à la détresse",
    "tcdEx.chain.name": "Analyse en chaîne",
    "tcdEx.chain.desc": "Décomposez la séquence complète entre un déclencheur et un comportement problématique.",
    "tcdEx.chain.behavior": "Comportement problématique",
    "tcdEx.chain.behaviorPh": "Décrivez précisément le comportement à analyser",
    "tcdEx.chain.trigger": "Événement déclencheur",
    "tcdEx.chain.triggerPh": "Qui, quoi, quand, où ?",
    "tcdEx.chain.vulnerability": "Facteurs de vulnérabilité",
    "tcdEx.chain.vulnerabilityPh": "Manque de sommeil, faim, stress, maladie, conflit...",
    "tcdEx.chain.chain": "Chaîne des maillons (pensées, émotions, sensations, actions)",
    "tcdEx.chain.chainPh": "Détaillez chaque étape entre le déclencheur et le comportement...",
    "tcdEx.chain.consequences": "Conséquences (court terme et long terme)",
    "tcdEx.chain.consequencesPh": "Court terme : soulagement ? Long terme : aggravation ?",
    "tcdEx.chain.skills": "Compétences TCD alternatives",
    "tcdEx.chain.skillsPh": "À quels maillons auriez-vous pu utiliser une compétence ? Laquelle ?",
    "tcdEx.stop.name": "STOP et TIP",
    "tcdEx.stop.desc": "Techniques de crise pour traverser un moment de douleur intense sans aggraver la situation.",
    "tcdEx.stop.crisis": "Situation de crise",
    "tcdEx.stop.crisisPh": "Décrivez la situation et l'intensité émotionnelle (0-10)",
    "tcdEx.stop.steps": "Application du STOP",
    "tcdEx.stop.stepsPh": "S (Arrêt) — T (Temporiser) — O (Observer) — P (Poursuivre en pleine conscience)",
    "tcdEx.stop.tip": "Technique TIP utilisée",
    "tcdEx.stop.tipPh": "Température (eau froide), Intensité physique, respiration lente, relaxation musculaire...",
    "tcdEx.stop.outcome": "Résultat",
    "tcdEx.stop.outcomePh": "Comment vous sentez-vous après ? L'intensité a-t-elle baissé ?",
    "tcdEx.acceptance.name": "Acceptation radicale et demi-sourire",
    "tcdEx.acceptance.desc": "Pratiquez l'acceptation complète de la réalité et les techniques corporelles associées.",
    "tcdEx.acceptance.situation": "Situation à accepter",
    "tcdEx.acceptance.situationPh": "Quelle réalité avez-vous du mal à accepter ?",
    "tcdEx.acceptance.signs": "Signes de non-acceptation",
    "tcdEx.acceptance.signsPh": "Amertume, rumination, tension corporelle, envie de nier...",
    "tcdEx.acceptance.steps": "Étapes d'acceptation pratiquées",
    "tcdEx.acceptance.stepsPh": "Observer la lutte, reconnaître la réalité, identifier les causes, accepter avec le corps...",
    "tcdEx.acceptance.body": "Pratique corporelle",
    "tcdEx.acceptance.bodyPh": "Demi-sourire, mains volontaires — décrivez votre expérience",
    "tcdEx.acceptance.result": "Effet ressenti",
    "tcdEx.acceptance.resultPh": "Comment vous sentez-vous après la pratique ?",
    "tcdEx.facts.name": "Vérifier les faits et agir à l'opposé",
    "tcdEx.facts.desc": "Évaluez si votre émotion correspond aux faits et, si non, agissez à l'opposé.",
    "tcdEx.facts.emotion": "Émotion ressentie et intensité",
    "tcdEx.facts.emotionPh": "Ex: Colère (8/10)",
    "tcdEx.facts.triggerFacts": "Faits objectifs de la situation",
    "tcdEx.facts.triggerFactsPh": "Que s'est-il réellement passé ? (sans interprétation)",
    "tcdEx.facts.interpretations": "Mes interprétations",
    "tcdEx.facts.interpretationsPh": "Quelles suppositions ai-je faites ? Sont-elles basées sur des faits ?",
    "tcdEx.facts.matchesFacts": "L'émotion correspond-elle aux faits ?",
    "tcdEx.facts.matchesFactsPh": "Oui (résolution de problème) ou Non (agir à l'opposé)",
    "tcdEx.facts.oppositeAction": "Action opposée réalisée",
    "tcdEx.facts.oppositeActionPh": "Qu'avez-vous fait à l'opposé de l'impulsion ? EN Y ALLANT À FOND",
    "tcdEx.dear.name": "DEAR MAN",
    "tcdEx.dear.desc": "Directives d'efficacite pour l'objectif : obtenir ce que vous voulez.",
    "tcdEx.dear.context": "Situation et objectif",
    "tcdEx.dear.contextPh": "Decrivez la situation et ce que vous voulez obtenir",
    "tcdEx.dear.describe": "D - Decrire (les faits)",
    "tcdEx.dear.describePh": "Ex: 'Tu m'as dit que tu serais a la maison pour le souper mais tu es arrive a 23 heures.'",
    "tcdEx.dear.describeHint": "Decrivez la SITUATION actuelle (si necessaire). Tenez-vous en aux faits. Dites a la personne a quoi vous reagissez exactement.",
    "tcdEx.dear.express": "E - Exprimer (sentiments et opinions)",
    "tcdEx.dear.expressPh": "Ex: 'Quand tu rentres a la maison si tard, je m'inquiete pour toi.'",
    "tcdEx.dear.expressHint": "Exprimez vos SENTIMENTS et OPINIONS sur la situation. Ne partez pas du principe que la personne sait ce que vous ressentez. Utilisez 'je veux' plutot que 'tu devrais'.",
    "tcdEx.dear.assert": "A - Affirmer (demande ou refus clair)",
    "tcdEx.dear.assertPh": "Ex: 'J'aimerais vraiment que tu m'appelles si tu vas rentrer si tard a la maison.'",
    "tcdEx.dear.assertHint": "Affirmez-vous en DEMANDANT ou en REFUSANT clairement. Ne partez pas du principe que les autres vont deviner. Rappelez-vous que les autres ne peuvent pas lire dans vos pensees.",
    "tcdEx.dear.reinforce": "R - Renforcer (consequences positives)",
    "tcdEx.dear.reinforcePh": "Ex: 'Je serais si soulagee et beaucoup plus facile a vivre si tu le faisais.'",
    "tcdEx.dear.reinforceHint": "Renforcez (recompensez) la personne a l'avance en lui presentant les effets positifs d'obtenir ce que vous voulez. Si necessaire, precisez les consequences negatives. N'oubliez pas de recompenser le comportement souhaite apres qu'il ait eu lieu.",
    "tcdEx.dear.mindful": "M - Maintenir sa pleine conscience",
    "tcdEx.dear.mindfulPh": "Comment avez-vous maintenu votre position ? Avez-vous utilise le 'disque raye' ?",
    "tcdEx.dear.mindfulHint": "Centrez-vous SUR VOS OBJECTIFS. Maintenez votre position. Ne vous laissez pas distraire. Ne sortez pas du sujet. Le 'disque raye' : Continuez a demander, a refuser ou a exprimer votre opinion, encore et encore. Ignorez les attaques et les diversions.",
    "tcdEx.dear.confident": "A - Avec assurance",
    "tcdEx.dear.confidentPh": "Comment avez-vous montre votre assurance ? (ton, contact visuel, posture)",
    "tcdEx.dear.confidentHint": "Ayez l'air competent et EFFICACE. Utilisez un ton et une attitude confiants ; etablissez un contact visuel adequat. Ne balbutiez pas, ne chuchotez pas, ne regardez pas le sol, ne reculez pas. Ne dites pas 'je ne suis pas sur'.",
    "tcdEx.dear.negotiate": "N - Negocier (alternatives)",
    "tcdEx.dear.negotiatePh": "Ex: 'Et si tu m'envoyais un message quand tu penses arriver tard ?'",
    "tcdEx.dear.negotiateHint": "Soyez pret a DONNER POUR RECEVOIR. Proposez et demandez d'autres solutions au probleme. Reduisez votre demande. Dites non, mais proposez une alternative pour resoudre le probleme autrement.",
    "tcdEx.dear.debrief": "Debriefing apres l'interaction (optionnel)",
    "tcdEx.dear.debriefPh": "Qu'est-ce qui a bien fonctionne ? A ameliorer ?",

    // GIVE Exercise
    "tcdEx.give.name": "GIVE",
    "tcdEx.give.desc": "Directives d'efficacite pour la relation : maintenir la relation.",
    "tcdEx.give.context": "Situation relationnelle",
    "tcdEx.give.contextPh": "Decrivez la situation et la relation que vous voulez preserver",
    "tcdEx.give.gentle": "G - Garder la bienveillance",
    "tcdEx.give.gentlePh": "Comment avez-vous ete agreable et respectueux ? Qu'avez-vous evite ?",
    "tcdEx.give.gentleHint": "SOYEZ AGREABLE et respectueux. Pas d'attaques : n'attaquez ni verbalement ni physiquement. Pas de menaces : decrivez les consequences calmement et sans exageration. Pas de jugements : pas de lecons de morale, pas de 'tu devrais'. Pas d'irrespect : ne souriez pas narquoisement, ne levez pas les yeux au ciel.",
    "tcdEx.give.interest": "I - S'Interesser",
    "tcdEx.give.interestPh": "Comment avez-vous montre votre interet pour l'autre personne ?",
    "tcdEx.give.interestHint": "ECOUTEZ et MANIFESTEZ DE L'INTERET pour l'autre et son point de vue. Faites face a la personne, maintenez un contact visuel. N'interrompez pas et ne parlez pas a la place de l'autre. Soyez sensible au desir de la personne de reporter la discussion. Montrez-vous patient.",
    "tcdEx.give.validate": "V - Valider",
    "tcdEx.give.validatePh": "Comment avez-vous montre que vous comprenez les sentiments et pensees de l'autre ?",
    "tcdEx.give.validateHint": "A travers vos MOTS et vos ACTIONS, montrez que vous comprenez les sentiments et les pensees de la personne sur la situation. Mettez-vous a sa place. 'Je realise que c'est difficile pour toi', 'Je vois que tu es occupe'.",
    "tcdEx.give.easy": "E - Etre cordial",
    "tcdEx.give.easyPh": "Comment avez-vous utilise l'humour ou mis la personne a l'aise ?",
    "tcdEx.give.easyHint": "Utilisez l'humour. SOURIEZ. Mettez la personne a l'aise. Ne vous prenez pas trop au serieux. Amadouez. Preferez la maniere douce a la maniere forte. Soyez diplomate.",
    "tcdEx.give.debrief": "Debriefing (optionnel)",
    "tcdEx.give.debriefPh": "Comment la relation a-t-elle ete impactee ? Qu'avez-vous appris ?",

    // FAST Exercise
    "tcdEx.fast.name": "FAST",
    "tcdEx.fast.desc": "Directives d'efficacite pour le respect de soi : maintenir le respect de soi.",
    "tcdEx.fast.context": "Situation en jeu",
    "tcdEx.fast.contextPh": "Decrivez la situation ou votre respect de soi est en jeu",
    "tcdEx.fast.fair": "F - Fair-play",
    "tcdEx.fast.fairPh": "Comment avez-vous ete honnete envers vous et envers l'autre personne ?",
    "tcdEx.fast.fairHint": "Soyez HONNETE envers VOUS et envers L'AUTRE personne. Rappelez-vous de VALIDER VOS PROPRES sentiments et desirs, aussi bien que ceux de l'autre personne.",
    "tcdEx.fast.apologies": "A - Arreter les excuses",
    "tcdEx.fast.apologiesPh": "Vous etes-vous excuse excessivement ? Pour quoi ne devriez-vous pas vous excuser ?",
    "tcdEx.fast.apologiesHint": "Ne vous excusez pas a outrance. Ne vous excusez pas d'etre en vie ou de faire une demande. Ne vous excusez pas d'avoir un avis ou d'etre en desaccord. Ne PRENEZ PAS UN AIR HONTEUX. N'invalidez pas ce qui est valide.",
    "tcdEx.fast.values": "S - Soutenir ses valeurs",
    "tcdEx.fast.valuesPh": "Quelles valeurs avez-vous defendues dans cette situation ?",
    "tcdEx.fast.valuesHint": "Restez fidele a VOS VALEURS. Ne sacrifiez pas vos valeurs ou votre integrite pour des raisons qui ne seraient pas TRES importantes. Soyez au clair avec votre moralite, avec ce que vous estimez etre une facon valable de penser et d'agir, et defendez votre position.",
    "tcdEx.fast.truthful": "T - Transmettre honnetement",
    "tcdEx.fast.truthfulPh": "Comment avez-vous ete honnete sans exagerer ?",
    "tcdEx.fast.truthfulHint": "Ne mentez pas. Ne vous presentez pas comme impuissant lorsque vous ne l'etes pas. N'exagerez pas et n'inventez pas des excuses.",
    "tcdEx.fast.debrief": "Debriefing (optionnel)",
    "tcdEx.fast.debriefPh": "Comment vous etes-vous senti(e) apres ? Votre respect de soi est-il preserve ?",

    // Dissociation Exercises Page
    "dissocEx.pageTitle": "Exercices : Dissociation et Trauma",
    "dissocEx.pageSubtitle": "Techniques d'ancrage, de stabilisation et de travail avec les parties",
    "dissocEx.grounding.name": "Ancrage et retour au présent",
    "dissocEx.grounding.desc": "Technique 5-4-3-2-1 pour se reconnecter au moment présent en cas de dissociation.",
    "dissocEx.grounding.see5": "5 choses que je vois (en détail)",
    "dissocEx.grounding.see5Ph": "Décrivez 5 choses visibles avec des détails (couleur, forme, taille...)",
    "dissocEx.grounding.hear4": "4 choses que j'entends",
    "dissocEx.grounding.hear4Ph": "Décrivez 4 sons que vous percevez en ce moment",
    "dissocEx.grounding.touch3": "3 choses que je touche",
    "dissocEx.grounding.touch3Ph": "Décrivez 3 sensations tactiles (texture, température...)",
    "dissocEx.grounding.smell2": "2 choses que je sens",
    "dissocEx.grounding.smell2Ph": "Décrivez 2 odeurs perçues",
    "dissocEx.grounding.taste1": "1 chose que je goûte",
    "dissocEx.grounding.taste1Ph": "Décrivez 1 goût",
    "dissocEx.grounding.orientation": "Orientation temporelle",
    "dissocEx.grounding.orientationPh": "Mon nom, la date, l'heure, le lieu, et : 'En ce moment, je suis en sécurité'",
    "dissocEx.grounding.effectiveness": "Efficacité (0-10)",
    "dissocEx.grounding.effectivenessPh": "À quel point l'ancrage a été efficace ? Sensations revenues ?",
    "dissocEx.safe.name": "Le lieu sûr intérieur",
    "dissocEx.safe.desc": "Créez un refuge intérieur de sécurité et de calme, activable en cas de détresse.",
    "dissocEx.safe.visual": "Ce que je vois dans mon lieu sûr",
    "dissocEx.safe.visualPh": "Couleurs, lumière, formes, espace...",
    "dissocEx.safe.sounds": "Ce que j'entends",
    "dissocEx.safe.soundsPh": "Sons de la nature, musique, silence...",
    "dissocEx.safe.touch": "Ce que je touche et ressens dans mon corps",
    "dissocEx.safe.touchPh": "Textures, température, chaleur, légèreté, détente...",
    "dissocEx.safe.smells": "Ce que je sens (odeurs)",
    "dissocEx.safe.smellsPh": "Odeurs associées à ce lieu",
    "dissocEx.safe.keyword": "Mot-clé ou geste associé",
    "dissocEx.safe.keywordPh": "Un mot ou un geste qui active le sentiment de sécurité",
    "dissocEx.safe.body": "Sensation corporelle de sécurité",
    "dissocEx.safe.bodyPh": "Où dans votre corps ressentez-vous la sécurité ?",
    "dissocEx.parts.name": "Travailler avec les parties (IFS)",
    "dissocEx.parts.desc": "Identifiez et communiquez avec une partie protectrice ou blessée de votre psyché.",
    "dissocEx.parts.identified": "Partie identifiée",
    "dissocEx.parts.identifiedPh": "Décrivez la partie (ex: 'la partie qui s'engourdit', 'le critique intérieur')",
    "dissocEx.parts.role": "Son rôle protecteur",
    "dissocEx.parts.rolePh": "De quoi cette partie essaie-t-elle de me protéger ?",
    "dissocEx.parts.communication": "Communication avec la partie",
    "dissocEx.parts.communicationPh": "Que dit-elle ? De quoi a-t-elle peur ? De quoi a-t-elle besoin ?",
    "dissocEx.parts.reassurance": "Réassurance depuis le Self",
    "dissocEx.parts.reassurancePh": "Ce que votre Self (essence calme) dit à cette partie...",
    "dissocEx.parts.newRole": "Nouveau rôle proposé (optionnel)",
    "dissocEx.parts.newRolePh": "Accepterait-elle un rôle différent, moins extrême ?",
    "dissocEx.window.name": "Fenêtre de tolérance",
    "dissocEx.window.desc": "Évaluez votre position dans la fenêtre de tolérance et pratiquez la régulation.",
    "dissocEx.window.state": "Position actuelle",
    "dissocEx.window.statePh": "Hyperactivation (anxiété, panique, colère) / Dans la fenêtre (présent, équilibre) / Hypoactivation (engourdissement, vide)",
    "dissocEx.window.signs": "Signes corporels observés",
    "dissocEx.window.signsPh": "Cœur rapide, muscles tendus, pensées qui s'emballent OU engourdissement, fatigue, déconnexion...",
    "dissocEx.window.trigger": "Ce qui m'a fait sortir de la fenêtre",
    "dissocEx.window.triggerPh": "Événement, pensée, souvenir, interaction...",
    "dissocEx.window.strategy": "Stratégie utilisée pour revenir dans la fenêtre",
    "dissocEx.window.strategyPh": "Hyperactivation : respiration, ancrage, lieu sûr / Hypoactivation : mouvement, stimulation sensorielle, marche",
    "dissocEx.window.result": "Résultat",
    "dissocEx.window.resultPh": "Suis-je revenu(e) dans la fenêtre ? En combien de temps ?",

    // Shared exercise form labels
    "exerciseForm.save": "Sauvegarder",
    "exerciseForm.back": "Retour",
    "exerciseForm.history": "Historique",
    "exerciseForm.sessions": "session(s)",
    "exerciseForm.fillOne": "Veuillez remplir au moins un champ",
    "exerciseForm.saved": "Exercice sauvegardé",
    "exerciseForm.saveError": "Erreur lors de la sauvegarde",

    // Psychoeducation Module UI
    "psychoed.notFound": "Module introuvable.",
    "psychoed.back": "Retour",
    "psychoed.progress": "Progression",
    "psychoed.chapters": "chapitres",
    "psychoed.chapter": "Chapitre",
    "psychoed.quizChild": "Quiz !",
    "psychoed.quizAdult": "Question de réflexion",
    "psychoed.correctChild": "Bravo ! ",
    "psychoed.correctAdult": "Correct ! ",
    "psychoed.wrongChild": "Pas tout à fait ! ",
    "psychoed.wrongAdult": "Pas exactement. ",
    "psychoed.prev": "Précédent",
    "psychoed.next": "Suivant",
    "psychoed.saving": "Sauvegarde...",
    "psychoed.complete": "Terminer le module !",
    "psychoed.completed": "Module terminé ! Bravo !",
    "psychoed.saveError": "Erreur lors de la sauvegarde",
    "psychoed.historyTitle": "Historique",

    // Auth extras
    "auth.autoLogout": "Vous avez été déconnecté(e) par mesure de sécurité après une période d'inactivité.",
    "auth.quote": "La plus grande gloire n'est pas de ne jamais tomber, mais de se relever à chaque chute.",

    // Profile extras
    "profile.manageSubscription": "Gérer mon abonnement",

    // Pricing
    "pricing.chooseAPlan": "Choisissez le plan adapté à vos besoins",
    "pricing.coveredByTherapist": "Couvert par votre thérapeute",
    "pricing.recommended": "Recommandé",
    "pricing.alreadySubscribed": "Déjà abonné",
    "pricing.patientNote": "Les patients suivis par un thérapeute avec un abonnement actif bénéficient d'un accès gratuit.",

    // Subscription extras
    "subscription.verifying": "Vérification du paiement...",
    "subscription.success": "Paiement réussi !",
    "subscription.successMessage": "Votre abonnement est maintenant actif. Profitez de toutes les fonctionnalités.",
    "subscription.failedMessage": "Le paiement n'a pas abouti. Veuillez réessayer.",
    "subscription.retry": "Réessayer",

    // Trial banner
    "trialBanner.trialExpired": "Essai expiré",
    "trialBanner.trialExpiredMessage": "Votre essai gratuit a expiré",
    "trialBanner.subscribePrompt": "Abonnez-vous pour retrouver l'accès à toutes les fonctionnalités.",
    "trialBanner.dontLoseAccess": "Passez à l'abonnement pour ne pas perdre l'accès.",

    // Consent
    "consent.dataProtection": "Protection de vos données",
    "consent.message": "En utilisant Kintsugi TCC, vous acceptez notre politique de confidentialité et le traitement de vos données de santé conformément au RGPD.",

    // Patient exercises extras
    "patientExercises.thoughts": "Pensées :",
    "patientExercises.balancedThought": "Pensée équilibrée :",
    "patientExercises.problemLabel": "Problème :",
    "patientExercises.difficultyLabel": "Difficulté :",
    "patientExercises.rewardLabel": "Récompense :",
    "patientExercises.assignedCount": "{count} exercice(s) assigné(s)",
    "patientExercises.standaloneCount": "{count} entrée(s) autonome(s)",
    "patientExercises.generating": "Génération...",
    "patientExercises.moreEntries": "... et {count} entrée(s) supplémentaire(s)",

    // Meditation instructions
    "meditation.breathing.inst1": "Asseyez-vous confortablement, le dos droit",
    "meditation.breathing.inst2": "Fermez les yeux doucement",
    "meditation.breathing.inst3": "Inspirez lentement par le nez en comptant jusqu'à 4",
    "meditation.breathing.inst4": "Retenez votre souffle pendant 4 secondes",
    "meditation.breathing.inst5": "Expirez lentement par la bouche en comptant jusqu'à 6",
    "meditation.breathing.inst6": "Répétez ce cycle pendant 5 minutes",
    "meditation.bodyScan.inst1": "Allongez-vous confortablement",
    "meditation.bodyScan.inst2": "Fermez les yeux et respirez profondément",
    "meditation.bodyScan.inst3": "Portez attention à vos pieds, notez les sensations",
    "meditation.bodyScan.inst4": "Remontez progressivement: chevilles, mollets, genoux...",
    "meditation.bodyScan.inst5": "Continuez jusqu'au sommet de votre tête",
    "meditation.bodyScan.inst6": "Terminez en prenant conscience de tout votre corps",
    "meditation.gratitudeMed.inst1": "Installez-vous confortablement",
    "meditation.gratitudeMed.inst2": "Respirez profondément trois fois",
    "meditation.gratitudeMed.inst3": "Pensez à trois choses pour lesquelles vous êtes reconnaissant",
    "meditation.gratitudeMed.inst4": "Ressentez la gratitude dans votre cœur",
    "meditation.gratitudeMed.inst5": "Visualisez ces moments positifs",
    "meditation.gratitudeMed.inst6": "Terminez en souriant intérieurement",
    "meditation.innerCalm.inst1": "Trouvez un endroit calme",
    "meditation.innerCalm.inst2": "Asseyez-vous en position confortable",
    "meditation.innerCalm.inst3": "Concentrez-vous sur votre respiration naturelle",
    "meditation.innerCalm.inst4": "Laissez vos pensées passer comme des nuages",
    "meditation.innerCalm.inst5": "Ne jugez pas, observez simplement",
    "meditation.innerCalm.inst6": "Revenez doucement à la respiration si vous vous perdez",
    "meditation.sleepPrep.inst1": "Allongez-vous dans votre lit",
    "meditation.sleepPrep.inst2": "Fermez les yeux et détendez votre mâchoire",
    "meditation.sleepPrep.inst3": "Relâchez toutes les tensions de vos épaules",
    "meditation.sleepPrep.inst4": "Imaginez une lumière douce qui vous enveloppe",
    "meditation.sleepPrep.inst5": "Chaque respiration vous détend davantage",
    "meditation.sleepPrep.inst6": "Laissez-vous glisser vers le sommeil",

    // Defusion instructions
    "defusion.leaves.inst1": "Fermez les yeux et imaginez un ruisseau paisible",
    "defusion.leaves.inst2": "Voyez des feuilles flotter doucement sur l'eau",
    "defusion.leaves.inst3": "Quand une pensée arrive, placez-la sur une feuille",
    "defusion.leaves.inst4": "Regardez la feuille s'éloigner lentement",
    "defusion.leaves.inst5": "Ne retenez pas les feuilles, laissez-les partir",
    "defusion.leaves.inst6": "Continuez pendant 5-10 minutes",
    "defusion.silly.inst1": "Identifiez une pensée qui vous trouble",
    "defusion.silly.inst2": "Répétez-la à voix haute avec une voix de dessin animé",
    "defusion.silly.inst3": "Essayez différentes voix: robot, chantant, très lent...",
    "defusion.silly.inst4": "Remarquez comment cela change votre relation à la pensée",
    "defusion.silly.inst5": "La pensée est toujours là, mais elle a moins de pouvoir",
    "defusion.prefix.inst1": "Prenez une pensée négative, par exemple 'Je suis nul'",
    "defusion.prefix.inst2": "Ajoutez le préfixe: 'J'ai la pensée que je suis nul'",
    "defusion.prefix.inst3": "Puis: 'Je remarque que j'ai la pensée que je suis nul'",
    "defusion.prefix.inst4": "Notez comment cela crée de la distance",
    "defusion.prefix.inst5": "Vous n'êtes pas vos pensées, vous les observez",
    "defusion.clouds.inst1": "Imaginez votre esprit comme un ciel bleu infini",
    "defusion.clouds.inst2": "Les pensées sont des nuages qui traversent ce ciel",
    "defusion.clouds.inst3": "Certains nuages sont sombres, d'autres clairs",
    "defusion.clouds.inst4": "Vous êtes le ciel, pas les nuages",
    "defusion.clouds.inst5": "Les nuages passent, le ciel reste toujours là",
    "defusion.thankMind.inst1": "Quand une pensée négative survient",
    "defusion.thankMind.inst2": "Dites intérieurement: 'Merci, esprit, pour cette pensée'",
    "defusion.thankMind.inst3": "Reconnaissez que votre esprit essaie de vous protéger",
    "defusion.thankMind.inst4": "Puis ramenez gentiment votre attention au présent",
    "defusion.thankMind.inst5": "Cette technique réduit la lutte contre les pensées",

    // Self-compassion content
    "compassion.letter.prompt1": "Décrivez une situation difficile que vous vivez",
    "compassion.letter.prompt2": "Que diriez-vous à un ami cher dans cette situation?",
    "compassion.letter.prompt3": "Écrivez-vous ces mêmes mots de réconfort",
    "compassion.touch.step1": "Placez une main sur votre cœur",
    "compassion.touch.step2": "Sentez la chaleur de votre main",
    "compassion.touch.step3": "Respirez profondément",
    "compassion.touch.step4": "Dites-vous: 'Je traverse un moment difficile'",
    "compassion.touch.step5": "Puis: 'Je mérite de la compassion'",
    "compassion.touch.step6": "Restez ainsi quelques minutes",
    "compassion.phrase1": "Que je sois en sécurité",
    "compassion.phrase2": "Que je sois heureux(se)",
    "compassion.phrase3": "Que je sois en bonne santé",
    "compassion.phrase4": "Que je vive avec facilité",
    "compassion.phrase5": "Je mérite de l'amour et de la compassion",
    "compassion.phrase6": "Je m'accepte tel(le) que je suis",

    // Assertiveness content
    "assertiveness.passive.description": "Éviter les conflits, ne pas exprimer ses besoins",
    "assertiveness.passive.examples": "Je ne dis rien,Ce n'est pas grave,Comme tu veux",
    "assertiveness.passive.consequence": "Frustration, ressentiment, non-respect de soi",
    "assertiveness.aggressive.description": "Imposer ses besoins sans tenir compte des autres",
    "assertiveness.aggressive.examples": "Tu dois faire ça !,C'est toujours pareil avec toi,Tu ne comprends jamais rien",
    "assertiveness.aggressive.consequence": "Conflits, isolement, culpabilité",
    "assertiveness.passiveAggressive.description": "Exprimer indirectement l'hostilité",
    "assertiveness.passiveAggressive.examples": "Bien sûr, comme d'habitude...,Non non, tout va bien (soupir),Fais ce que tu veux",
    "assertiveness.passiveAggressive.consequence": "Malentendus, tensions, relations superficielles",
    "assertiveness.assertive.description": "Exprimer ses besoins tout en respectant les autres",
    "assertiveness.assertive.examples": "Je ressens... quand tu...,J'aurais besoin de...,Je comprends ton point de vue, et je pense que...",
    "assertiveness.assertive.consequence": "Relations saines, respect mutuel, confiance en soi",
    "assertiveness.examplesLabel": "Exemples :",
    "assertiveness.descPlaceholderD": "Quand tu m'as coupé la parole pendant la réunion...",
    "assertiveness.descPlaceholderE": "Je me suis senti(e) frustré(e) et pas écouté(e)...",
    "assertiveness.descPlaceholderS": "J'aimerais pouvoir finir mes phrases avant que tu répondes...",
    "assertiveness.descPlaceholderC": "Cela nous permettrait d'avoir des échanges plus constructifs...",

    // Maslow needs content
    "needs.physiological.description": "Nourriture, eau, sommeil, santé, abri",
    "needs.physiological.q1": "Dormez-vous suffisamment ?",
    "needs.physiological.q2": "Mangez-vous de manière équilibrée ?",
    "needs.physiological.q3": "Avez-vous un logement stable et confortable ?",
    "needs.physiological.q4": "Votre santé physique est-elle satisfaisante ?",
    "needs.safety.description": "Sécurité financière, emploi, santé, propriété",
    "needs.safety.q1": "Vous sentez-vous en sécurité financièrement ?",
    "needs.safety.q2": "Votre emploi est-il stable ?",
    "needs.safety.q3": "Vous sentez-vous physiquement en sécurité ?",
    "needs.safety.q4": "Avez-vous un sentiment de stabilité dans votre vie ?",
    "needs.belonging.description": "Amour, amitié, intimité, famille",
    "needs.belonging.q1": "Avez-vous des relations amicales satisfaisantes ?",
    "needs.belonging.q2": "Vous sentez-vous aimé(e) et soutenu(e) ?",
    "needs.belonging.q3": "Faites-vous partie d'un groupe ou d'une communauté ?",
    "needs.belonging.q4": "Vos relations intimes vous épanouissent-elles ?",
    "needs.esteem.description": "Respect, reconnaissance, confiance en soi",
    "needs.esteem.q1": "Vous sentez-vous respecté(e) par les autres ?",
    "needs.esteem.q2": "Êtes-vous fier(ère) de vos accomplissements ?",
    "needs.esteem.q3": "Avez-vous confiance en vos capacités ?",
    "needs.esteem.q4": "Vous sentez-vous valorisé(e) dans votre travail ?",
    "needs.selfActualization.description": "Créativité, épanouissement, réalisation de son potentiel",
    "needs.selfActualization.q1": "Poursuivez-vous vos passions et intérêts ?",
    "needs.selfActualization.q2": "Avez-vous le sentiment de vous réaliser ?",
    "needs.selfActualization.q3": "Utilisez-vous pleinement vos talents ?",
    "needs.selfActualization.q4": "Contribuez-vous à quelque chose de plus grand que vous ?",

    // Maslow pyramid examples (shown on each pyramid bar)
    "needs.physiological.examples": "Sommeil réparateur / Repos / Calme / Moins de stimulation",
    "needs.safety.examples": "Prévisibilité / Stabilité / Sécurité financière / Matériel",
    "needs.belonging.examples": "Reconnaissance / Valorisation / Affection / Soutien émotionnel",
    "needs.esteem.examples": "Estime de soi / Stimulation / Nouveauté / Sens / Compréhension",
    "needs.selfActualization.examples": "Être écouté / Compris / Pouvoir de choix / Décision / Temps pour moi",

    // Pyramid card
    "needs.pyramidTitle": "Pyramide des besoins de Maslow",
    "needs.pyramidHint": "Cliquez sur un niveau pour explorer ses besoins",
    "needs.evaluateSection": "Évaluez la satisfaction de chaque dimension (1 à 10)",

    // Specific needs table
    "needs.tableTitle": "Tableau : Exploration des besoins fondamentaux",
    "needs.tableHint": "Ajoutez vos besoins spécifiques dans cette catégorie. Vous pouvez en ajouter autant que nécessaire.",
    "needs.col.specific": "Besoin spécifique",
    "needs.col.specific.placeholder": "Ex. Dormir 8h par nuit",
    "needs.col.nourished": "Ce besoin est-il nourri ?",
    "needs.col.response": "Comment pourrais-je mieux y répondre ?",
    "needs.col.response.placeholder": "Actions concrètes…",
    "needs.col.priority": "Priorité",
    "needs.col.deadline": "Échéance",
    "needs.nourished.yes": "Oui",
    "needs.nourished.no": "Non",
    "needs.nourished.partial": "Un peu",
    "needs.priority.high": "Haute",
    "needs.priority.medium": "Moyenne",
    "needs.priority.low": "Basse",
    "needs.addRow": "Ajouter un besoin",

    // Value domains
    "values.domain.family": "Famille",
    "values.domain.family.desc": "Relations familiales, parentalité, être un bon parent/enfant/frère/sœur",
    "values.domain.relationships": "Relations Intimes",
    "values.domain.relationships.desc": "Relations amoureuses, intimité, connexion émotionnelle",
    "values.domain.friends": "Amitié",
    "values.domain.friends.desc": "Amitiés, relations sociales, appartenance à un groupe",
    "values.domain.work": "Travail/Carrière",
    "values.domain.work.desc": "Accomplissement professionnel, contribution, développement",
    "values.domain.education": "Éducation/Apprentissage",
    "values.domain.education.desc": "Curiosité, développement personnel, acquisition de connaissances",
    "values.domain.leisure": "Loisirs",
    "values.domain.leisure.desc": "Activités récréatives, hobbies, plaisirs",
    "values.domain.spirituality": "Spiritualité",
    "values.domain.spirituality.desc": "Connexion spirituelle, sens de la vie, transcendance",
    "values.domain.community": "Communauté/Citoyenneté",
    "values.domain.community.desc": "Engagement social, bénévolat, contribution à la société",
    "values.domain.health": "Santé/Bien-être",
    "values.domain.health.desc": "Santé physique et mentale, équilibre de vie",
    "values.domain.environment": "Environnement/Nature",
    "values.domain.environment.desc": "Connexion à la nature, écologie, respect de l'environnement",

    // Quality category examples
    "qualities.interpersonal.ex": "Empathique,À l'écoute,Généreux,Loyal,Bienveillant",
    "qualities.intellectual.ex": "Curieux,Créatif,Analytique,Perspicace,Réfléchi",
    "qualities.emotional.ex": "Calme,Résilient,Optimiste,Patient,Sensible",
    "qualities.moral.ex": "Honnête,Intègre,Juste,Responsable,Humble",
    "qualities.practical.ex": "Organisé,Fiable,Persévérant,Débrouillard,Efficace",

    // Capability area examples
    "capabilities.professional.ex": "Gestion de projet,Communication,Leadership,Résolution de problèmes",
    "capabilities.social.ex": "Écoute active,Négociation,Travail d'équipe,Networking",
    "capabilities.personal.ex": "Gestion du temps,Adaptabilité,Apprentissage,Organisation",
    "capabilities.creative.ex": "Innovation,Design,Écriture,Artistique",
    "capabilities.physicalSkills.ex": "Sport,Danse,Bricolage,Cuisine",

    // Reward ideas
    "reinforcement.reward1": "Prendre un moment de détente",
    "reinforcement.reward2": "Écouter ma musique préférée",
    "reinforcement.reward3": "Faire une promenade",
    "reinforcement.reward4": "Appeler un ami",
    "reinforcement.reward5": "Prendre un bon repas",
    "reinforcement.reward6": "Regarder un épisode de ma série",
    "reinforcement.reward7": "Lire quelques pages d'un livre",
    "reinforcement.reward8": "Faire une activité créative",
    "reinforcement.describeAccomplishment": "Décrivez votre accomplissement",
    "reinforcement.reflectionPlaceholder": "Qu'avez-vous appris sur vous-même ?",

    // Pomodoro extras
    "pomodoro.min": "min",

    // Cognitive Restructuring Page
    "exercise.cognitive_restructuring": "Restructuration cognitive",
    "exercise.cognitive_restructuring.desc": "Identifiez et modifiez vos schémas de pensée",
    "cogRestructuring.title": "Restructuration Cognitive",
    "cogRestructuring.pageSubtitle": "Identifiez vos distorsions cognitives et développez des pensées alternatives plus équilibrées",
    "cogRestructuring.newEntry": "Nouvelle restructuration",
    "cogRestructuring.history": "Historique",
    "cogRestructuring.step.situation": "Situation",
    "cogRestructuring.step.situationDesc": "Décrivez la situation qui a déclenché vos pensées négatives",
    "cogRestructuring.step.thought": "Pensée automatique",
    "cogRestructuring.step.thoughtDesc": "Quelle pensée vous est venue spontanément ?",
    "cogRestructuring.step.emotions": "Émotions ressenties",
    "cogRestructuring.step.emotionsDesc": "Identifiez les émotions liées à cette pensée",
    "cogRestructuring.step.distortions": "Distorsions cognitives",
    "cogRestructuring.step.distortionsDesc": "Identifiez les biais de raisonnement présents dans votre pensée",
    "cogRestructuring.step.evidence": "Examen des preuves",
    "cogRestructuring.step.evidenceDesc": "Analysez objectivement les preuves pour et contre votre pensée",
    "cogRestructuring.step.alternative": "Pensée alternative",
    "cogRestructuring.step.alternativeDesc": "Formulez une pensée plus équilibrée et réaliste",
    "cogRestructuring.situationPlaceholder": "Ex: Lors d'une réunion, mon collègue n'a pas répondu à mon message...",
    "cogRestructuring.thoughtPlaceholder": "Ex: Il m'ignore, personne ne me respecte...",
    "cogRestructuring.emotionIntensityBefore": "Intensité émotionnelle avant la restructuration",
    "cogRestructuring.emotionIntensityAfter": "Intensité émotionnelle après la restructuration",
    "cogRestructuring.evidenceFor": "Preuves en faveur de cette pensée",
    "cogRestructuring.evidenceForPlaceholder": "Quels faits objectifs soutiennent cette pensée ?",
    "cogRestructuring.evidenceAgainst": "Preuves contre cette pensée",
    "cogRestructuring.evidenceAgainstPlaceholder": "Quels faits objectifs contredisent cette pensée ?",
    "cogRestructuring.alternativePlaceholder": "Ex: Il est peut-être occupé, je n'ai pas assez d'informations pour conclure...",
    "cogRestructuring.actionPlan": "Plan d'action",
    "cogRestructuring.actionPlanPlaceholder": "Que pouvez-vous faire concrètement suite à cette réflexion ?",
    "cogRestructuring.saved": "Restructuration cognitive enregistrée !",
    "cogRestructuring.fillRequired": "Veuillez remplir la situation et la pensée automatique",
    "cogRestructuring.noEntries": "Aucune entrée pour le moment",
    "cogRestructuring.startRestructuring": "Commencez par identifier une pensée automatique négative",
    "cogRestructuring.low": "Faible",
    "cogRestructuring.high": "Intense",
    // Cognitive Distortions
    "distortion.allOrNothing": "Pensée tout-ou-rien",
    "distortion.allOrNothing.desc": "Voir les choses en noir ou blanc, sans nuances",
    "distortion.catastrophizing": "Catastrophisation",
    "distortion.catastrophizing.desc": "Imaginer le pire scénario possible",
    "distortion.overgeneralization": "Surgénéralisation",
    "distortion.overgeneralization.desc": "Tirer une conclusion générale à partir d'un seul événement",
    "distortion.mindReading": "Lecture de pensée",
    "distortion.mindReading.desc": "Supposer ce que les autres pensent sans preuve",
    "distortion.fortuneTelling": "Prédiction de l'avenir",
    "distortion.fortuneTelling.desc": "Prédire négativement l'avenir sans preuve",
    "distortion.magnification": "Magnification / Minimisation",
    "distortion.magnification.desc": "Exagérer le négatif ou minimiser le positif",
    "distortion.emotionalReasoning": "Raisonnement émotionnel",
    "distortion.emotionalReasoning.desc": "Croire que ce qu'on ressent reflète la réalité",
    "distortion.shouldStatements": "Les « je devrais »",
    "distortion.shouldStatements.desc": "S'imposer des règles rigides et irréalistes",
    "distortion.labeling": "Étiquetage",
    "distortion.labeling.desc": "Se définir par un seul événement négatif",
    "distortion.personalization": "Personnalisation",
    "distortion.personalization.desc": "Se sentir responsable de choses hors de son contrôle",

    // Patient progress extras
    "progress.averageMoodValue": "{value}/10",

  },
  en: {
    // Common
    "app.name": "Kintsugi CBT",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.less": "less",
    "common.loading": "Loading...",
    "common.back": "Back",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.or": "or",
    "common.yes": "Yes",
    "common.no": "No",

    // Auth
    "auth.login": "Log in",
    "auth.register": "Create account",
    "auth.logout": "Log out",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.confirmPassword": "Confirm password",
    "auth.fullName": "Full name",
    "auth.iAm": "I am a...",
    "auth.therapist": "Therapist",
    "auth.patient": "Patient",
    "auth.therapistEmail": "Your therapist's email (optional)",
    "auth.therapistEmailHint": "Your therapist will receive an email to activate your account",
    "auth.patientNote": "Note: A therapist must activate your account to access exercises.",
    "auth.loginWith": "Log in with Google",
    "auth.registerWith": "Sign up with Google",
    "auth.noAccount": "Don't have an account?",
    "auth.hasAccount": "Already have an account?",
    "auth.createAccount": "Create my account",
    "auth.creating": "Creating...",
    "auth.connecting": "Connecting...",
    "auth.startJourney": "Start your therapeutic journey",
    "auth.welcome": "Welcome",
    "auth.invalidCredentials": "Invalid credentials",
    "auth.passwordMismatch": "Passwords do not match",
    "auth.passwordMinLength": "Password must be at least 6 characters",
    "auth.accountCreated": "Account created successfully!",
    "auth.loginSuccess": "Logged in successfully!",
    "auth.accountPending": "Your account must be activated by your therapist.",
    "auth.forgotPasswordLink": "Forgot password?",
    "auth.forgotPassword": "Forgot password",
    "auth.forgotPasswordDesc": "Enter your email address and we'll send you a link to reset your password.",
    "auth.sendResetLink": "Send reset link",
    "auth.resetEmailSent": "Email sent!",
    "auth.resetEmailSentDesc": "If an account exists with this email, you will receive a reset link shortly. Check your spam folder.",
    "auth.resetEmailNotReceived": "Didn't receive the email? Check your spam or resend the link.",
    "auth.resendEmail": "Resend email",
    "auth.backToLogin": "Back to login",
    "auth.newPassword": "New password",
    "auth.newPasswordDesc": "Choose a new password for your account.",
    "auth.passwordPolicy": "Password must be at least 8 characters with one uppercase, one lowercase, and one number.",
    "auth.passwordReset": "Password reset!",
    "auth.passwordResetDesc": "Your password has been reset successfully. You can now log in with your new password.",
    "auth.resetPassword": "Reset password",

    // Navigation
    "nav.dashboard": "Dashboard",
    "nav.dersQuestionnaire": "DERS Questionnaire",
    "nav.sexualityTcc": "CBT Sexuality",
    "nav.myDoneExercises": "My completed exercises",
    "nav.statistics": "Statistics",
    "nav.administration": "Administration",
    "nav.patients": "Patients",
    "nav.sessionReports": "Session Reports",
    "nav.exercises": "Exercises",
    "nav.reminders": "Reminders",
    "nav.profile": "My Profile",
    "app.refresh": "Refresh the app",
    "app.refresh_hint": "Reload the latest version of the app without signing out",
    "app.refresh_now": "Refresh",
    "app.update_available": "New version available",
    "nav.exercisesSection": "Exercises",
    "nav.cbtExercises": "CBT Exercises",
    "nav.barkleyParents": "Barkley — Parents",
    "nav.barkleyChildren": "Barkley — Children",
    "nav.barkleyTeens": "Barkley — Teens",
    "nav.myBadges": "My Badges",
    "nav.barkleyParent": "ADHD Children - Parental Guide",
    "nav.barkleyChild": "ADHD Children - Exercises (6-12 yrs)",
    "nav.barkleyTeen": "Teen Exercises (12-17 yrs)",
    "nav.tdahKidsWorkbook": "ADHD Galactic Workbook",
    "nav.psychoed": "Psychoeducation",
    "patient.privacyBanner": "Your exercises are private by default. After each exercise, you can choose to share it with your therapist.",
    "patient.manageSharing": "Manage my sharing",
    "badge.premier_pas": "First Step",
    "badge.explorateur": "Explorer",
    "badge.super_heros_tdah": "ADHD Super-Hero",
    "badge.gardien_jardin": "Garden Guardian",
    "badge.quiz_master": "Quiz Master",
    "badge.regulier": "Regular",
    "badge.champion": "Champion",
    "badge.collectionneur": "Collector",
    "badge.genereux": "Generous",
    "badge.earned": "Badges earned",
    "badge.toUnlock": "Badges to unlock",
    "badge.noBadges": "No badges yet",
    "badge.noBadgesDesc": "Complete exercises to unlock your first badges! Each badge represents a new milestone in your journey.",
    "parentTask.title": "Mission Board",
    "parentTask.todayMissions": "Today's missions",
    "parentTask.totalMissions": "Total missions",
    "parentTask.badgesEarned": "Badges earned",
    "parentTask.streak": "Consecutive days",
    "parentTask.missionBadges": "Mission Badges",
    "parentTask.nextBadge": "Next badge",
    "parentTask.apprentice": "Apprentice Astronaut",
    "parentTask.pilot": "Space Pilot",
    "parentTask.commander": "Galactic Commander",
    "parentTask.hero": "Space Hero",
    "parentTask.legend": "Cosmic Legend",
    "parentTask.master": "Master of the Universe",
    "parentTask.subtitle": "Complete your missions to earn badges!",
    "parentTask.missionComplete": "Mission accomplished!",
    "parentTask.newBadge": "New Badge!",
    "parentTask.allDone": "All of today's missions are complete!",
    "parentTask.addMission": "Add a mission...",
    "parentTask.thisWeek": "This week",
    "parentTask.missions": "missions",
    "parentTask.great": "Awesome!",
    "badge.new": "New!",
    "badge.newBadgeUnlocked": "New badge unlocked!",
    "badge.newBadgesUnlocked": "{count} new badges unlocked!",
    "common.viewAll": "View all",
    "common.errorGeneric": "Error",
    "common.saving": "Saving...",
    "checklist.title": "Morning Checklist",
    "checklist.myTasks": "My morning tasks",
    "checklist.dailyNotes": "Daily notes",
    "checklist.notesPlaceholder": "How was your morning? (optional)",
    "checklist.saved": "Checklist saved!",
    "checklist.errorSaving": "Error saving",
    "checklist.addTask": "Add a task...",
    "checklist.tasks": "tasks",
    "checklist.history": "History (last 14 days)",
    "checklist.noHistory": "No history yet",
    "checklist.perfect": "Perfect!",
    "checklist.great": "Great!",
    "checklist.keepGoing": "Keep going!",
    "checklist.courage": "You can do it!",
    "install.title": "Install Kintsugi CBT",
    "install.subtitle": "Quick access from your home screen",
    "install.howTo": "How to",
    "install.install": "Install",
    "install.screenTitle": "Install on your screen",
    "install.step1": "Tap the",
    "install.share": "Share",
    "install.step1b": "button at the bottom of Safari",
    "install.step2": "Scroll down and tap",
    "install.addToHome": "Add to Home Screen",
    "install.step3": "Tap",
    "install.add": "Add",
    "install.step3b": "in the top right corner",
    "install.understood": "Got it",
    "sessions.title": "Session Reports",
    "sessions.subtitle": "Manage your session reports by patient",
    "sessions.newReport": "New report",
    "sessions.editReport": "Edit report",
    "sessions.createFirst": "Create your first session report",
    "sessions.noReports": "No session reports",
    "sessions.backToList": "Back",
    "sessions.patient": "Patient",
    "sessions.selectPatient": "Select a patient",
    "sessions.allPatients": "All patients",
    "sessions.sessionType": "Session type",
    "sessions.typeInitial": "Initial assessment",
    "sessions.typeSuivi": "CBT follow-up session",
    "sessions.typeAdo": "CBT follow-up — children & teens (12-17)",
    "ado.schoolStress.title": "My school stress journal",
    "ado.schoolStress.desc": "Every school day, 2 minutes: your stress, what happened, what you told yourself, what helped.",
    "ado.exposurePlan.title": "My classroom challenge plan",
    "ado.exposurePlan.desc": "A ladder of challenges from easiest to hardest, with your anxiety before / after.",
    "psychoed.recommendedExercises": "Recommended exercises",
    "psychoed.recommendedExercisesHint": "To put this module into practice. Your therapist can also assign them to you.",
    "psychoed.newTag": "specific",
    "nav.perfectionism": "Perfectionism and excellencism",
    "perfScale.navTitle": "Perfectionism questionnaire",
    "perfScale.subtitle": "20 questions · 5 minutes · track your progress over time",
    "perfScale.history": "My progress",
    "perfScale.pending": "Your therapist invites you to complete this questionnaire",
    "perfScale.dim1": "Excellencism: aiming very high, flexibly.",
    "perfScale.dim2": "Perfectionistic standards: demanding flawlessness.",
    "perfScale.dim3": "Perfectionistic concerns: fear of mistakes, doubt, self-criticism.",
    "perfScale.start": "Start",
    "perfScale.startAgain": "Take the questionnaire again",
    "perfScale.notePlaceholder": "Optional note (current context: exams, overload, holidays…)",
    "perfScale.submit": "See my results",
    "perfScale.saved": "Questionnaire saved",
    "perfScale.needTwo": "Complete the questionnaire at least twice to see your progress.",
    "perfScale.lastResult": "Latest result",
    "perfScale.allResults": "All completions",
    "perfScale.yourResult": "Your result",
    "perfScale.concerns": "Concerns",
    "perfScale.disclaimer": "Non-normed monitoring tool inspired by the Model of Excellencism and Perfectionism (Gaudreau) and the FMPS (Frost). It is not a diagnosis; discuss it with your therapist.",
    "perfScale.trend": "Progress over time",
    "perfScale.trendHint": "Goal: stable or rising excellencism, falling perfectionistic standards and concerns.",
    "psychoed.scaleTag": "questionnaire",
    "perf.journal.title": "Perfectionism journal",
    "perf.journal.desc": "Situation, demanded standard, 'I must' thought, behaviour, cost, then the excellencist version of the standard.",
    "perf.goodEnough.title": "'Good enough' experiment",
    "perf.goodEnough.desc": "Set an 80% criterion, predict, do it, compare: an exposure to imperfection.",
    "perf.costBenefit.title": "Cost-benefit analysis of perfectionism",
    "perf.costBenefit.desc": "Short- and long-term benefits and costs in four boxes, then a personal conclusion.",
    "perf.continuum.title": "Standards continuum",
    "perf.continuum.desc": "Place your standards from 0 to 100 and mark the excellence zone and the perfection zone.",
    "psychoed.perfectionism.title": "Perfectionism and excellencism",
    "psychoed.perfectionismAdo.title": "Perfectionism and excellence (ages 12-17)",
    "psychoed.perfectionismAdo.subtitle": "Aim high without the pressure: understanding perfectionism and becoming an 'excellencist'",
    "nav.grief": "Grief",
    "library.grief": "Grief (CBT)",
    "library.psychoedGrief": "Grief psychoeducation",
    "psychoed.grief.title": "The grieving process",
    "psychoed.grief.subtitle": "Understanding grief, recognising when it gets stuck and moving forward with the CBT approach",
    "psychoed.griefAdo.title": "Living through a loss (12-17)",
    "psychoed.griefAdo.subtitle": "Understanding what happens inside you when you lose someone, and finding ways to move forward without forgetting",
    "grief.memoryJournal.title": "Memory journal",
    "grief.memoryJournal.desc": "Graded exposure to memories: chosen memory, detailed account, emotion before/after, what this memory says about the bond.",
    "grief.letter.title": "Letter to the person who died",
    "grief.letter.desc": "Write what was left unsaid: regrets, gratitude, anger, news from your life, then the imagined reply.",
    "grief.avoidance.title": "Hierarchy of avoided situations",
    "grief.avoidance.desc": "List what you have been avoiding since the death, rate the difficulty, plan graded confrontation and record the outcome.",
    "grief.reengagement.title": "Re-engagement plan",
    "grief.reengagement.desc": "Each week: one value, one concrete activity, anticipated obstacles and a review.",
    "griefScale.navTitle": "Grief scale",
    "griefScale.subtitle": "13 questions · 3 minutes · track your progress over time",
    "griefScale.history": "My progress",
    "griefScale.pending": "Your therapist invites you to complete this scale",
    "griefScale.dim1": "Separation distress: yearning, preoccupation, loneliness.",
    "griefScale.dim2": "Emotional distress and avoidance: acceptance, anger, guilt, avoidance, loss of meaning.",
    "griefScale.dim3": "Impact on life: activities and relationships.",
    "griefScale.monthsLabel": "How many months ago did the loss occur? (optional)",
    "griefScale.start": "Start",
    "griefScale.startAgain": "Complete the scale again",
    "griefScale.submit": "See my results",
    "griefScale.saved": "Scale saved",
    "griefScale.notePlaceholder": "Optional note (current context: anniversary, holidays, paperwork…)",
    "griefScale.yourResult": "Your result",
    "griefScale.total": "Total score",
    "griefScale.level": "Grief level",
    "griefScale.prolongedFlag": "More than six months after the loss with intense grief affecting daily life: talk to your therapist, specific grief work can help.",
    "griefScale.disclaimer": "Non-normed monitoring tool inspired by prolonged grief criteria (ICD-11, DSM-5-TR) and the PG-13 / ICG scales (Prigerson). It is not a diagnosis; intense grief is normal, especially in the first months.",
    "griefScale.trend": "Progress over time",
    "griefScale.trendHint": "Goal: a gradual decrease across the three dimensions, especially the impact on life.",
    "griefScale.needTwo": "Complete the scale at least twice to see your progress.",
    "griefScale.lastResult": "Last result",
    "griefScale.allResults": "All completions",
    "griefScale.level_low": "low",
    "griefScale.level_moderate": "moderate",
    "griefScale.level_high": "high",
    "psychoed.perfectionism.subtitle": "Aim high without burning out: understanding perfectionism and cultivating excellence",
    "psychoed.perfectionism.desc": "10-chapter module: adaptive/maladaptive perfectionism, excellencism (Gaudreau), vicious circle, distortions, costs and benefits, safety behaviours, self-compassion, maintenance plan.",
    "library.adoTeen": "Teen CBT follow-up (12-17)",
    "library.perfectionism": "Perfectionism and excellencism",
    "library.psychoedPerfectionism": "Perfectionism psychoeducation",
    "sessions.field.contexte_familial_scolaire": "Family & school context",
    "sessions.field.implication_parents": "Parental involvement",
    "sessions.field.developpement_motivation": "Development / motivation",
    "cr.contexte_familial_scolaire": "Family & school context",
    "cr.implication_parents": "Parental involvement",
    "cr.developpement_motivation": "Development / motivation",
    "auth.createPassword": "Create your password",
    "auth.createPasswordDesc": "Welcome to Kintsugi TCC! Choose a password to activate your personal access.",
    "auth.accountCreated": "Your access is ready",
    "auth.accountCreatedDesc": "Your password is saved. Sign in with your e-mail address to find your exercises and follow-up.",
    "sessions.typeCouple": "Couples therapy session",
    "sessions.date": "Session date",
    "sessions.save": "Save",
    "sessions.update": "Update",
    "sessions.edit": "Edit",
    "sessions.delete": "Delete",
    "sessions.created": "Report created!",
    "sessions.updated": "Report updated!",
    "sessions.deleted": "Report deleted",
    "sessions.confirmDelete": "Are you sure you want to delete this report?",
    "sessions.search": "Search...",
    "sessions.exportPdf": "Export as PDF",
    "sessions.pdfExported": "PDF exported successfully!",
    "sessions.selectPatientFirst": "Select a patient to manage exercises",
    "sessions.aiSuggestions": "AI Suggestions — click to validate",
    "sessions.pendingExercises": "Pending exercises",
    "sessions.completedExercises": "Completed exercises",
    "sessions.exercisesToAssign": "Exercises to assign with this report",
    "sessions.browseCatalog": "Browse exercise catalog",
    "sessions.searchExercises": "Search exercises...",

    // Document import for report generation
    "docImport.title": "Generate report from a document using AI",
    "docImport.upload": "Upload file",
    "docImport.formats": "Accepted formats: TXT, PDF (max 10 MB)",
    "docImport.generate": "Generate report",
    "docImport.generating": "Analyzing and structuring the report with AI...",
    "docImport.generated": "Report generated successfully!",
    "docImport.error": "Error",
    "docImport.patience": "This may take a few seconds",
    "docImport.invalidFormat": "Unsupported format. Accepted formats: TXT, PDF",
    "docImport.fileTooLarge": "File exceeds the 10 MB limit",

    "sessions.field.informations_generales": "General information",
    "sessions.field.anamnese": "Anamnesis",
    "sessions.field.evaluation_dsm5": "DSM-5 psychopathological evaluation",
    "sessions.field.hypothese_diagnostic_principal": "Primary suspected diagnostic hypothesis",
    "sessions.field.hypotheses_diagnostics_differentiels": "Differential diagnostic hypotheses to explore",
    "sessions.field.hypothese_clinique_generale": "General clinical hypothesis",
    "sessions.field.problematiques": "Identified issues",
    "sessions.field.protocole_therapeutique": "Integrated CBT 2nd and 3rd wave therapeutic protocol",
    "sessions.field.processus_cibles": "Targeted processes",
    "sessions.field.strategies_therapeutiques": "Therapeutic strategies",
    "sessions.field.evaluations_standardisees": "Standardized assessments to administer",
    "sessions.field.objectifs_therapeutiques": "Therapeutic objectives",
    "sessions.field.recommandations_suivi": "Follow-up recommendations",
    "sessions.field.notes_libres": "Free notes (optional)",
    "sessions.field.exercices": "Exercises",
    "sessions.field.agenda_objectifs": "Agenda / session objectives",
    "sessions.field.ameliorations_progres": "Improvements / progress",
    "sessions.field.revue_semaine": "Weekly review / key events",
    "sessions.field.humeur_symptomes": "Mood / symptoms",
    "sessions.field.echelles_administrees": "Scales administered",
    "sessions.field.revue_exercices": "Exercise review",
    "sessions.field.analyse_chaine": "Chain analysis",
    "sessions.field.analyse_chaine_abc": "Chain analysis (ABC)",
    "sessions.field.analyse_chaine_sorc": "Chain analysis (SORC)",
    "sessions.field.situation_pensees_emotions": "Situation - Thoughts - Emotions - Behaviors - Consequences",
    "sessions.field.pensees_automatiques": "Automatic thoughts / beliefs",
    "sessions.field.analyse_tcc": "CBT analysis / key points",
    "sessions.field.interventions": "Interventions",
    "sessions.field.experiences_comportementales": "Behavioral experiments / exposure",
    "sessions.field.nouveaux_exercices": "New exercises / tasks",
    "sessions.field.objectifs_smart": "SMART objectives / adjustment",
    "sessions.field.plan_prochaine_seance": "Plan / next session",

    // Dashboard - Therapist
    "therapist.hello": "Hello,",
    "therapist.overview": "Here's an overview of your activity",
    "therapist.activePatients": "Active patients",
    "therapist.totalPatients": "Total patients",
    "therapist.pendingExercises": "Pending exercises",
    "therapist.completedToday": "Completed today",
    "therapist.quickActions": "Quick actions",
    "therapist.managePatients": "Manage patients",
    "therapist.exerciseLibrary": "Exercise library",
    "therapist.reminders": "Reminders",
    "therapist.recentPatients": "Recent patients",
    "therapist.viewAll": "View all",
    "therapist.exerciseOverview": "Exercise overview by patient",
    "therapist.searchPatient": "Search patient...",
    "therapist.noPatientFound": "No patient found",
    "therapist.showMore": "Show more",
    "therapist.showLess": "Show less",
    "therapist.exerciseReview": "Exercise review",
    "therapist.totalEntries": "Total entries",
    "therapist.activeTypes": "Active types",
    "therapist.completed": "Completed",
    "therapist.pending": "Pending",
    "therapist.searchExerciseType": "Search exercise type...",
    "therapist.noExercisesFound": "No exercises found",
    "therapist.noExerciseData": "This patient has no exercises yet",
    "therapist.allAssignments": "All assigned exercises",
    "therapist.entries": "entries",
    "therapist.types": "types",
    "therapist.pending": "Pending",

    // Dashboard - Patient
    "patient.hello": "Hello,",
    "patient.howAreYou": "How are you feeling today?",
    "patient.consecutiveDays": "Consecutive days",
    "patient.moodEntries": "Mood entries",
    "patient.completedExercises": "Exercises completed",
    "patient.pendingExercises": "Pending exercises",
    "patient.moodEvolution": "Your mood evolution",
    "patient.recentEmotions": "Recent emotions:",
    "patient.yourProgress": "Your progress",
    "patient.exercisesCompleted": "Exercises completed",
    "patient.completedOf": "You completed {completed} out of {total} assigned exercises.",
    "patient.toDo": "To do",
    "patient.allDone": "All caught up!",
    "patient.yourExercises": "Your exercises",
    "patient.noExercisesAssigned": "No exercises have been assigned to you yet. Your therapist will assign them soon.",
    "patient.startExercise": "Start",
    "patient.exerciseProgress": "Progress by exercise",
    "patient.assigned": "Assigned",
    "patient.completed": "Completed",
    "patient.standalone": "Free entries",
    "patient.noProgress": "Start your exercises to see your progress here.",

    // Exercise names
    "exercise.mood": "Mood Tracking",
    "exercise.mood.desc": "Record your emotional state",
    "exercise.emotion_register": "Emotions",
    "exercise.emotion_register.desc": "Emotional regulation through Socratic questioning",
    "exercise.gratitude": "Gratitude",
    "exercise.gratitude.desc": "Practice gratitude",
    "exercise.pomodoro": "Pomodoro",
    "exercise.pomodoro.desc": "Stay focused and productive",
    "exercise.meditation": "Meditation",
    "exercise.meditation.desc": "Practice mindfulness",
    "exercise.cognitive_defusion": "Cognitive Defusion",
    "exercise.cognitive_defusion.desc": "Detach from your thoughts",
    "exercise.self_compassion": "Self-Compassion",
    "exercise.self_compassion.desc": "Be kind to yourself",
    "exercise.behavioral_activation": "Activation",
    "exercise.behavioral_activation.desc": "Plan positive activities",
    "exercise.values_exploration": "Values (ACT)",
    "exercise.values_exploration.desc": "Explore your core values",
    "exercise.needs_exploration": "Needs (Maslow)",
    "exercise.needs_exploration.desc": "Identify your essential needs",
    "exercise.problem_solving": "Problem Solving",
    "exercise.problem_solving.desc": "Solve your difficulties",
    "exercise.qualities_exploration": "Qualities",
    "exercise.qualities_exploration.desc": "Strengthen your self-esteem",
    "exercise.capabilities_exploration": "Capabilities",
    "exercise.capabilities_exploration.desc": "Build your confidence",
    "exercise.assertiveness": "Assertiveness",
    "exercise.assertiveness.desc": "Improve your communication",
    "exercise.self_reinforcement": "Self-Reinforcement",
    "exercise.self_reinforcement.desc": "Celebrate your accomplishments",

    // Profile
    "profile.title": "My Profile",
    "profile.subtitle": "Manage your personal information",
    "profile.accountInfo": "Account information",
    "profile.accountDesc": "Your role and email",
    "profile.editProfile": "Edit profile",
    "profile.setPassword": "Set a password",
    "profile.changePassword": "Change password",
    "profile.setPasswordDesc": "Set a password to also log in with email and password",
    "profile.changePasswordDesc": "Change your current password",
    "profile.googleNotice": "You signed up via Google. Set a password to also log in with email and password.",
    "profile.newPassword": "New password",
    "profile.confirmPassword": "Confirm password",
    "profile.passwordSet": "Password set!",
    "profile.passwordChanged": "Password changed!",
    "profile.profileUpdated": "Profile updated!",
    "profile.role": "Role",
    "profile.appearance": "Appearance",
    "profile.language": "Language",
    "profile.darkMode": "Dark mode",
    "profile.lightMode": "Light mode",
    "profile.autoMode": "Auto (system)",
    "profile.french": "Français",
    "profile.english": "English",
    "profile.spanish": "Español",

    // Landing
    "landing.hero": "Your cognitive behavioral therapy space",
    "landing.heroSub": "Guided CBT exercises, personalized tracking and support adapted to your therapeutic journey.",
    "landing.getStarted": "Get Started",
    "landing.learnMore": "Learn More",
    "landing.heroTitle": "CBT Exercises for your patients",
    "landing.heroDesc": "A complete platform for Cognitive Behavioral Therapy practitioners. Share personalized exercises with your patients and track their progress.",
    "landing.tryFree": "Try for free",
    "landing.assignExercises": "Assign exercises to your patients",
    "landing.trackProgress": "Track their progress",
    "landing.evidenceBased": "Evidence-based CBT tools",
    "landing.secureConfidential": "Secure and confidential",
    "landing.moodTracking": "Emotional tracking",
    "landing.exerciseCount": "16 CBT exercises",
    "landing.completeTools": "Complete therapeutic tools",
    "landing.completeToolsDesc": "A library of exercises based on cognitive behavioral therapies, ACT and mindfulness.",
    "landing.cta": "Create your therapist account",
    "landing.ctaDesc": "Start your 14-day free trial. No credit card required.",
    "landing.ctaButton": "Start for free",
    "landing.howItWorks": "How it works",
    "landing.step1Title": "Create your therapist account",
    "landing.step1Desc": "Sign up as a therapist to access all the tools.",
    "landing.step2Title": "Add your patients",
    "landing.step2Desc": "Invite your patients and activate their accounts to give them access.",
    "landing.step3Title": "Assign exercises",
    "landing.step3Desc": "Choose from our CBT exercise library and customize them.",
    "landing.step4Title": "Track progress",
    "landing.step4Desc": "Review your patients' responses and their progress.",
    "landing.readyToStart": "Ready to get started?",
    "landing.readyDesc": "Join the therapists who already use Kintsugi TCC to support their patients.",
    "landing.createFreeAccount": "Create a free account",
    "landing.footer": "© 2026 Kintsugi TCC — VMLH-TCC EURL. All rights reserved.",
    "landing.privacy": "Privacy Policy",
    "landing.features": "Features",
    "landing.pricing": "Pricing",
    "landing.soloTitle": "Not currently seeing a therapist?",
    "landing.soloDesc": "Access all CBT exercises and the Barkley ADHD program on your own. No commitment, cancel anytime.",
    "landing.soloPrice": "€9/month",
    "landing.soloCTA": "Sign up independently",
    "landing.soloFeature1": "16+ guided CBT exercises",
    "landing.soloFeature2": "Complete Barkley ADHD program",
    "landing.soloFeature3": "Mood and progress tracking",
    "landing.soloFeature4": "Interactive psychoeducation",
    "landing.soloWarning": "While these exercises are accessible independently, guidance from a trained CBT practitioner is strongly recommended for optimal, personalized support. Using these tools alone does not replace professional therapeutic care.",

    // Charter
    "charter.acceptLabel": "I have read and accept the",
    "charter.linkLabel": "Professional Secrecy Charter",
    "charter.acceptSuffix": " and commit to respecting the confidentiality of my patients' data.",
    "charter.badgeTooltip": "Professional secrecy charter accepted",
    "charter.myTherapist": "My therapist",
    "charter.bannerText": "Please accept the Professional Secrecy Charter to display the trust badge to your patients.",
    "charter.acceptBtn": "Accept the charter",
    "charter.readCharter": "Read the full charter",

    // Owner invitation & Referral
    "owner.inviteTitle": "Invite a colleague",
    "owner.inviteDesc": "Create an invitation link for a fellow therapist. They will receive free access without a subscription. Each therapist manages their own patients independently and confidentially.",
    "owner.colleagueName": "Colleague's name",
    "owner.createInvite": "Create invitation",
    "owner.inviteCopied": "Invitation link copied!",
    "owner.invitationsList": "Sent invitations",
    "owner.used": "Used",
    "owner.copyLink": "Copy",
    "referral.title": "Referral program",
    "referral.desc": "Refer fellow therapists and earn 1 free month for every 10 referred therapists who subscribe (monthly or annual with commitment).",
    "referral.copyLink": "Copy link",
    "referral.copied": "Referral link copied!",
    "referral.totalReferred": "Referred",
    "referral.paying": "Subscribers",
    "referral.freeMonths": "Free months",
    "referral.progress": "Next bonus",
    "auth.ownerInvite": "Colleague invitation",
    "auth.ownerInviteNote": "You have been invited by the platform owner. Your access will be free.",

    // Kintsugi & TCC section
    "landing.kintsugi.sectionTitle": "Why Kintsugi?",
    "landing.kintsugi.subtitle": "The art of rebuilding, elevated through therapy",
    "landing.kintsugi.paragraph1": "Kintsugi is an ancient Japanese art of repairing broken ceramics by highlighting the cracks with gold. Rather than hiding the damage, this philosophy transforms wounds into something more beautiful and more precious than before.",
    "landing.kintsugi.paragraph2": "This is exactly the approach of Cognitive Behavioral Therapy. CBT doesn't seek to erase your difficulties, but to help you understand them, work through them, and emerge stronger. Each repaired crack becomes a line of gold — proof of your resilience.",
    "landing.kintsugi.value1Title": "Acknowledge the cracks",
    "landing.kintsugi.value1Desc": "Like Kintsugi, CBT begins by recognizing suffering without denying or minimizing it.",
    "landing.kintsugi.value2Title": "Repair with care",
    "landing.kintsugi.value2Desc": "Structured, scientifically validated exercises to rebuild thought patterns, step by step.",
    "landing.kintsugi.value3Title": "Reveal your strength",
    "landing.kintsugi.value3Desc": "The result isn't a return to the original state, but a stronger, more aware, and more resilient version of yourself.",

    // Exercise pages common
    "exercise.newEntry": "New entry",
    "exercise.history": "History",
    "exercise.previousEntries": "Previous entries",
    "exercise.previousExercises": "Previous Exercises",
    "exercise.previousExplorations": "Previous Explorations",
    "exercise.noEntries": "No entries yet",
    "exercise.save": "Save",
    "exercise.saving": "Saving...",
    "exercise.saved": "Saved!",
    "exercise.completed": "Completed",
    "exercise.pending": "Pending",
    "exercise.responses": "Response(s)",
    "exercise.noResponse": "No response submitted",
    "exercise.entries": "entries",
    "exercise.downloadPDF": "Download PDF",
    "auth.authError": "Authentication error",
    "auth.authenticating": "Authenticating...",
    "reinforcement.saved": "Accomplishment saved!",
    "psychoeducation.previous": "Previous",
    "psychoeducation.next": "Next",
    "exercise.reflection": "Reflection (optional)",
    "exercise.delete": "Delete",
    "exercise.deleted": "Entry deleted",

    // Mood tracker
    "mood.title": "Mood Tracking",
    "mood.subtitle": "Record your daily emotional state",
    "mood.howFeel": "How are you feeling?",
    "mood.veryBad": "Very bad",
    "mood.veryGood": "Very good",
    "mood.whatEmotions": "What emotions are you feeling?",
    "mood.otherEmotion": "Other emotion or feeling...",
    "mood.notes": "Notes (optional)",
    "mood.notesPlaceholder": "How was your day?",
    "mood.record": "Record my mood",
    "mood.recording": "Recording...",
    "mood.recorded": "Mood recorded!",

    // Thought record
    "thought.title": "Emotion Register",
    "thought.subtitle": "Emotional regulation through Socratic questioning",
    "thought.newRecord": "New entry",
    "thought.situation": "Triggering situation",
    "thought.situationPlaceholder": "Describe the situation that triggered this emotion...",
    "thought.automaticThoughts": "Emotion felt",
    "thought.automaticThoughtsPlaceholder": "Describe what you are feeling...",
    "thought.emotions": "Type of emotion",
    "thought.basicEmotions": "Basic emotions",
    "thought.commonEmotions": "Other common emotions",
    "thought.customEmotion": "Other emotion or feeling...",
    "thought.intensity": "Emotional intensity",
    "thought.evidenceFor": "Evidence for",
    "thought.evidenceForPlaceholder": "What evidence supports this thought?",
    "thought.evidenceAgainst": "Evidence against",
    "thought.evidenceAgainstPlaceholder": "What evidence contradicts this thought?",
    "thought.balancedThought": "Balanced thought",
    "thought.balancedThoughtPlaceholder": "What more balanced thought could you adopt?",
    "thought.newIntensity": "New emotional intensity",
    "thought.socratic.title": "Socratic Questioning",
    "thought.socratic.desc": "Question your emotion to determine if it is proportional to the situation",
    "thought.socratic.q1": "Is this emotion proportional to the situation?",
    "thought.socratic.q1placeholder": "Reflect: does the intensity of your emotion match the objective facts of the situation?",
    "thought.socratic.q2": "What would I tell a friend in this situation?",
    "thought.socratic.q2placeholder": "Imagine a close friend experiencing the exact same situation. What would you say to them?",
    "thought.socratic.q3": "What are the objective facts vs. my interpretations?",
    "thought.socratic.q3placeholder": "Separate concrete facts from your assumptions or personal interpretations...",
    "thought.externalize.title": "Externalization and adapted response",
    "thought.externalize.desc": "Distance yourself from your emotion and formulate a constructive response",
    "thought.externalize.letter": "Write to your emotion",
    "thought.externalize.letterPlaceholder": "Address your emotion directly as if it were a separate entity. E.g.: 'Dear anxiety, I recognize you, you are here because... but you are not me and you will pass...'",
    "thought.externalize.response": "My adapted response",
    "thought.externalize.responsePlaceholder": "How do you want to respond to this situation constructively? What behavior do you choose to adopt?",
    "thought.reeval.desc": "After this reflection, how much do you still feel this emotion?",

    // Gratitude
    "gratitude.title": "Gratitude Journal",
    "gratitude.subtitle": "Cultivate gratitude to improve your well-being",
    "gratitude.item": "Gratitude",
    "gratitude.itemPlaceholder": "Something you are grateful for...",

    // Behavioral activation
    "activation.title": "Behavioral Activation",
    "activation.subtitle": "Plan positive activities",
    "activation.activity": "Activity",
    "activation.category": "Category",
    "activation.timeOfDay": "Time of day",
    "activation.plannedFor": "Planned for",
    "activation.enjoyment": "Expected enjoyment",
    "activation.mastery": "Expected mastery",
    "activation.noActivities": "No activities for this day",
    "activation.addActivity": "Add activity",

    // Meditation
    "meditation.title": "Guided Meditation",
    "meditation.subtitle": "Mindfulness practices for your well-being",
    "meditation.sessionsCompleted": "sessions completed",
    "meditation.completed": "Meditation session recorded!",
    "meditation.tips": "Meditation tips",
    "meditation.tip1": "Create a quiet space",
    "meditation.startSession": "Start session",
    "meditation.sessions": "Previous sessions",

    // Cognitive defusion
    "defusion.title": "Cognitive Defusion",
    "defusion.sessionsCompleted": "exercises completed",
    "defusion.completed": "Defusion exercise recorded!",
    "defusion.subtitle": "Detach from your negative thoughts",
    "defusion.yourThought": "Your thought:",
    "defusion.followSteps": "Follow these steps:",

    // Self-compassion
    "compassion.title": "Self-Compassion",
    "compassion.sessionsCompleted": "exercises completed",
    "compassion.completed": "Self-compassion exercise recorded!",
    "compassion.subtitle": "Develop kindness towards yourself",

    // Assertiveness
    "assertiveness.title": "Assertiveness",
    "assertiveness.subtitle": "Improve your assertive communication",
    "assertiveness.descMethod": "The DESC Method",
    "assertiveness.fullResponse": "Your complete DESC response:",

    // Self-reinforcement
    "reinforcement.title": "Self-Reinforcement",
    "reinforcement.subtitle": "Celebrate your accomplishments and reward yourself",
    "reinforcement.newAccomplishment": "New accomplishment",
    "reinforcement.whatAccomplished": "What did you accomplish today?",
    "reinforcement.accomplishmentPlaceholder": "Describe what you achieved, even small things...",
    "reinforcement.difficulty": "Difficulty level",
    "reinforcement.easy": "Easy",
    "reinforcement.medium": "Medium",
    "reinforcement.hard": "Hard",
    "reinforcement.emotionsFelt": "What emotions are you feeling?",
    "reinforcement.emotionsPlaceholder": "E.g.: pride, satisfaction, relief...",
    "reinforcement.chooseReward": "Choose a reward",
    "reinforcement.customReward": "Or write your own reward...",
    "reinforcement.celebrate": "Celebrate this accomplishment",
    "reinforcement.myAccomplishments": "My accomplishments",
    "reinforcement.noAccomplishments": "No accomplishments recorded",
    "reinforcement.startCelebrating": "Start celebrating your successes",

    // Values exploration
    "values.title": "Values Exploration",
    "values.subtitle": "Identify and explore your core values",

    // Needs exploration
    "needs.title": "Needs Exploration",
    "needs.subtitle": "Identify your essential needs according to Maslow",
    "needs.reflectionQuestions": "Reflection questions:",

    // Problem solving
    "problem.title": "Problem Solving",
    "problem.subtitle": "Solve your difficulties step by step",

    // Qualities exploration
    "qualities.title": "Qualities Exploration",
    "qualities.subtitle": "Strengthen your self-esteem",

    // Capabilities exploration
    "capabilities.title": "Capabilities Exploration",
    "capabilities.subtitle": "Build your confidence by recognizing what you can do",
    "capabilities.competenceAreas": "Areas of competence",

    // Patient exercises view
    "patientExercises.assigned": "Assigned exercises",
    "patientExercises.standalone": "Standalone exercises",
    "patientExercises.myExercises": "My exercises",
    "patientExercises.downloadPdf": "Download PDF",
    "psychoeducation.downloadPdf": "Download as PDF",
    "psychoeducation.downloadPdfHint": "Download this module as a PDF (chapters, illustrations, quiz and answer key)",
    "psychoeducation.pdfError": "PDF unavailable",
    "patientExercises.paperVersion": "Paper version",
    "patientExercises.paperVersionHint": "Download the printable worksheet (PDF) to do this exercise on paper",
    "patientExercises.paperVersionError": "Paper worksheet unavailable at the moment",
    "patientExercises.entries": "entry(ies)",
    "patientExercises.responses": "Response(s)",
    "patientExercises.noResponse": "No response submitted",
    "patientExercises.noExercises": "No exercises recorded",
    "patientExercises.completed": "Completed",
    "patientExercises.pending": "Pending",
    "patientExercises.entryRecorded": "Entry recorded",

    // Patient management
    "patientMgmt.title": "Patients",
    "patientMgmt.subtitle": "Manage your patients and their access",
    "patientMgmt.addNew": "Add a new patient",
    "patientMgmt.active": "Active",
    "patientMgmt.inactive": "Inactive",
    "patientMgmt.activate": "Activate",
    "patientMgmt.deactivate": "Deactivate",
    "patientMgmt.viewProgress": "View progress",
    "patientMgmt.viewExercises": "View exercises",

    // Patient progress
    "progress.title": "Progress",
    "progress.notFound": "Patient not found",
    "progress.moodEntries": "Mood entries",
    "progress.exercisesCompleted": "Exercises completed",
    "progress.emotionsProcessed": "Emotions processed",
    "progress.moodHistory": "Mood history",
    "progress.noMoodEntries": "No mood entries",
    "progress.thoughtRecords": "Thought records",
    "progress.thought": "Thought:",
    "progress.balancedThought": "Balanced thought:",
    "progress.noEmotionRegisters": "No emotion registers",
    "progress.gratitudeEntries": "Gratitude entries",
    "progress.noGratitude": "No gratitude entries",
    "progress.assignedExercises": "Assigned exercises",
    "progress.noAssignments": "No exercises assigned",

    // Exercise library
    "library.title": "Exercise Library",
    "library.subtitle": "Browse and assign exercises to your patients",
    "library.customTitle": "Custom title (optional)",
    "library.assign": "Assign",
    "library.assigning": "Assigning...",
    "library.assigned": "Exercise assigned!",
    "library.currentAssignments": "Current assignments",
    "library.remove": "Remove",
    "library.pending": "In progress",
    "library.completed": "Completed",
    "library.assignedToCount": "Assigned to {count} patient(s)",
    "library.exerciseUnassigned": "Exercise removed",

    // Psychoeducation
    "psychoeducation.title": "Psychoeducation",
    "psychoeducation.subtitle": "Theory modules",
    "psychoeducation.modules": "Theory modules",
    "psychoeducation.assignModule": "Assign a module",
    "psychoeducation.moduleAssigned": "Module assigned",
    "psychoeducation.moduleUnassigned": "Module removed",
    "psychoeducation.noModulesAssigned": "No modules have been assigned to you yet. Your therapist will assign them soon.",

    // Reminders
    "reminders.title": "Reminders",
    "reminders.subtitle": "Reminder history and management",

    // Pomodoro
    "pomodoro.title": "Pomodoro",
    "pomodoro.subtitle": "Stay focused and productive",
    "pomodoro.startFocus": "Start a focus session",
    "pomodoro.sessions": "Previous sessions",

    // Subscription
    "subscription.trial": "Free trial",
    "subscription.daysLeft": "days left",

    // Navigation extras
    "nav.myExercises": "My exercises",
    "nav.psychoeducation": "Psychoeducation",
    "nav.theoryModules": "Theory modules",
    "nav.addPatient": "Add a patient",
    "nav.meditations": "Video Meditations",
    "nav.myMeditations": "My Meditations",

    // Video Meditations
    "meditations.title": "Video Meditations",
    "meditations.subtitle": "Upload and manage guided meditation videos for your patients",
    "meditations.uploadNew": "Upload a video",
    "meditations.dropzoneText": "Click to select a video",
    "meditations.videoTitle": "Video title",
    "meditations.titlePlaceholder": "E.g.: Guided breathing 10min",
    "meditations.language": "Language",
    "meditations.description": "Description (optional)",
    "meditations.descPlaceholder": "Meditation description...",
    "meditations.uploading": "Uploading...",
    "meditations.assembling": "Finalizing...",
    "meditations.chunkInfo": "Sending in chunks to support large files",
    "meditations.uploadBtn": "Upload video",
    "meditations.uploadSuccess": "Video uploaded successfully!",
    "meditations.uploadError": "Upload error",
    "meditations.invalidFormat": "Unsupported format. Use MP4, MOV, AVI, WebM or MKV.",
    "meditations.fileTooLarge": "File too large (max 800 MB)",
    "meditations.library": "Video library",
    "meditations.emptyLibrary": "No videos uploaded",
    "meditations.emptyLibraryHint": "Upload your first meditation video above",
    "meditations.assign": "Assign",
    "meditations.assignTo": "Assign to patients",
    "meditations.assignHint": "Select patients who will have access to",
    "meditations.assigned": "Meditation assigned",
    "meditations.unassigned": "Meditation removed",
    "meditations.searchPatient": "Search for a patient...",
    "meditations.noPatients": "No active patients",
    "meditations.confirmDelete": "Delete this meditation?",
    "meditations.deleted": "Meditation deleted",
    "meditations.noSupport": "Your browser does not support video playback.",
    "meditations.patientTitle": "My Meditations",
    "meditations.patientSubtitle": "Guided meditation videos assigned by your therapist",
    "meditations.viewed": "Viewed",
    "meditations.new": "New",
    "meditations.backToList": "Back to list",
    "meditations.noAssignments": "No meditations assigned",
    "meditations.noAssignmentsHint": "Your therapist will assign meditation videos to you",

    // Common extras
    "common.error": "Error saving",
    "common.errorLoading": "Error loading",
    "common.continue": "Continue",
    "common.finish": "Finish",
    "common.start": "Start",
    "common.pause": "Pause",
    "common.resume": "Resume",
    "common.restart": "Restart",
    "common.add": "Add",
    "common.refresh": "Refresh",
    "common.search": "Search...",
    "common.select": "Select",
    "common.selected": "Selected",
    "common.fillRequired": "Please fill in all required fields",
    "common.selectAtLeastOne": "Select at least one item",
    "common.recentHistory": "Recent history",
    "common.noEntriesYet": "No entries yet",
    "common.completed": "Completed",
    "common.pending": "Pending",
    "common.active": "Active",
    "common.inactive": "Inactive",
    "common.access": "Access",
    "common.instructions": "Instructions",
    "common.tip": "Tip",
    "common.optional": "optional",
    "common.examples": "Examples",
    "common.domain": "Domain",
    "common.of": "of",

    // Therapist dashboard extras
    "therapist.noPatients": "No patients yet",
    "therapist.addFirstPatient": "Add your first patient",

    // Patient management extras
    "patientMgmt.patientAdded": "Patient added successfully!",
    "patientMgmt.errorAdding": "Error adding patient",
    "patientMgmt.addPatient": "Add patient",
    "patientMgmt.addNewPatient": "Add a new patient",
    "patientMgmt.fullName": "Full name",
    "patientMgmt.immediateAccess": "The patient will receive immediate access to the platform.",
    "patientMgmt.patientList": "Patient list",
    "patientMgmt.newEmail": "New email",
    "patientMgmt.emailUpdated": "Email updated successfully!",
    "patientMgmt.errorUpdating": "Error updating",
    "patientMgmt.searchPatient": "Search for a patient...",
    "patientMgmt.noPatientFound": "No patient found",
    "patientMgmt.activated": "Patient activated",
    "patientMgmt.deactivated": "Patient deactivated",
    "patientMgmt.tracking": "Track",
    "patientMgmt.pendingPatients": "Pending patients",
    "patientMgmt.pendingPatientsDesc": "These patients registered but are not linked to any therapist.",
    "patientMgmt.claimPatient": "Claim",
    "patientMgmt.patientClaimed": "Patient linked successfully",
    "patientMgmt.errorClaiming": "Error linking patient",
    "patientMgmt.unnamed": "Unnamed",
    "patientMgmt.invitationCode": "Invitation code",
    "patientMgmt.invitationCodeDesc": "Share this link with your patients so they can register.",
    "patientMgmt.copyLink": "Copy link",
    "patientMgmt.linkCopied": "Invitation link copied!",
    "patientMgmt.qrScanDesc": "Scan this QR code to access the registration form.",
    "patientMgmt.downloadQR": "Download QR code",
    "auth.invitedBy": "Invited by",
    "auth.invitationNote": "Your account will be activated after your therapist validates it.",
    "auth.invalidInvitationCode": "Invalid invitation code",
    "auth.passwordPolicy": "Min. 8 characters, 1 uppercase, 1 lowercase, 1 number",
    "auth.healthDataConsent": "I consent to the processing of my health data as part of my therapeutic follow-up, in accordance with the GDPR.",
    "auth.privacyLink": "Privacy Policy",
    "auth.rateLimited": "Too many attempts. Try again in 5 minutes.",
    "patientMgmt.refusePatient": "Refuse",
    "patientMgmt.confirmRefuse": "Are you sure you want to refuse this patient?",
    "patientMgmt.patientRefused": "Patient refused",
    "patientMgmt.errorRefusing": "Error refusing patient",
    "patientMgmt.confirmDelete": "Permanently delete this patient and all their data?",
    "patientMgmt.patientDeleted": "Patient permanently deleted",
    "patientMgmt.errorDeleting": "Error deleting patient",

    // Exercise library extras
    "library.cognitive": "Cognitive",
    "library.behavioral": "Behavioral",
    "library.monitoring": "Monitoring",
    "library.act": "ACT",
    "library.mindfulness": "Mindfulness",
    "library.positive": "Positive Psychology",
    "library.organization": "Organization",
    "library.selfAwareness": "Self-Awareness",
    "library.barkleyParents": "Barkley — Parents",
    "library.barkleyChildren": "Barkley — Children",
    "library.barkleyTeens": "Barkley — Teens",
    "library.tdahAdulte": "ADHD Adults",
    "library.selectPatient": "Select a patient",
    "library.selectPatientError": "Please select a patient",
    "library.assignExercise": "Assign an exercise",
    "library.assignTheExercise": "Assign the exercise",
    "library.exerciseAssigned": "Exercise assigned successfully!",
    "library.errorAssigning": "Error assigning exercise",
    "library.instructionsOptional": "Instructions (optional)",
    "library.instructionsPlaceholder": "Specific instructions for the patient...",
    "library.titleExample": "E.g.: Thought exercise #1",

    // Mood extras
    "mood.selectEmotion": "Select at least one emotion",
    "mood.errorRecording": "Error recording",
    "mood.startTracking": "Start tracking your mood",
    "mood.veryBadLabel": "Very bad",
    "mood.notGood": "Not good",
    "mood.okay": "Okay",
    "mood.good": "Good",
    "mood.veryGoodLabel": "Very good",

    // Emotion labels
    "emotion.happy": "Happy",
    "emotion.calm": "Calm",
    "emotion.anxious": "Anxious",
    "emotion.sad": "Sad",
    "emotion.angry": "Angry",
    "emotion.stressed": "Stressed",
    "emotion.hopeful": "Hopeful",
    "emotion.tired": "Tired",
    "emotion.grateful": "Grateful",
    "emotion.frustrated": "Frustrated",
    "emotion.peaceful": "Peaceful",
    "emotion.motivated": "Motivated",
    "emotion.joy": "Joy",
    "emotion.sadness": "Sadness",
    "emotion.anger": "Anger",
    "emotion.fear": "Fear",
    "emotion.disgust": "Disgust",
    "emotion.surprise": "Surprise",
    "emotion.ashamed": "Ashamed",
    "emotion.guilty": "Guilty",
    "emotion.scared": "Scared",
    "emotion.hopeless": "Hopeless",
    "emotion.irritated": "Irritated",
    "emotion.worried": "Worried",

    // Thought record extras
    "thought.recorded": "Emotion recorded!",
    "thought.errorRecording": "Error recording",
    "thought.yourThought": "Your emotion:",
    "thought.tipTitle": "Tip",
    "thought.tipText": "By writing to your emotion, you create distance that allows you to regulate it. The emotion is not you — it's a temporary reaction.",
    "thought.afterReflection": "After this reflection, how much do you still feel these emotions?",
    "thought.noRecords": "No records",
    "thought.evidenceForTitle": "Evidence for this thought",
    "thought.evidenceAgainstTitle": "Evidence against this thought",
    "thought.evidenceForPrompt": "What facts support this thought?",
    "thought.evidenceAgainstPrompt": "What facts contradict this thought?",
    "thought.balancedThoughtPrompt": "Formulate a more nuanced and realistic thought...",

    // Gratitude extras
    "gratitude.todayGrateful": "Today, I am grateful for...",
    "gratitude.addGratitude": "Add a gratitude",
    "gratitude.reflectionLabel": "Reflection (optional)",
    "gratitude.reflectionPlaceholder": "What do these gratitudes teach me about my life?",
    "gratitude.saveGratitudes": "Save my gratitudes",
    "gratitude.pastGratitudes": "My past gratitudes",
    "gratitude.errorSaving": "Error saving",
    "gratitude.saved": "Gratitudes saved!",
    "gratitude.addAtLeastOne": "Add at least one gratitude",
    "gratitude.startNoting": "Start noting your gratitudes",

    // Behavioral activation extras
    "activation.pageTitle": "Behavioral Activation",
    "activation.daysPlanned": "days planned",
    "activation.saved": "Day recorded!",
    "activation.saveDay": "Save the day",
    "activation.pageSubtitle": "Plan positive activities to improve your mood",
    "activation.completedCount": "completed",
    "activation.addImproveHint": "Add activities to improve your mood",
    "activation.social": "Social",
    "activation.physical": "Physical",
    "activation.creative": "Creative",
    "activation.wellbeing": "Well-being",
    "activation.productive": "Productive",
    "activation.relaxation": "Relaxation",
    "activation.morning": "Morning",
    "activation.afternoon": "Afternoon",
    "activation.evening": "Evening",
    "activation.activityPlaceholder": "E.g.: Walk 20 minutes",

    // Meditation extras
    "meditation.breathing": "Conscious Breathing",
    "meditation.breathingDesc": "Basic exercise to calm the mind",
    "meditation.bodyScan": "Body Scan",
    "meditation.bodyScanDesc": "Become aware of each part of your body",
    "meditation.gratitudeMed": "Gratitude Meditation",
    "meditation.gratitudeMedDesc": "Cultivate a sense of gratitude",
    "meditation.innerCalm": "Inner Calm",
    "meditation.innerCalmDesc": "Find peace and serenity",
    "meditation.sleepPrep": "Sleep Preparation",
    "meditation.sleepPrepDesc": "Deep relaxation before sleep",
    "meditation.minutes": "minutes",
    "meditation.backToMeditations": "Back to meditations",
    "meditation.tipFindTime": "Find the right time",
    "meditation.tipFindTimeDesc": "Morning upon waking or evening before sleep are ideal times.",
    "meditation.tipCreateSpace": "Create a quiet space",
    "meditation.tipCreateSpaceDesc": "Choose a quiet place where you won't be disturbed.",
    "meditation.tipBePatient": "Be patient",
    "meditation.tipBePatientDesc": "Meditation improves with practice. Start with 5 minutes.",

    // Cognitive defusion extras
    "defusion.pageSubtitle": "ACT techniques to distance yourself from your thoughts",
    "defusion.writeThought": "Write a thought that worries you",
    "defusion.thoughtPlaceholder": "E.g.: I'm not good enough...",
    "defusion.startExercise": "Start the exercise",
    "defusion.backToExercises": "Back to exercises",
    "defusion.leaves": "Leaves on a Stream",
    "defusion.leavesDesc": "Visualize your thoughts as leaves floating on water",
    "defusion.silly": "Silly Voice",
    "defusion.sillyDesc": "Repeat the thought in a funny voice to reduce its impact",
    "defusion.prefix": "'I'm having the thought that...'",
    "defusion.prefixDesc": "Add a prefix to create distance from your thoughts",
    "defusion.clouds": "Clouds in the Sky",
    "defusion.cloudsDesc": "Your thoughts are clouds passing through the sky of your mind",
    "defusion.thankMind": "Thank You, Mind",
    "defusion.thankMindDesc": "Thank your mind for its thoughts, then refocus",

    // Self-compassion extras
    "compassion.pageSubtitle": "Learn to be kind to yourself",
    "compassion.letter": "Compassion Letter",
    "compassion.letterDesc": "Write yourself a letter as you would for a dear friend",
    "compassion.touch": "Physical Self-Soothing",
    "compassion.touchDesc": "Use touch to comfort yourself",
    "compassion.phrases": "Kindness Phrases",
    "compassion.phrasesDesc": "Repeat phrases of compassion for yourself",
    "compassion.finishExercise": "Finish the exercise",
    "compassion.nextStep": "Next step",
    "compassion.repeatInstruction": "Repeat each phrase 3 times, slowly and with intention. Click on each phrase once you have repeated it.",
    "compassion.writeHere": "Write here...",
    "compassion.backToExercises": "Back to exercises",

    // Assertiveness extras
    "assertiveness.situationLabel": "Describe a situation where you had difficulty being assertive",
    "assertiveness.situationPlaceholder": "Example: During a work meeting, my colleague interrupted me several times...",
    "assertiveness.emotionsLabel": "What emotions did you feel?",
    "assertiveness.emotionsPlaceholder": "Frustration, anger, shame, anxiety...",
    "assertiveness.styleQuestion": "What communication style did you adopt in this situation?",
    "assertiveness.passive": "Passive",
    "assertiveness.aggressive": "Aggressive",
    "assertiveness.passiveAggressive": "Passive-Aggressive",
    "assertiveness.assertive": "Assertive",
    "assertiveness.descTechnique": "A structured technique for assertive expression.",
    "assertiveness.describe": "Describe",
    "assertiveness.describeDetail": "Describe the facts objectively",
    "assertiveness.express": "Express",
    "assertiveness.expressDetail": "Express your emotions with 'I'",
    "assertiveness.specify": "Specify",
    "assertiveness.specifyDetail": "Specify what you want",
    "assertiveness.consequences": "Consequences",
    "assertiveness.consequencesDetail": "Indicate the positive consequences",
    "assertiveness.reformulate": "Reformulate in your own words",
    "assertiveness.reformulatePlaceholder": "Write how you would formulate your assertive message...",
    "assertiveness.reflectionLabel": "Reflection: How do you feel after this exercise?",
    "assertiveness.reflectionPlaceholder": "What did you learn? Do you feel more confident using this technique?",
    "assertiveness.saved": "Assertiveness exercise saved!",
    "assertiveness.steps.situation": "The Situation",
    "assertiveness.steps.style": "Current Style",
    "assertiveness.steps.desc": "DESC Method",
    "assertiveness.steps.practice": "Practice",

    // Values exploration extras
    "values.pageSubtitle": "Discover what truly matters to you (ACT - Steven C. Hayes)",
    "values.domainOf": "Domain {current} of {total}",
    "values.whatImportant": "What is important to you in this area?",
    "values.importancePlaceholder": "Describe what truly matters to you...",
    "values.howToAct": "How would you like to act in accordance with this value?",
    "values.actionsPlaceholder": "What concrete actions would reflect this value?",
    "values.ratingQuestion": "On a scale of 1 to 10, how much are you currently living according to this value?",
    "values.saved": "Values exploration saved!",
    "values.domainsExplored": "domains explored",

    // Needs exploration extras
    "needs.pageSubtitle": "Maslow's Pyramid - Identify your fundamental needs",
    "needs.howSatisfied": "How satisfied is this need?",
    "needs.notSatisfied": "Not satisfied at all",
    "needs.fullySatisfied": "Fully satisfied",
    "needs.yourReflections": "Your reflections on this need",
    "needs.reflectionsPlaceholder": "What is missing? What is working well?",
    "needs.saveExploration": "Save exploration",
    "needs.saved": "Needs exploration saved!",
    "needs.physiological": "Physiological Needs",
    "needs.safety": "Safety Needs",
    "needs.belonging": "Belonging Needs",
    "needs.esteem": "Esteem Needs",
    "needs.selfActualization": "Self-Actualization Needs",

    // Problem solving extras
    "problem.pageSubtitle": "A structured approach to overcoming difficulties",
    "problem.step.problem": "Define the Problem",
    "problem.step.problemDesc": "Clearly describe the problem to solve",
    "problem.step.goals": "Goals",
    "problem.step.goalsDesc": "What do you want to achieve?",
    "problem.step.solutions": "Generate Solutions",
    "problem.step.solutionsDesc": "Brainstorm without judgment",
    "problem.step.evaluate": "Evaluate Solutions",
    "problem.step.evaluateDesc": "Weigh the pros and cons",
    "problem.step.plan": "Action Plan",
    "problem.step.planDesc": "Concrete steps to follow",
    "problem.whatProblem": "What problem are you facing?",
    "problem.problemPlaceholder": "Describe the problem clearly and specifically...",
    "problem.problemTip": "Be specific. Instead of \"I'm stressed\", try \"I feel overwhelmed by the workload at the office this week\".",
    "problem.whatGoal": "What do you want to achieve?",
    "problem.goalPlaceholder": "Describe your ideal goal...",
    "problem.smartTip": "Specific, Measurable, Achievable, Realistic, Time-bound.",
    "problem.generateSolutions": "Generate as many solutions as possible, even the craziest ones. Don't judge yet!",
    "problem.solution": "Solution",
    "problem.solutionPlaceholder": "Describe the solution...",
    "problem.addSolution": "Add a solution",
    "problem.evaluateIntro": "Evaluate each solution by listing pros and cons.",
    "problem.pros": "Pros (+)",
    "problem.prosPlaceholder": "Positive points...",
    "problem.cons": "Cons (-)",
    "problem.consPlaceholder": "Negative points...",
    "problem.chosenSolution": "Chosen solution:",
    "problem.none": "None",
    "problem.actionSteps": "Action steps:",
    "problem.stepPlaceholder": "Describe the step...",
    "problem.addStep": "Add a step",
    "problem.saved": "Problem solving saved!",

    // Qualities exploration extras
    "qualities.pageSubtitle": "Strengthen your self-esteem by recognizing your strengths",
    "qualities.suggestions": "Quality suggestions",
    "qualities.myQualities": "My Qualities",
    "qualities.quality": "Quality",
    "qualities.qualityPlaceholder": "Name a quality you possess...",
    "qualities.examplePlaceholder": "Give a concrete example where you demonstrated this quality...",
    "qualities.sourcePlaceholder": "Who recognized this quality in you? (friend, family, colleague...)",
    "qualities.addQuality": "Add a quality",
    "qualities.reflection": "Reflection",
    "qualities.reflectionPlaceholder": "How do you feel after listing these qualities? What did you learn about yourself?",
    "qualities.saveQualities": "Save my qualities",
    "qualities.saved": "Qualities exploration saved!",
    "qualities.addAtLeastOne": "Add at least one quality",
    "qualities.interpersonal": "Interpersonal Qualities",
    "qualities.intellectual": "Intellectual Qualities",
    "qualities.emotional": "Emotional Qualities",
    "qualities.moral": "Moral Qualities",
    "qualities.practical": "Practical Qualities",

    // Capabilities exploration extras
    "capabilities.pageSubtitle": "Build your confidence by recognizing what you can do",
    "capabilities.myCapabilities": "My Capabilities",
    "capabilities.capability": "Capability",
    "capabilities.capabilityPlaceholder": "Name a capability or skill...",
    "capabilities.currentLevel": "Current level",
    "capabilities.learning": "Learning",
    "capabilities.competent": "Competent",
    "capabilities.proficient": "Proficient",
    "capabilities.expert": "Expert",
    "capabilities.evidencePlaceholder": "Evidence: describe a situation where you successfully used this capability...",
    "capabilities.goalPlaceholder": "Development goal (optional): how to improve this capability?",
    "capabilities.addCapability": "Add a capability",
    "capabilities.saveCapabilities": "Save my capabilities",
    "capabilities.saved": "Capabilities exploration saved!",
    "capabilities.addAtLeastOne": "Add at least one capability",
    "capabilities.professional": "Professional Skills",
    "capabilities.social": "Social Skills",
    "capabilities.personal": "Personal Skills",
    "capabilities.creative": "Creative Skills",
    "capabilities.physicalSkills": "Physical Skills",

    // Pomodoro extras
    "pomodoro.pageSubtitle": "Productivity and focus technique",
    "pomodoro.taskName": "Task name",
    "pomodoro.taskPlaceholder": "E.g.: Course revision...",
    "pomodoro.duration": "Duration (minutes)",
    "pomodoro.started": "Pomodoro started!",
    "pomodoro.completed": "Pomodoro completed! Take a break.",
    "pomodoro.enterTask": "Enter a task name",
    "pomodoro.errorStarting": "Error starting",
    "pomodoro.todayCount": "Pomodoros today",
    "pomodoro.recentSessions": "Recent sessions",
    "pomodoro.noSessions": "No sessions yet",

    // Patient progress extras
    "progress.averageMood": "Average mood",
    "progress.moodEvolution": "Mood evolution (last 30 days)",
    "progress.notEnoughData": "Not enough data",
    "progress.topEmotions": "Most frequent emotions",
    "progress.mood": "Mood",
    "progress.thoughts": "Thoughts",
    "progress.gratitude": "Gratitude",
    "progress.exercises": "Exercises",
    "progress.moodHistoryTitle": "Mood history",
    "progress.thoughtRecordsTitle": "Thought records",
    "progress.gratitudeJournal": "Gratitude journal",
    "progress.assignedExercisesTitle": "Assigned exercises",
    "progress.situation": "Situation:",
    "progress.balancedThoughtLabel": "Balanced thought:",
    "progress.viewExercises": "View exercises",
    "progress.reportGenerated": "Report generated on",

    // Reminders extras
    "reminders.totalSent": "Total reminders sent",
    "reminders.today": "Reminders today",
    "reminders.pendingReminders": "Pending reminders",
    "reminders.autoReminders": "Automatic reminders",
    "reminders.autoRemindersDesc": "Reminders are automatically sent to patients who haven't completed their exercises after 3 days. Maximum one reminder per day per exercise.",
    "reminders.history": "Reminder history",
    "reminders.assigned": "Assigned:",
    "reminders.lastReminder": "Last reminder:",
    "reminders.resend": "Resend",
    "reminders.sentSuccess": "Reminder sent successfully!",
    "reminders.errorSending": "Error sending reminder",
    "reminders.noReminders": "No reminders sent yet",
    "reminders.autoSendDesc": "Reminders will be automatically sent to patients after 3 days of inactivity",

    // Psychoeducation extras
    "psychoeducation.pageSubtitle": "Understanding the theoretical foundations of cognitive behavioral therapy",
    "psychoeducation.aboutModule": "About this module",
    "psychoeducation.aboutModuleDesc": "Psychoeducation is an essential step in the therapeutic journey. Understanding the mechanisms of your difficulties gives you the power to act. Each module presents a fundamental theoretical model, followed by a quiz to test your understanding.",
    "psychoeducation.sections": "sections",
    "psychoeducation.quiz": "Quiz",
    "psychoeducation.quizTitle": "Comprehension Quiz",
    "psychoeducation.checkAnswers": "Check my answers",
    "psychoeducation.backToModules": "Back to modules",
    "psychoeducation.correct": "correct",
    "psychoeducation.section": "Section",
    "psychoeducation.goToQuiz": "Go to Quiz",
    "psychoeducation.newerModulesTitle": "Advanced Modules",
    "psychoeducation.classicModulesTitle": "Theoretical Foundations",
    "psychoeducation.currentAssignments": "Assigned patients",
    "psychoeducation.addPatient": "Add a patient",

    // AI suggestions extras
    "ai.title": "Personalized AI Suggestions",
    "ai.history": "History",
    "ai.description": "AI will analyze your mood data and recent exercises to suggest CBT exercises adapted to your current situation.",
    "ai.contextLabel": "Additional context (optional)",
    "ai.contextPlaceholder": "Describe how you feel today or a particular situation...",
    "ai.analyzing": "Analyzing...",
    "ai.getSuggestions": "Get suggestions",
    "ai.generated": "Suggestions generated!",
    "ai.errorGenerating": "Error generating suggestions",
    "ai.basedOn": "Based on",
    "ai.entriesAnalyzed": "entries analyzed",
    "ai.averageMood": "Average mood:",
    "ai.newSuggestions": "New suggestions",
    "ai.noHistory": "No suggestion history",
    "ai.avg": "Avg:",

    // MoodChart extras
    "chart.mood": "Mood:",
    "chart.notEnoughData": "Not enough data to display chart",
    "chart.avg": "Avg:",

    // ExerciseIntro extras
    "exerciseIntro.objectives": "Exercise objectives",
    "exerciseIntro.understand": "Understanding the exercise",
    "exerciseIntro.foundation": "Foundation:",

    // Exercise Data - Thought Record
    "exerciseData.emotion_register.title": "Emotion Register",
    "exerciseData.emotion_register.obj1": "Identify and precisely name the emotions felt",
    "exerciseData.emotion_register.obj2": "Question the proportionality of the emotion to the situation",
    "exerciseData.emotion_register.obj3": "Externalize the emotion to create therapeutic distance",
    "exerciseData.emotion_register.obj4": "Develop more adapted emotional responses",
    "exerciseData.emotion_register.explanation": "The Emotion Register is an emotional regulation tool based on Socratic questioning. Rather than being overwhelmed by your emotions, you learn to observe, question, and externalize them. By writing directly to your emotion as if it were a separate entity, you create distance that prevents falling into negative attitudes and helps you choose a more adapted response.",
    "exerciseData.emotion_register.foundation": "Socratic method — Emotional regulation (Beck, Greenberger & Padesky)",
    // Exercise Data - Behavioral Activation
    "exerciseData.behavioral_activation.title": "Behavioral Activation",
    "exerciseData.behavioral_activation.obj1": "Increase pleasant and rewarding activities",
    "exerciseData.behavioral_activation.obj2": "Break the cycle of avoidance and inactivity",
    "exerciseData.behavioral_activation.obj3": "Improve mood through action",
    "exerciseData.behavioral_activation.obj4": "Develop a balanced daily routine",
    "exerciseData.behavioral_activation.explanation": "Behavioral activation is based on the principle that our behaviors directly influence our mood. When feeling depressed, we tend to isolate and avoid activities, which worsens our emotional state. By gradually planning meaningful activities, we can reverse this negative cycle.",
    "exerciseData.behavioral_activation.foundation": "Jacobson et al. — Behavioral Activation (1996)",
    // Exercise Data - Mood Tracking
    "exerciseData.mood_tracking.title": "Mood Tracking",
    "exerciseData.mood_tracking.obj1": "Become aware of your emotional states",
    "exerciseData.mood_tracking.obj2": "Identify emotional patterns and triggers",
    "exerciseData.mood_tracking.obj3": "Measure mood evolution over time",
    "exerciseData.mood_tracking.obj4": "Facilitate communication with your therapist",
    "exerciseData.mood_tracking.explanation": "Daily mood tracking is an essential CBT tool. By regularly recording your mood level and emotions, you develop better emotional awareness. This helps identify patterns, recurring triggers, and objectively measure your progress throughout therapy.",
    "exerciseData.mood_tracking.foundation": "Beck et al. — Self-monitoring in CBT (1979)",
    // Exercise Data - Pomodoro
    "exerciseData.pomodoro.title": "Pomodoro Technique",
    "exerciseData.pomodoro.obj1": "Improve focus and productivity",
    "exerciseData.pomodoro.obj2": "Reduce procrastination and task-related anxiety",
    "exerciseData.pomodoro.obj3": "Learn to manage time effectively",
    "exerciseData.pomodoro.obj4": "Integrate regular breaks for wellbeing",
    "exerciseData.pomodoro.explanation": "The Pomodoro technique divides work into focused intervals (25 minutes) followed by short breaks. This method is particularly useful in CBT for patients with performance anxiety or procrastination, as it makes tasks less intimidating by breaking them into manageable segments.",
    "exerciseData.pomodoro.foundation": "Francesco Cirillo — Pomodoro Technique (1980s)",
    // Exercise Data - Cognitive Defusion
    "exerciseData.cognitive_defusion.title": "Cognitive Defusion",
    "exerciseData.cognitive_defusion.obj1": "Distance yourself from negative thoughts",
    "exerciseData.cognitive_defusion.obj2": "Reduce the emotional impact of thoughts",
    "exerciseData.cognitive_defusion.obj3": "Observe your thoughts without identifying with them",
    "exerciseData.cognitive_defusion.obj4": "Develop psychological flexibility",
    "exerciseData.cognitive_defusion.explanation": "Cognitive defusion is a technique from Acceptance and Commitment Therapy (ACT). Rather than trying to change the content of thoughts, it teaches us to change our relationship with them. The goal is to see thoughts as simple mental events, not as absolute truths.",
    "exerciseData.cognitive_defusion.foundation": "Steven Hayes — ACT and Cognitive Defusion (1999)",
    // Exercise Data - Meditation
    "exerciseData.meditation.title": "Mindfulness Meditation",
    "exerciseData.meditation.obj1": "Develop present-moment awareness",
    "exerciseData.meditation.obj2": "Reduce stress and anxiety",
    "exerciseData.meditation.obj3": "Improve emotional regulation",
    "exerciseData.meditation.obj4": "Cultivate a non-judgmental attitude",
    "exerciseData.meditation.explanation": "Mindfulness meditation involves deliberately paying attention to the present moment, without judgment. Integrated into CBT as MBCT (Mindfulness-Based Cognitive Therapy), it helps prevent depressive relapses and better manage anxiety by developing detached awareness of thoughts and emotions.",
    "exerciseData.meditation.foundation": "Jon Kabat-Zinn — MBSR (1979) / Segal, Williams, Teasdale — MBCT (2002)",
    // Exercise Data - Gratitude
    "exerciseData.gratitude.title": "Gratitude Journal",
    "exerciseData.gratitude.obj1": "Direct attention toward positive aspects",
    "exerciseData.gratitude.obj2": "Counterbalance the natural negativity bias",
    "exerciseData.gratitude.obj3": "Increase subjective wellbeing",
    "exerciseData.gratitude.obj4": "Strengthen positive social relationships",
    "exerciseData.gratitude.explanation": "The gratitude journal is a positive psychology exercise integrated into CBT. By regularly noting things you are grateful for, you train your brain to notice more positive aspects of your life. This helps counterbalance the brain's natural tendency to focus on the negative.",
    "exerciseData.gratitude.foundation": "Robert Emmons — Gratitude Research (2003)",
    // Exercise Data - Self Compassion
    "exerciseData.self_compassion.title": "Self-Compassion",
    "exerciseData.self_compassion.obj1": "Develop a kind attitude toward yourself",
    "exerciseData.self_compassion.obj2": "Reduce excessive self-criticism",
    "exerciseData.self_compassion.obj3": "Strengthen emotional resilience",
    "exerciseData.self_compassion.obj4": "Accept imperfections with humanity",
    "exerciseData.self_compassion.explanation": "Self-compassion involves treating yourself with the same kindness you would offer a dear friend going through a difficulty. It includes three components: self-kindness, recognition of our common humanity, and mindful awareness of our suffering without excessive identification.",
    "exerciseData.self_compassion.foundation": "Kristin Neff — Self-Compassion (2003)",
    // Exercise Data - Values Exploration
    "exerciseData.values_exploration.title": "Values Exploration",
    "exerciseData.values_exploration.obj1": "Identify your core values",
    "exerciseData.values_exploration.obj2": "Evaluate alignment between your actions and values",
    "exerciseData.values_exploration.obj3": "Define concrete actions guided by your values",
    "exerciseData.values_exploration.obj4": "Give meaning and direction to your life",
    "exerciseData.values_exploration.explanation": "Values exploration is a key ACT exercise. Values are like a compass that guides our choices and actions. Unlike goals, they are never achieved but give us direction. By clarifying what truly matters to you, you can make decisions more aligned with your deeper self.",
    "exerciseData.values_exploration.foundation": "Russ Harris — ACT Made Simple (2009)",
    // Exercise Data - Needs Exploration
    "exerciseData.needs_exploration.title": "Needs Exploration",
    "exerciseData.needs_exploration.obj1": "Identify unmet fundamental needs",
    "exerciseData.needs_exploration.obj2": "Understand the hierarchy of human needs",
    "exerciseData.needs_exploration.obj3": "Evaluate the satisfaction level of each need",
    "exerciseData.needs_exploration.obj4": "Plan actions to better meet your needs",
    "exerciseData.needs_exploration.explanation": "Based on Maslow's pyramid, this exercise helps you evaluate how well your fundamental needs are being met. In CBT, understanding unmet needs helps identify sources of distress and define concrete actions to improve quality of life at each level.",
    "exerciseData.needs_exploration.foundation": "Abraham Maslow — Hierarchy of Needs (1943)",
    // Exercise Data - Problem Solving
    "exerciseData.problem_solving.title": "Problem Solving",
    "exerciseData.problem_solving.obj1": "Structure your approach to problems",
    "exerciseData.problem_solving.obj2": "Generate multiple alternative solutions",
    "exerciseData.problem_solving.obj3": "Objectively evaluate advantages and disadvantages",
    "exerciseData.problem_solving.obj4": "Develop a concrete action plan",
    "exerciseData.problem_solving.explanation": "Problem-solving therapy is a structured approach that helps face difficulties systematically. It is particularly useful when feeling overwhelmed or stuck. By breaking a complex problem into manageable steps, you regain a sense of control and competence.",
    "exerciseData.problem_solving.foundation": "D'Zurilla & Goldfried — Problem-Solving Therapy (1971)",
    // Exercise Data - Qualities Exploration
    "exerciseData.qualities_exploration.title": "Qualities Exploration",
    "exerciseData.qualities_exploration.obj1": "Recognize and value your personal qualities",
    "exerciseData.qualities_exploration.obj2": "Counterbalance the tendency to focus on flaws",
    "exerciseData.qualities_exploration.obj3": "Strengthen self-esteem in a realistic way",
    "exerciseData.qualities_exploration.obj4": "Identify qualities to develop further",
    "exerciseData.qualities_exploration.explanation": "This exercise helps you identify and recognize your personal qualities. In CBT, people with low self-esteem tend to minimize their qualities and amplify their flaws. By concretely documenting your qualities with examples, you build a more balanced and realistic self-image.",
    "exerciseData.qualities_exploration.foundation": "Christine Padesky — Strengths-Based Resilience Model (2012)",
    // Exercise Data - Capabilities Exploration
    "exerciseData.capabilities_exploration.title": "Skills Exploration",
    "exerciseData.capabilities_exploration.obj1": "Inventory your skills across different domains",
    "exerciseData.capabilities_exploration.obj2": "Recognize your mastery levels",
    "exerciseData.capabilities_exploration.obj3": "Identify skills to develop",
    "exerciseData.capabilities_exploration.obj4": "Strengthen your sense of self-efficacy",
    "exerciseData.capabilities_exploration.explanation": "Skills exploration helps create a realistic inventory of what you can do. Self-efficacy — the belief in your ability to accomplish tasks — is a key factor in psychological wellbeing. By identifying your skills and planning their development, you strengthen your self-confidence.",
    "exerciseData.capabilities_exploration.foundation": "Albert Bandura — Self-Efficacy (1977)",
    // Exercise Data - Assertiveness
    "exerciseData.assertiveness.title": "Assertive Communication",
    "exerciseData.assertiveness.obj1": "Identify your usual communication style",
    "exerciseData.assertiveness.obj2": "Learn the DESC technique for self-assertion",
    "exerciseData.assertiveness.obj3": "Express your needs while respecting others",
    "exerciseData.assertiveness.obj4": "Develop healthier interpersonal relationships",
    "exerciseData.assertiveness.explanation": "Assertiveness is the ability to express your needs, opinions, and emotions in a direct and respectful way. The DESC technique (Describe, Express, Specify, Consequences) is a structured tool for formulating assertive messages. It is particularly useful in conflict situations or when struggling to set boundaries.",
    "exerciseData.assertiveness.foundation": "Sharon Anthony Bower — Asserting Yourself (1976)",
    // Exercise Data - Self Reinforcement
    "exerciseData.self_reinforcement.title": "Self-Reinforcement",
    "exerciseData.self_reinforcement.obj1": "Recognize and celebrate your accomplishments",
    "exerciseData.self_reinforcement.obj2": "Develop a healthy reward system",
    "exerciseData.self_reinforcement.obj3": "Reinforce positive behaviors",
    "exerciseData.self_reinforcement.obj4": "Increase intrinsic motivation",
    "exerciseData.self_reinforcement.explanation": "Self-reinforcement is based on operant conditioning principles. By rewarding yourself after completing a difficult task or adopting a positive behavior, you increase the likelihood of repeating that behavior. It is a powerful tool for maintaining motivation and building new habits.",
    "exerciseData.self_reinforcement.foundation": "B.F. Skinner — Positive Reinforcement (1938)",
    // Exercise Data - Cognitive Restructuring
    "exerciseData.cognitive_restructuring.title": "Cognitive Restructuring",
    "exerciseData.cognitive_restructuring.obj1": "Identify dysfunctional thought patterns",
    "exerciseData.cognitive_restructuring.obj2": "Challenge irrational beliefs",
    "exerciseData.cognitive_restructuring.obj3": "Develop more realistic and adaptive thoughts",
    "exerciseData.cognitive_restructuring.obj4": "Reduce anxiety and depressive symptoms",
    "exerciseData.cognitive_restructuring.explanation": "Cognitive restructuring is at the heart of CBT. It involves identifying negative automatic thoughts and dysfunctional cognitive schemas, then critically examining them to develop more realistic and adaptive alternatives. This process gradually modifies the core beliefs that maintain psychological suffering.",
    "exerciseData.cognitive_restructuring.foundation": "Aaron Beck — Cognitive Triad and Schemas (1979)",

    // Psychoeducation - CBT Model
    "psycho.cbt.title": "The CBT Model",
    "psycho.cbt.subtitle": "Cognitive Behavioral Therapies",
    "psycho.cbt.desc": "Understanding the link between thoughts, emotions, and behaviors",
    "psycho.cbt.s1t": "Introduction to CBT",
    "psycho.cbt.s1c": "Cognitive Behavioral Therapies (CBT) are a set of scientifically validated psychological treatments, recommended by the WHO and major health authorities worldwide. Developed in the 1960s by Aaron Beck and Albert Ellis, they are based on a fundamental principle: our thoughts influence our emotions, which in turn influence our behaviors. This cycle can be virtuous or vicious. Unlike psychoanalytic therapies, CBT focuses on the present, is structured, and targets concrete objectives. The therapist and patient work in active collaboration, like a team. CBT has proven effective in treating depression, anxiety disorders, phobias, OCD, PTSD, eating disorders, and many other conditions.",
    "psycho.cbt.s2t": "The cognitive triangle",
    "psycho.cbt.s2c": "The central CBT model is the 'cognitive triangle' illustrating the interaction between three components: thoughts (cognitions), emotions, and behaviors. Here's how it works: a situation triggers automatic thoughts, which generate emotions, which lead to behaviors. Concrete example: your friend doesn't reply to your message (situation) -> 'He's mad at me, I don't matter to him' (automatic thought) -> anxiety, sadness (emotions) -> you send 10 messages in a row or never contact them again (behavior). In CBT, we learn to intervene at each vertex of this triangle. Modifying a single element impacts the other two.",
    "psycho.cbt.s3t": "Cognitive distortions",
    "psycho.cbt.s3c": "Aaron Beck identified recurring reasoning errors called 'cognitive distortions.' These are not deliberate lies but automatic filters that distort our perception of reality. The main distortions are: (1) All-or-nothing thinking: seeing things in black or white ('If it's not perfect, it's worthless'). (2) Catastrophizing: imagining the worst possible scenario. (3) Overgeneralization: drawing a general conclusion from a single event. (4) Mind reading: assuming what others think without evidence. (5) Disqualifying the positive: rejecting positive experiences. Recognizing these biases is the first step to correcting them.",
    "psycho.cbt.s4t": "The three waves of CBT",
    "psycho.cbt.s4c": "CBT has evolved through three successive waves. The 1st wave (1950s-60s) is behavioral: focusing on observable behaviors, conditioning (Pavlov, Skinner), and exposure techniques. The 2nd wave (1960s-80s) integrates cognition through Beck and Ellis: automatic thoughts, cognitive schemas, and cognitive restructuring. The 3rd wave (2000s to present) adds acceptance, mindfulness, and values. It includes ACT (Acceptance and Commitment Therapy by Hayes), MBCT (Mindfulness-Based Cognitive Therapy), and Young's Schema Therapy. This latest wave no longer seeks to control negative thoughts but to change our relationship with them.",
    "psycho.cbt.q1q": "What is the fundamental principle of CBT?",
    "psycho.cbt.q1a": "Our behaviors are solely determined by our childhood",
    "psycho.cbt.q1b": "Our thoughts influence our emotions and behaviors",
    "psycho.cbt.q1c": "Emotions are uncontrollable",
    "psycho.cbt.q1d": "Only medication can treat psychological disorders",
    "psycho.cbt.q1e": "CBT is based on the idea that our thoughts influence our emotions and behaviors, and that by changing our thoughts, we can change our reactions.",
    "psycho.cbt.q2q": "What is a cognitive distortion?",
    "psycho.cbt.q2a": "A neurological disorder",
    "psycho.cbt.q2b": "A recurring reasoning error that distorts reality",
    "psycho.cbt.q2c": "A deliberate lie",
    "psycho.cbt.q2d": "A symptom of schizophrenia",
    "psycho.cbt.q2e": "Cognitive distortions are systematic biases in information processing, not deliberate lies.",
    // Psychoeducation - Anxiety
    "psycho.anx.title": "Cognitive Model of Anxiety",
    "psycho.anx.subtitle": "Aaron Beck",
    "psycho.anx.desc": "Understanding the cognitive mechanisms of anxiety according to Beck",
    "psycho.anx.s1t": "Beck's model for anxiety",
    "psycho.anx.s1c": "According to Aaron Beck (1976), anxiety is not directly caused by situations themselves, but by our interpretation of those situations. The cognitive model of anxiety rests on a double bias: a systematic overestimation of danger ('This is very dangerous') and an underestimation of one's own ability to cope ('I won't be able to handle it'). This imbalance between perceived threat and perceived resources creates the anxious state.",
    "psycho.anx.s2t": "Danger schemas",
    "psycho.anx.s2c": "Anxious people develop 'danger schemas': deep cognitive structures centered on vulnerability and threat, often formed during childhood. These schemas function as filters: a neutral event is automatically interpreted as threatening. They capture only information that confirms danger and ignore safety signals. This is called attentional bias toward threat.",
    "psycho.anx.s3t": "The vicious cycle of anxiety",
    "psycho.anx.s3c": "Anxiety is self-maintaining through a vicious cycle: (1) Perception of a threat. (2) Physiological activation (increased heart rate, sweating, tension). (3) Catastrophic interpretation of sensations: 'My heart is racing, I'm having a heart attack!' (4) Increased anxiety. (5) Avoidance or safety behaviors providing temporary relief but preventing verification that the danger wasn't real. This cycle is at the heart of panic disorder and generalized anxiety.",
    "psycho.anx.s4t": "Anxious automatic thoughts",
    "psycho.anx.s4c": "Automatic thoughts in anxiety often start with 'What if...?' They overestimate both the probability and severity of the feared scenario. They are rapid, involuntary, and seem completely credible in the moment. CBT techniques to combat them include: Socratic questioning ('What is the actual probability?'), decentering ('What would I tell a friend?'), the downward arrow technique, and gradual exposure to test anxious predictions in reality.",
    "psycho.anx.q1q": "According to Beck, anxiety is maintained by:",
    "psycho.anx.q1a": "Too much sleep",
    "psycho.anx.q1b": "Overestimation of danger and underestimation of one's abilities",
    "psycho.anx.q1c": "A lack of vitamins",
    "psycho.anx.q1d": "A weak personality",
    "psycho.anx.q1e": "Beck's cognitive model shows that anxiety rests on a double bias: overestimating threats and underestimating one's ability to cope.",
    "psycho.anx.q2q": "Why do avoidance behaviors reinforce anxiety?",
    "psycho.anx.q2a": "Because they physically exhaust the person",
    "psycho.anx.q2b": "Because they prevent verifying that the danger isn't real",
    "psycho.anx.q2c": "Because they cause side effects",
    "psycho.anx.q2d": "Because they have no effect",
    "psycho.anx.q2e": "Avoidance provides temporary relief but prevents the brain from learning that the situation is not dangerous, which maintains and reinforces anxiety.",
    // Psychoeducation - Depression
    "psycho.dep.title": "Cognitive Model of Depression",
    "psycho.dep.subtitle": "Aaron Beck",
    "psycho.dep.desc": "The cognitive triad and depressogenic schemas by Beck",
    "psycho.dep.s1t": "Beck's cognitive triad",
    "psycho.dep.s1c": "Aaron Beck (1967) identified three types of negative thoughts characteristic of depression, called the 'cognitive triad.' (1) Negative view of self: 'I'm worthless, I'm a burden to others.' (2) Negative view of the world: 'The world is unfair, nothing works.' (3) Negative view of the future: 'Nothing will ever improve, it's hopeless.' These three components reinforce each other and create a global negative filter through which the depressed person interprets all their experience.",
    "psycho.dep.s2t": "Depressogenic schemas",
    "psycho.dep.s2c": "Depressogenic schemas are deep, rigid beliefs formed in childhood from early experiences (parental criticism, rejection, neglect, loss). They remain latent until activated by a stressful event. Examples: 'I'm not worthy of being loved' (incompetence), 'If others really knew me, they would reject me' (defectiveness). Jeffrey Young's Schema Therapy (1990) developed specific techniques to modify these deep structures.",
    "psycho.dep.s3t": "The vicious cycle of depression",
    "psycho.dep.s3c": "Depression is self-maintaining: negative thoughts lead to decreased motivation, which causes reduced activities (stopping exercise, social isolation). This inactivity deprives the person of pleasure and accomplishment, which 'confirms' the negative thoughts. Social withdrawal also reduces support from loved ones. This cycle can be broken through behavioral activation: resuming enjoyable activities to recreate positive experiences.",
    "psycho.dep.s4t": "Cognitive treatment of depression",
    "psycho.dep.s4c": "Cognitive treatment follows a structured progression. Phase 1: Psychoeducation and behavioral activation. Phase 2: Identifying automatic thoughts using thought records. Phase 3: Cognitive restructuring - examining thoughts as hypotheses, gathering evidence, formulating alternatives. Phase 4: Modifying core schemas. Meta-analyses show CBT is as effective as antidepressants for mild to moderate depression, with a lower relapse rate long-term.",
    "psycho.dep.q1q": "What are the three elements of Beck's cognitive triad?",
    "psycho.dep.q1a": "Past, present, future",
    "psycho.dep.q1b": "Negative view of self, world, and future",
    "psycho.dep.q1c": "Sadness, anger, fear",
    "psycho.dep.q1d": "Thought, emotion, behavior",
    "psycho.dep.q1e": "Beck's cognitive triad describes the three domains of negative thoughts characteristic of depression.",
    "psycho.dep.q2q": "Why is behavioral activation effective against depression?",
    "psycho.dep.q2a": "It eliminates negative thoughts instantly",
    "psycho.dep.q2b": "It recreates positive experiences that contradict negative thoughts",
    "psycho.dep.q2c": "It replaces medication",
    "psycho.dep.q2d": "It eliminates fatigue",
    "psycho.dep.q2e": "By resuming enjoyable activities, the person generates positive experiences that break the vicious cycle of negative thoughts -> inactivity -> confirmation of thoughts.",
    // Psychoeducation - Grey
    "psycho.grey.title": "Grey's Neurobiology",
    "psycho.grey.subtitle": "Jeffrey Alan Grey",
    "psycho.grey.desc": "Behavioral activation and inhibition systems",
    "psycho.grey.s1t": "Reinforcement Sensitivity Theory",
    "psycho.grey.s1c": "Jeffrey Alan Grey (1982) proposed a neurobiological theory of personality based on two fundamental brain systems. The BAS (Behavioral Activation System) drives us toward rewards, while the BIS (Behavioral Inhibition System) holds us back in the face of threats. Each individual has a unique balance between these two systems, explaining differences in temperament and vulnerability to psychological disorders.",
    "psycho.grey.s2t": "BAS: Behavioral Activation System",
    "psycho.grey.s2c": "The BAS is activated by reward signals and is associated with dopaminergic pathways (reward circuit). It generates positive emotions (hope, enthusiasm) and approach behaviors. A highly reactive BAS is associated with extraversion and optimism. An underactive BAS can contribute to depression (anhedonia, loss of motivation). Behavioral activation in CBT aims to restimulate the BAS.",
    "psycho.grey.s3t": "BIS: Behavioral Inhibition System",
    "psycho.grey.s3c": "The BIS is activated by signals of punishment, novelty, and innate fear stimuli. It is associated with the septohippocampal circuit and serotonin. When activated, it produces behavioral inhibition, increased vigilance, and physiological arousal. An overactive BIS is strongly associated with chronic anxiety, phobias, and generalized anxiety disorder. Anxiolytics work by reducing BIS activity.",
    "psycho.grey.s4t": "Clinical and therapeutic implications",
    "psycho.grey.s4c": "Grey's model has direct therapeutic implications. For depression (underactive BAS): behavioral activation to restimulate the reward system. For anxiety (overactive BIS): gradual exposure to reduce sensitivity. For addictions (overactive BAS): regulating impulsivity. Understanding your BAS/BIS profile helps develop adapted strategies. A questionnaire (BIS/BAS Scales by Carver & White, 1994) allows evaluation of your own profile.",
    "psycho.grey.q1q": "Which of Grey's systems is associated with anxiety?",
    "psycho.grey.q1a": "The BAS (Behavioral Activation System)",
    "psycho.grey.q1b": "The BIS (Behavioral Inhibition System)",
    "psycho.grey.q1c": "The parasympathetic nervous system",
    "psycho.grey.q1d": "The prefrontal cortex",
    "psycho.grey.q1e": "The BIS is the inhibition system that, when overactive, is associated with anxiety and hypervigilance.",
    "psycho.grey.q2q": "Which disorder is associated with an underactive BAS?",
    "psycho.grey.q2a": "Generalized anxiety disorder",
    "psycho.grey.q2b": "Depression (anhedonia and loss of motivation)",
    "psycho.grey.q2c": "Obsessive-compulsive disorder",
    "psycho.grey.q2d": "Social phobia",
    "psycho.grey.q2e": "An underactive BAS means the reward system functions less well, leading to loss of motivation, interest, and pleasure - core symptoms of depression.",
    // Psychoeducation - Conditioning
    "psycho.cond.title": "Conditioning",
    "psycho.cond.subtitle": "Pavlov & Skinner",
    "psycho.cond.desc": "The fundamentals of classical and operant conditioning",
    "psycho.cond.s1t": "Classical conditioning (Pavlov)",
    "psycho.cond.s1c": "Ivan Pavlov (1849-1936) discovered classical conditioning while studying salivation in dogs. When a neutral stimulus (a bell) is repeatedly paired with food, the bell alone eventually triggers salivation. In psychopathology, this explains phobias and PTSD: a traumatic event associates fear with neutral stimuli. The 'Little Albert' case (Watson, 1920) demonstrated this experimentally, paving the way for exposure treatments.",
    "psycho.cond.s2t": "Operant conditioning (Skinner)",
    "psycho.cond.s2c": "B.F. Skinner showed that behaviors are shaped by their consequences. Four key mechanisms: (1) Positive reinforcement: adding something pleasant to increase behavior. (2) Negative reinforcement: removing something unpleasant to increase behavior (avoidance relieves anxiety, so we avoid more). (3) Positive punishment: adding something unpleasant to decrease behavior. (4) Negative punishment: removing something pleasant to decrease behavior. Negative reinforcement is the most important in psychopathology.",
    "psycho.cond.s3t": "CBT applications: exposure therapy",
    "psycho.cond.s3c": "Treatment of phobias through gradual exposure directly stems from Pavlov's principles. When a person is repeatedly exposed to the feared stimulus without the negative consequence occurring, the fear-stimulus association gradually weakens (extinction). Exposure can be in vivo, in imagination, or in virtual reality. The key is staying in the situation long enough for anxiety to naturally decrease. Exposure has success rates of 70-90% for specific phobias.",
    "psycho.cond.s4t": "Avoidance: the negative reinforcement trap",
    "psycho.cond.s4c": "Avoidance is the central mechanism maintaining anxiety through negative reinforcement. When you avoid an anxiety-provoking situation, you feel immediate relief which 'rewards' the brain and reinforces avoidance. The problem: you never verify the situation isn't actually dangerous. Avoidance gradually expands: first the elevator, then high floors, then buildings, then you don't leave home. In CBT, this is reversed through gradual exposure.",
    "psycho.cond.q1q": "Why does avoidance maintain anxiety?",
    "psycho.cond.q1a": "Because it requires a lot of energy",
    "psycho.cond.q1b": "Because temporary relief reinforces avoidance behavior (negative reinforcement)",
    "psycho.cond.q1c": "Because it causes headaches",
    "psycho.cond.q1d": "Because it has no effect",
    "psycho.cond.q1e": "Avoidance provides immediate relief that reinforces the tendency to avoid, but prevents discovering that the situation is not dangerous.",
    "psycho.cond.q2q": "What is extinction in classical conditioning?",
    "psycho.cond.q2a": "The disappearance of an animal species",
    "psycho.cond.q2b": "The gradual weakening of the conditioned response when the stimulus is no longer paired with the consequence",
    "psycho.cond.q2c": "The increase of fear when facing the stimulus",
    "psycho.cond.q2d": "Complete forgetting of the conditioned stimulus",
    "psycho.cond.q2e": "Extinction occurs when the conditioned stimulus is repeatedly presented without the unconditioned stimulus. The fear response gradually weakens. This is the principle of therapeutic exposure.",
    // Psychoeducation - Darwin
    "psycho.darwin.title": "Emotions According to Darwin",
    "psycho.darwin.subtitle": "Charles Darwin",
    "psycho.darwin.desc": "The evolutionary approach to human emotions",
    "psycho.darwin.s1t": "The universality of emotions",
    "psycho.darwin.s1c": "In his 1872 work 'The Expression of the Emotions in Man and Animals,' Darwin proposed that basic emotions are universal, biologically programmed, and shared by all human beings regardless of culture. Paul Ekman (1971) confirmed this by studying isolated peoples of New Guinea: they expressed and recognized the same basic emotions. This universality indicates deep biological roots.",
    "psycho.darwin.s2t": "The adaptive function of emotions",
    "psycho.darwin.s2c": "For Darwin, each emotion evolved to serve a specific survival function. Fear mobilizes the body to flee or freeze (fight-flight-freeze). Anger provides energy to defend oneself. Disgust protects against toxic substances. Sadness signals loss and attracts social support. Joy reinforces beneficial behaviors. Even 'negative' emotions are essential adaptation tools. The problem is not the emotion itself, but its intensity when it becomes disproportionate.",
    "psycho.darwin.s3t": "Primary and secondary emotions",
    "psycho.darwin.s3c": "Six primary emotions are distinguished: joy, sadness, anger, fear, disgust, and surprise. They are innate, rapid, and universal. Secondary emotions are combinations enriched by cognition: shame (fear + sadness + social cognition), guilt (sadness + anger toward self), pride (joy + self-esteem), jealousy (fear + anger + attachment). Robert Plutchik's (1980) 'wheel of emotions' illustrates how primaries combine like colors. In CBT, precise emotional naming (granularity) allows better regulation.",
    "psycho.darwin.s4t": "Implications for CBT",
    "psycho.darwin.s4c": "Darwin's approach has fundamental therapeutic implications. (1) Normalizing emotions: CBT's goal is not to eliminate 'negative' emotions but to reduce disproportionate intensity. (2) Distinguishing emotion from thought: emotions are automatic body responses; thoughts are our interpretation. CBT works on thoughts to modify emotional responses. (3) Developing emotional intelligence: research shows that simply naming an emotion ('affect labeling') reduces its intensity.",
    "psycho.darwin.q1q": "According to Darwin's approach, 'negative' emotions are:",
    "psycho.darwin.q1a": "Diseases to be eliminated",
    "psycho.darwin.q1b": "Signs of weakness",
    "psycho.darwin.q1c": "Survival mechanisms with an adaptive function",
    "psycho.darwin.q1d": "Solely of cultural origin",
    "psycho.darwin.q1e": "Darwin showed that all emotions have an adaptive function for survival.",
    "psycho.darwin.q2q": "How many universal primary emotions have been identified?",
    "psycho.darwin.q2a": "3 (joy, sadness, anger)",
    "psycho.darwin.q2b": "4 (joy, sadness, anger, fear)",
    "psycho.darwin.q2c": "6 (joy, sadness, anger, fear, disgust, surprise)",
    "psycho.darwin.q2d": "10 or more",
    "psycho.darwin.q2e": "Darwin and Ekman identified 6 universal primary emotions: joy, sadness, anger, fear, disgust, and surprise. They are recognized across all human cultures.",

    // Barkley ADHD Module
    "psycho.barkley.title": "Barkley Program — Child ADHD",
    "psycho.barkley.subtitle": "Parental Support for ADHD",
    "psycho.barkley.desc": "An 8-session program to help parents of children with ADHD adapt their approach and restore a positive relationship.",
    "psycho.barkley.s1t": "Session 1 — Understanding ADHD & the Barkley Program",
    "psycho.barkley.s1c": "ADHD (Attention Deficit Hyperactivity Disorder) is a common neurodevelopmental disorder (about 1 child per class). It is not caused by 'bad parenting'. The difficulties largely stem from fragile executive functions:\n\n• Working memory: the 'mental post-it' that holds instructions.\n• Inhibition: the 'brake' that prevents acting on every impulse.\n• Flexibility: the ability to 'switch tracks' when transitioning between activities.\n\nThe Barkley program is a parental coaching approach that gives you concrete strategies to reduce conflicts and restore a positive relationship. It doesn't judge you — it trains you.\n\nExercise: ADHD Identity Card — List 3 to 5 frequent difficult situations (homework, meals, bedtime…). For each, identify the observed behavior, executive functions involved, and how you feel.",
    "psycho.barkley.s2t": "Session 2 — Restoring Communication: 'Special Moments'",
    "psycho.barkley.s2c": "With an ADHD child, communication often revolves around problems. The goal is to reestablish moments where you simply enjoy being together.\n\n10 to 20 minutes of quality time every day is more powerful than an occasional big outing.\n\nSpecial Moment Rules:\n• One child at a time (exclusivity)\n• No phone/TV/housework simultaneously\n• The child chooses the activity (within acceptable bounds)\n• The parent describes, comments, praises, but gives as few orders as possible\n• Avoid contentious topics (school, behavior…)\n\nEnd with a positive phrase: 'I really enjoyed playing with you.'\n\nTracking: Note each day if the moment happened, the activity, and enjoyment level (0-10) for you and the child.",
    "psycho.barkley.s3t": "Session 3 — Learning to Give Effective Instructions",
    "psycho.barkley.s3c": "Many conflicts arise from a mismatch between how instructions are given and the child's attentional abilities.\n\nBasic rules:\n1. Get physically close, get down to the child's level\n2. Capture attention: eye contact, fewer distractions (screen off…)\n3. Formulate instructions: clear, short, positive ('put your shoes away' rather than 'stop leaving your shoes everywhere')\n4. One instruction at a time; break complex tasks into steps\n5. Ask the child to repeat the instruction\n6. Stay nearby for a few seconds to see if they start\n\nTransformation example:\n'Can you stop messing around every morning!' → 'When the alarm goes off, get dressed first, then come have breakfast.'\n\nExercise: Write down 5 phrases you often say and rework them to be shorter, more positive, and more specific.",
    "psycho.barkley.s4t": "Session 4 — Helping the Child Be Independent",
    "psycho.barkley.s4c": "The goal is to reduce the 'they never leave us alone' feeling and develop the child's autonomy.\n\nExercise — 'Mom/Dad is Busy' Times:\n1. Tell the child what you're going to do ('I'm going to make a phone call')\n2. Specify what they shouldn't do (come talk, interrupt)\n3. Suggest what they can do instead (coloring, legos, reading…)\n4. At first, come back after 1 minute to praise: 'Thank you for not disturbing me, you're doing great on your own.'\n5. Then leave and return after 3-5 minutes, etc.\n\nPractical tool: A small hourglass or visual timer — 'When the sand is at the bottom, you can come talk to me.'\n\nEach day, choose 1 situation to practice and gradually increase the duration.",
    "psycho.barkley.s5t": "Session 5 — Setting Up a Token/Points System",
    "psycho.barkley.s5c": "The goal is to move from 'reminders and shouting' to a structured positive reinforcement system.\n\nSteps:\n1. Create a chart with days as columns and target behaviors as rows (+ a BONUS row)\n2. Choose maximum 3 behaviors to start, phrased positively:\n   • 'I come to the table as soon as I'm called'\n   • 'I put my school supplies in my bag in the evening'\n   • 'I respect the screen time agreed with my parents'\n3. Define rewards with the child:\n   • Small daily rewards (choose the cartoon, 10 min game with mom/dad…)\n   • Big weekly rewards (family movie, small toy, chosen activity)\n4. Exchange rate: 1 good box = 1 point, 10 points = 1 small reward, 30 points = 1 big reward\n5. In case of failure: don't humiliate, simply don't give the point, then remind the rule\n\nTest the system for 7 days without changing the rules mid-course.",
    "psycho.barkley.s6t": "Session 6 — Managing Anger & Opposition: Time-Out",
    "psycho.barkley.s6c": "Opposition is partly normal in development, but exacerbated in ADHD. The ADHD child is often emotionally overwhelmed: the goal of time-out is to create a calm-down space, not to punish.\n\n5-step protocol:\n1. Prevention: simple instructions, stable framework, humor, avoid humiliating words\n2. If opposition starts: get closer, eye contact, remind the rule, offer an alternative\n3. If the crisis escalates: announce withdrawal time, accompany without talking too much\n4. During time-out: don't talk through the door, only return for safety reasons\n5. After the crisis: talk when calm, favor repair over punishment (tidying up, cleaning, setting the table…)\n\nExercise: Build a personalized 'Crisis Management Plan' in 3 columns — warning signs, what I will systematically do, what I will stop doing.\n\nSelf-observation: after each crisis, note the trigger, what you did differently, and what you'd like to improve.",
    "psycho.barkley.s7t": "Session 7 — Managing Behavior in Public Places",
    "psycho.barkley.s7c": "The goal is to generalize home progress to stores, restaurants, transport, etc.\n\nThe 'Training Outing':\n\nBefore the outing:\n1. Choose a simple place to start (quick shopping, short walk)\n2. Stop in front of the place and remind 3 rules maximum\n3. Ask the child to repeat the rules\n4. Announce the reward if rules are followed and the consequence if not\n\nDuring the outing:\n• Identify a possible time-out spot immediately (quiet corner, bench…)\n• Multiply discreet compliments ('thank you for staying near me')\n\nAfter the outing:\n• Debrief: 'what was easy? difficult?'\n• Give the reward quickly if rules were generally followed\n\nDo 1-2 training outings per week, even very short ones.",
    "psycho.barkley.s8t": "Session 8 — Preventing Relapse & Self-Guidance Plan",
    "psycho.barkley.s8c": "The goal is to learn to analyze new problems yourself (new school year, adolescence, new contexts…).\n\nThe key tool is ABC analysis:\n• A — Antecedents: what happens just before (context, instruction, fatigue…)\n• B — Behavior: what the child does precisely\n• C — Consequences: what adults do / what the child gains (attention, task avoidance, screen time…)\n\nFor each problem:\n1. Fill in 3 ABC rows for similar episodes\n2. Identify which antecedents can be modified\n3. Identify which consequences inadvertently reinforce the behavior\n4. Build an action plan combining tools from previous sessions\n\nFinal messages:\n• The child's behavior depends greatly on immediate antecedents and consequences\n• The goal isn't a 'perfect' child but to reduce crises, improve the relationship, and calm the family atmosphere\n• You're progressing step by step — every small change counts.",
    "psycho.barkley.q1q": "ADHD is primarily caused by:",
    "psycho.barkley.q1a": "Bad parenting",
    "psycho.barkley.q1b": "A neurodevelopmental disorder affecting executive functions",
    "psycho.barkley.q1c": "Excess sugar in the diet",
    "psycho.barkley.q1d": "Lack of discipline at school",
    "psycho.barkley.q1e": "ADHD is a neurodevelopmental disorder. Difficulties stem from fragile executive functions (attention, inhibition, working memory), not from parenting.",
    "psycho.barkley.q2q": "What is the recommended duration for the daily 'Special Moment'?",
    "psycho.barkley.q2a": "1 to 2 minutes",
    "psycho.barkley.q2b": "10 to 20 minutes",
    "psycho.barkley.q2c": "At least 1 hour",
    "psycho.barkley.q2d": "There is no recommended duration",
    "psycho.barkley.q2e": "The Barkley program recommends 10 to 20 minutes of quality time per day, on a regular basis. This is more effective than an occasional big outing.",
    "psycho.barkley.q3q": "What is the best way to give instructions to an ADHD child?",
    "psycho.barkley.q3a": "Shout from another room so they hear well",
    "psycho.barkley.q3b": "Give multiple instructions at once to save time",
    "psycho.barkley.q3c": "Get close, capture attention, give a short positive instruction",
    "psycho.barkley.q3d": "Write all instructions on paper and give it to the child",
    "psycho.barkley.q3e": "You should get physically close, capture attention (eye contact), formulate a clear, short, positive instruction, then ask the child to repeat it.",
    "psycho.barkley.q4q": "What is the main purpose of time-out in the Barkley program?",
    "psycho.barkley.q4a": "To punish the child so they understand their mistake",
    "psycho.barkley.q4b": "To create a calm-down space, not punishment",
    "psycho.barkley.q4c": "To isolate the child as long as possible",
    "psycho.barkley.q4d": "To scare the child into obedience",
    "psycho.barkley.q4e": "Time-out is a calm-down space. The ADHD child is often emotionally overwhelmed. After the crisis, repair is preferred over punishment.",

    // Barkley exercise names
    "barkley.identityCard.title": "ADHD Identity Card",
    "barkley.identityCard.desc": "Identify difficult situations and executive functions involved",
    "barkley.specialMoment.title": "Special Moment",
    "barkley.specialMoment.desc": "Daily tracking of parent-child special time",
    "barkley.instructions.title": "Effective Instructions",
    "barkley.instructions.desc": "Transform your instructions to be positive and clear",
    "barkley.tokenSystem.title": "Token/Points System",
    "barkley.tokenSystem.desc": "Set up and track a positive reinforcement system",
    "barkley.crisisPlan.title": "Crisis Management Plan",
    "barkley.crisisPlan.desc": "Build a personalized protocol for managing crises",
    "barkley.abcAnalysis.title": "ABC Analysis",
    "barkley.abcAnalysis.desc": "Analyze antecedents, behaviors, and consequences",
    "barkley.saved": "Exercise saved!",
    "barkley.history": "History",

    // Barkley - Children
    "barkley.childVolcano.title": "My Inner Volcano",
    "barkley.childVolcano.desc": "Identify anger signals and choose a calming strategy",
    "barkley.childAutonomy.title": "My Autonomy Timer",
    "barkley.childAutonomy.desc": "Practice staying alone on a chosen activity",
    "barkley.childSuperpowers.title": "My Super Powers",
    "barkley.childSuperpowers.desc": "Discover your strengths and qualities",
    "barkley.childSolutions.title": "The Solution Wheel",
    "barkley.childSolutions.desc": "Choose a strategy when facing a problem",
    "barkley.childSuccess.title": "My Success Board",
    "barkley.childSuccess.desc": "Note your daily successes, even small ones",
    "barkley.childDetective.title": "The Instruction Detective",
    "barkley.childDetective.desc": "Rephrase an instruction in your own words",

    // Barkley - Teens
    "barkley.teenSituation.title": "My Situation Analysis",
    "barkley.teenSituation.desc": "Analyze a situation with your thoughts and emotions",
    "barkley.teenPlanner.title": "My Weekly Planner",
    "barkley.teenPlanner.desc": "Build and follow your weekly schedule",
    "barkley.teenEmotionJournal.title": "Express Emotion Journal",
    "barkley.teenEmotionJournal.desc": "Note one emotion per day with its trigger",
    "barkley.teenSmartGoals.title": "My SMART Goals",
    "barkley.teenSmartGoals.desc": "Define and track concrete goals",
    "barkley.teenCNV.title": "Non-Violent Communication",
    "barkley.teenCNV.desc": "Practice 4-step requests (NVC)",
    "barkley.teenWeeklyReview.title": "My Weekly Review",
    "barkley.teenWeeklyReview.desc": "Quick weekly self-assessment",

    // ACT Psychoeducation module (Russ Harris)
    "psycho.act.title": "ACT — Acceptance and Commitment Therapy",
    "psycho.act.subtitle": "Based on Russ Harris's work",
    "psycho.act.desc": "Complete module on Acceptance and Commitment Therapy (ACT). Learn the 6 core processes of psychological flexibility.",

    "psycho.act.s1t": "1. What is ACT?",
    "psycho.act.s1c": "ACT (Acceptance and Commitment Therapy) is a third-wave behavioral therapy developed by Steven C. Hayes. Unlike traditional approaches that aim to reduce symptoms, ACT doesn't aim to feel better, but to live better.\n\nACT is based on an observation: suffering is part of life. It's not the presence of difficult thoughts or emotions that causes our problems, but the way we handle them. When we struggle against our internal experiences (experiential avoidance), we remain trapped in a vicious circle.\n\nACT's goal is to develop psychological flexibility: the ability to be in contact with the present moment and, depending on the situation, to maintain or change behavior in service of one's values.",

    "psycho.act.s2t": "2. The Hexaflex model: the 6 processes",
    "psycho.act.s2c": "ACT is organized around 6 interconnected processes, represented in a hexagon (Hexaflex):\n\n1. COGNITIVE DEFUSION — Distancing from your thoughts instead of believing them literally. Thoughts are words and images, not facts.\n\n2. ACCEPTANCE (Making Room) — Welcoming difficult emotions and sensations without fighting them. The opposite of experiential avoidance.\n\n3. CONTACT WITH THE PRESENT MOMENT — Being fully attentive to the here and now, with openness and curiosity.\n\n4. THE OBSERVER SELF (Self as context) — Connecting to the part of us that observes without judging. We are not our thoughts or emotions — we are the context in which they appear.\n\n5. VALUES — Clarifying what truly matters to us. Values are like a compass guiding our actions.\n\n6. COMMITTED ACTION — Taking action in alignment with our values, even in the presence of difficult thoughts and emotions.",

    "psycho.act.s3t": "3. Experiential avoidance: the trap",
    "psycho.act.s3c": "Experiential avoidance is the attempt to suppress, control, or flee from unpleasant internal experiences (thoughts, emotions, memories, sensations). It is the central process of psychological suffering according to ACT.\n\nExamples:\n• Drinking alcohol to avoid feeling anxiety\n• Avoiding social situations for fear of judgment\n• Ruminating to try to solve an emotional problem\n• Constantly distracting yourself to avoid thinking\n\nThe control paradox: The more we fight an emotion, the more it intensifies. Russ Harris uses the 'quicksand monster' metaphor: the more you struggle, the deeper you sink.\n\nThe ACT solution is not to fight, but to observe and welcome these experiences while acting toward what matters.",

    "psycho.act.s4t": "4. Cognitive defusion: detaching from thoughts",
    "psycho.act.s4c": "We have approximately 60,000 to 80,000 thoughts per day. Cognitive fusion is when we take our thoughts as absolute reality. 'I'm worthless' becomes a fact rather than just a thought.\n\nDefusion techniques (Russ Harris):\n\n• 'I'M HAVING THE THOUGHT THAT...' — Add this prefix before any difficult thought. 'I'm incompetent' becomes 'I'm having the thought that I'm incompetent'. This creates instant distance.\n\n• LEAVES ON A STREAM — Visualize each thought as a leaf floating on a stream, drifting away.\n\n• SILLY VOICE — Repeat the thought in a cartoon voice. This reduces its emotional impact.\n\n• THANK YOUR MIND — When your mind produces an anxious thought, simply say 'Thanks mind, that's very interesting'.\n\nDefusion doesn't mean thoughts disappear. It means they lose their grip on our behavior.",

    "psycho.act.s5t": "5. Acceptance and Contact with the present moment",
    "psycho.act.s5c": "Acceptance in ACT does not mean resignation or passive tolerance. It's an active openness to internal experiences, even unpleasant ones.\n\nTHE EXPANSION TECHNIQUE (Making Room):\n1. Observe the sensation in your body\n2. Breathe toward that area\n3. Make room around the sensation\n4. Allow it to be there, without fighting\n\nDROPPING ANCHOR (ACE technique):\nWhen an emotional storm hits:\n• A — Acknowledge: 'I notice I'm feeling anxiety'\n• C — Connect: Push your feet into the floor, stretch, look around\n• E — Engage: Choose an action aligned with your values\n\nDropping anchor doesn't calm the storm — but it prevents you from being swept away. Contact with the present moment is trained through mindfulness exercises: notice 5 things you see, 4 you touch, 3 you hear.",

    "psycho.act.s6t": "6. Values: your inner compass",
    "psycho.act.s6c": "Values are the life directions that matter to you. They differ from goals:\n\n• A GOAL can be achieved (run a marathon)\n• A VALUE is a continuous direction (taking care of your health)\n\nRuss Harris suggests exploring values in different domains:\n• Relationships (family, couple, friendship)\n• Work / studies\n• Leisure / creativity\n• Health / well-being\n• Personal development\n• Community / spirituality\n\nTo identify your values, ask yourself:\n- 'What kind of person do I want to be?'\n- 'If I could act without fear, what would I do?'\n- 'What would I stand for even if it were difficult?'\n\nThe CHOICE POINT is a visual tool: facing a difficult situation, you choose between AWAY moves (flee, avoid) or TOWARD moves (advancing toward your values).",

    "psycho.act.s7t": "7. The Observer Self and the ACT Matrix",
    "psycho.act.s7c": "THE OBSERVER SELF:\nImagine you are the sky. Thoughts and emotions are like clouds — they come and go, sometimes dark, sometimes light. But the sky always remains intact behind the clouds.\n\nYou are not your thoughts, nor your emotions, nor your story. You are the context in which all of this appears.\n\nTHE ACT MATRIX:\nA simple and powerful tool that divides experience into 4 quadrants:\n\n       TOWARD what matters ↑\n  Internal experiences ← → Behaviors\n       AWAY from what matters ↓\n\n• Top right: Actions toward my values\n• Top left: What matters (people, values)\n• Bottom left: Internal obstacles (fears, thoughts, emotions)\n• Bottom right: Avoidance behaviors\n\nThe matrix helps become aware of our patterns and make choices more aligned with our values.",

    "psycho.act.s8t": "8. Committed Action: living according to your values",
    "psycho.act.s8c": "ACT always leads to action. Knowing your values without acting is meaningless. Committed action means:\n\n1. IDENTIFY an important value\n2. DEFINE a SMART goal aligned with it\n3. FIND the smallest possible step\n4. ANTICIPATE internal obstacles\n5. COMMIT to acting WITH difficulties, not WITHOUT them\n\nWillingness is crucial in ACT: are you willing to welcome difficult thoughts and emotions to move toward what matters? It's not courage — it's a conscious choice.\n\nRuss Harris summarizes ACT in one sentence: 'Accept what is out of your personal control, and commit to actions that enrich your life.'\n\nACT is a journey, not a destination. Every day offers choice points. At each one, you can choose to move toward the person you want to be.",

    "psycho.act.q1q": "According to ACT, what is the main goal of therapy?",
    "psycho.act.q1a": "Eliminate negative thoughts",
    "psycho.act.q1b": "Develop psychological flexibility",
    "psycho.act.q1c": "Feel better in all circumstances",
    "psycho.act.q1d": "Control one's emotions",
    "psycho.act.q1e": "ACT does not aim to eliminate negative thoughts or emotions, but to develop psychological flexibility: the ability to be present and act according to one's values, even in the presence of internal difficulties.",

    "psycho.act.q2q": "What is experiential avoidance?",
    "psycho.act.q2a": "Avoiding dangerous situations",
    "psycho.act.q2b": "Ignoring therapist's advice",
    "psycho.act.q2c": "Attempting to suppress or flee from unpleasant internal experiences",
    "psycho.act.q2d": "Refusing to take medication",
    "psycho.act.q2e": "Experiential avoidance is the attempt to control, suppress, or flee from our internal experiences (thoughts, emotions, sensations). Paradoxically, the more we fight, the more suffering intensifies.",

    "psycho.act.q3q": "In the defusion technique 'I'm having the thought that...', what is the goal?",
    "psycho.act.q3a": "Create distance from the thought to reduce its grip",
    "psycho.act.q3b": "Prove that the thought is false",
    "psycho.act.q3c": "Replace the thought with a positive thought",
    "psycho.act.q3d": "Analyze the thought in depth",
    "psycho.act.q3e": "The prefix 'I'm having the thought that...' creates distance between you and the thought. It remains present but loses its power to dictate your actions. This is defusion, not cognitive restructuring.",

    "psycho.act.q4q": "What is the difference between a value and a goal according to ACT?",
    "psycho.act.q4a": "Values are easier to achieve than goals",
    "psycho.act.q4b": "Goals are more important than values",
    "psycho.act.q4c": "A value is a continuous life direction, a goal can be achieved",
    "psycho.act.q4d": "There is no difference",
    "psycho.act.q4e": "A value is like a compass direction — you never reach it, you move toward it (e.g., being a loving parent). A goal is a concrete endpoint (e.g., reading a parenting book this month). Values guide goals.",

    // ACT Exercise names
    "nav.actExercises": "ACT",
    "act.droppingAnchor.title": "Dropping Anchor",
    "act.droppingAnchor.desc": "ACT grounding technique to return to the present moment",
    "act.expansion.title": "Making Room (Expansion)",
    "act.expansion.desc": "Welcome difficult emotions without fighting them",
    "act.observerSelf.title": "The Observer Self",
    "act.observerSelf.desc": "Connect to the part of you that observes without judging",
    "act.choicePoint.title": "The Choice Point",
    "act.choicePoint.desc": "Identify what moves you away from or toward your values",
    "act.matrix.title": "The ACT Matrix",
    "act.matrix.desc": "Analyze your behaviors: toward or away from what matters",
    "act.committedAction.title": "Committed Action",
    "act.committedAction.desc": "Define and follow actions aligned with your values",
    "act.realityWitness.title": "Witness reality",
    "act.realityWitness.desc": "Step out of rumination by anchoring attention in the real world via the 5 senses",
    "act.choiceQuestion.title": "The choice question",
    "act.choiceQuestion.desc": "Does this move me toward or away from the person I want to be?",
    "act.controlMyth.title": "The control myth",
    "act.controlMyth.desc": "Distinguish what I cannot control from what I can partially control",

    // TDAH Adult Psychoeducation
    "psycho.tdah.title": "Adult ADHD — CBT Approach",
    "psycho.tdah.subtitle": "Based on Safren's program and the TCCMontréal guide",
    "psycho.tdah.desc": "Complete module on understanding and managing adult ADHD through CBT.",
    "psycho.tdah.s1t": "1. What is adult ADHD?",
    "psycho.tdah.s1c": "ADHD affects approximately 4-5% of adults. It does not disappear in adulthood — it evolves.\n\nThree presentations: Predominantly Inattentive, Predominantly Hyperactive-Impulsive, and Combined. Adult ADHD is often accompanied by emotional dysregulation, time perception difficulties, hyperfocus, and weak working memory.",
    "psycho.tdah.s2t": "2. Safren's CBT model of ADHD",
    "psycho.tdah.s2c": "Safren's three-level model: 1) Neurobiological deficits (dopamine/norepinephrine), 2) Functional difficulties (lateness, disorganization), 3) Psychological consequences (negative thoughts, anxiety, depression). CBT acts on levels 2 and 3.",
    "psycho.tdah.s3t": "3. ADHD's impact on daily life",
    "psycho.tdah.s3c": "ADHD affects work (missed deadlines, underperformance), relationships (forgetting commitments, emotional reactions), finances (impulsive purchases), and health (sleep disorders, comorbidities: anxiety 50%, depression 30-40%). These are NOT character flaws — they are consequences of a neurobiological disorder.",
    "psycho.tdah.s4t": "4. Lifestyle and ADHD",
    "psycho.tdah.s4c": "Sleep (75% of adults with ADHD have sleep disorders), exercise (increases dopamine — 30 min has effects comparable to stimulant medication), nutrition (regular meals, protein at breakfast), and mindfulness meditation are the foundation of ADHD treatment.",
    "psycho.tdah.s5t": "5. Organization and planning",
    "psycho.tdah.s5c": "ABC Prioritization: A=urgent+important, B=important not urgent, C=secondary. Single agenda for everything. 2-minute rule. Reminder systems. Evening preparation routine.",
    "psycho.tdah.s6t": "6. Managing distractibility",
    "psycho.tdah.s6c": "Environmental strategies (phone away, clear desk), time strategies (know your attention capacity, work in short blocks), cognitive strategies (idea parking lot, 'just 5 minutes' technique).",
    "psycho.tdah.s7t": "7. Cognitive restructuring in ADHD",
    "psycho.tdah.s7c": "Typical ADHD thoughts: 'I'm lazy' (reality: dopamine deficit), 'Everyone manages except me' (overgeneralization). Alternative thoughts must acknowledge ADHD as an explanatory factor while preserving agency.",
    "psycho.tdah.s8t": "8. Procrastination and relapse prevention",
    "psycho.tdah.s8c": "ADHD procrastination is linked to dopamine deficit. Techniques: micro-steps, first step commitment, immediate rewards, body doubling. ADHD is chronic — expect and adjust for relapses.",
    "psycho.tdah.q1q": "According to Safren's model, what is the third level?",
    "psycho.tdah.q1a": "Organizational difficulties",
    "psycho.tdah.q1b": "Neurobiological deficits",
    "psycho.tdah.q1c": "Psychological consequences (negative thoughts, anxiety, depression)",
    "psycho.tdah.q1d": "Sleep disorders",
    "psycho.tdah.q1e": "Level 3 is psychological consequences: repeated failures generate negative automatic thoughts fueling anxiety and depression.",
    "psycho.tdah.q2q": "In the ABC system, what does an A task represent?",
    "psycho.tdah.q2a": "A secondary task",
    "psycho.tdah.q2b": "An urgent AND important task to do today",
    "psycho.tdah.q2c": "An important but not urgent task",
    "psycho.tdah.q2d": "A task that can be delegated",
    "psycho.tdah.q2e": "A = urgent + important (today), B = important but not urgent (this week), C = secondary.",
    "psycho.tdah.q3q": "Why is exercise particularly beneficial for ADHD?",
    "psycho.tdah.q3a": "It increases dopamine and norepinephrine, same neurotransmitters as ADHD medications",
    "psycho.tdah.q3b": "It tires the body and reduces hyperactivity",
    "psycho.tdah.q3c": "It only improves sleep",
    "psycho.tdah.q3d": "It has no proven effect on ADHD",
    "psycho.tdah.q3e": "Exercise increases dopamine and norepinephrine. 30 minutes has effects comparable to a low dose of stimulant.",
    "psycho.tdah.q4q": "What's different about ADHD procrastination?",
    "psycho.tdah.q4a": "There is no difference",
    "psycho.tdah.q4b": "ADHD procrastination is a conscious choice",
    "psycho.tdah.q4c": "ADHD procrastination is only stress-related",
    "psycho.tdah.q4d": "It's linked to dopamine deficit and difficulty initiating non-stimulating tasks",
    "psycho.tdah.q4e": "ADHD procrastination is neurobiological: not enough dopamine for non-stimulating tasks, causing 'start-up paralysis'.",

    "nav.tdahAdulte": "Adult ADHD",
    "tdah.priorities.title": "ABC Priorities",
    "tdah.priorities.desc": "A/B/C task prioritization system",
    "tdah.documents.title": "Document Management",
    "tdah.documents.desc": "Single management system for mail and bills",
    "tdah.attention.title": "Attention Capacity",
    "tdah.attention.desc": "Assess and train your attention capacity",
    "tdah.thoughts.title": "ADHD Automatic Thoughts",
    "tdah.thoughts.desc": "Recording and cognitive restructuring",
    "tdah.procrastination.title": "Anti-Procrastination",
    "tdah.procrastination.desc": "Breaking tasks into micro-steps",
    "tdah.goals.title": "Goals & Motivation",
    "tdah.goals.desc": "Set realistic goals and maintain motivation",

    // TDAH Kids Workbook
    "exercise.tdahKids.superpowers.title": "Cosmic Super-Powers",
    "exercise.tdahKids.superpowers.desc": "Discover your secret strengths",
    "exercise.tdahKids.safetyBubble.title": "Space Bubble",
    "exercise.tdahKids.safetyBubble.desc": "Create your personal calm space",
    "exercise.tdahKids.relaxation.title": "Return to Orbit",
    "exercise.tdahKids.relaxation.desc": "Muscle relaxation",
    "exercise.tdahKids.radar.title": "Galactic Radar",
    "exercise.tdahKids.radar.desc": "Sensations journal",
    "exercise.tdahKids.recharge.title": "Recharge Station",
    "exercise.tdahKids.recharge.desc": "Calm and restoring moments",
    "exercise.tdahKids.turbo.title": "Turbo Ship",
    "exercise.tdahKids.turbo.desc": "Understand your turbo brain",
    "exercise.tdahKids.mission.title": "Space Mission",
    "exercise.tdahKids.mission.desc": "Self-control challenges",
    "exercise.tdahKids.superheroDay.title": "Astronaut Day",
    "exercise.tdahKids.superheroDay.desc": "Daily energy tracking",
    "exercise.tdahKids.victories.title": "Victory Stars",
    "exercise.tdahKids.victories.desc": "Celebrate your achievements",
    "exercise.tdahKids.questions.title": "Mission Control Message",
    "exercise.tdahKids.questions.desc": "Questions for your therapist",
    "exercise.parentTaskBoard.title": "Mission Board",
    "exercise.parentTaskBoard.desc": "Daily missions with badges to earn",
    "exercise.morningChecklist.title": "Morning Checklist",
    "exercise.morningChecklist.desc": "Task list for your morning routine",
    "psychoed.tdahChild.title": "Understanding my ADHD",
    "psychoed.tdahChild.desc": "Fun module to understand ADHD",
    "psychoed.limitsChild.title": "My Secret Garden - Limits",
    "psychoed.limitsChild.desc": "Learn about your limits in a fun way",
    "psychoed.limitsAdult.title": "Communicating Limits",
    "psychoed.limitsAdult.desc": "Assertive techniques to defend your limits",
    "psychoed.schemaTherapy.title": "Schema Therapy",
    "psychoed.tcd.title": "DBT (Linehan)",
    "psychoed.dissociationTrauma.title": "Dissociation & Trauma",
    "psychoed.toc.title": "Understanding OCD",
    "psychoed.toc.subtitle": "Obsessive-Compulsive Disorder: understanding and taking action",
    "psychoed.borderline.title": "Understanding Borderline Personality Disorder",
    "psychoed.borderline.subtitle": "Borderline Personality Disorder: understanding for better living",
    "psychoed.erectileDysfunction.title": "Erectile dysfunction and performance anxiety",
    "psychoed.erectileDysfunction.subtitle": "Understanding performance anxiety and practicing sensate focus",
    "exercices.schemaTherapy.title": "Schema Exercises",
    "exercices.tcd.title": "DBT Exercises",
    "exercices.dissociationTrauma.title": "Dissociation Exercises",
    "nav.exercicesAvances": "Advanced Exercises",

    // Schema Exercises Page
    "schemaEx.pageTitle": "Exercises: Schema Therapy",
    "schemaEx.pageSubtitle": "Practical exercises to identify and transform your schemas",
    "schemaEx.journal.name": "Schema Journal",
    "schemaEx.journal.desc": "Identify your schemas daily by spotting situations that trigger intense emotional reactions.",
    "schemaEx.journal.situation": "Triggering situation",
    "schemaEx.journal.situationPh": "Describe the situation (who, what, when, where)",
    "schemaEx.journal.emotion": "Emotion felt and intensity (0-10)",
    "schemaEx.journal.emotionPh": "E.g.: Sadness (8/10), anger (5/10)",
    "schemaEx.journal.thoughts": "Automatic thoughts",
    "schemaEx.journal.thoughtsPh": "What thoughts arose? E.g.: 'Nobody cares about me'",
    "schemaEx.journal.schema": "Schema probably activated",
    "schemaEx.journal.schemaPh": "E.g.: Abandonment, Defectiveness, Failure, Emotional deprivation...",
    "schemaEx.journal.reaction": "Behavioral reaction",
    "schemaEx.journal.reactionPh": "What did you do? (surrender, avoidance or overcompensation)",
    "schemaEx.memo.name": "Flash memo card",
    "schemaEx.memo.desc": "Create a portable card to counter your schema when it activates.",
    "schemaEx.memo.voice": "What the schema says",
    "schemaEx.memo.voicePh": "E.g.: 'Nobody really loves me'",
    "schemaEx.memo.why": "Why it's a schema, not reality",
    "schemaEx.memo.whyPh": "List the evidence against this belief",
    "schemaEx.memo.realistic": "What is more realistic",
    "schemaEx.memo.realisticPh": "E.g.: 'Some people genuinely love me'",
    "schemaEx.memo.action": "What I can do now",
    "schemaEx.memo.actionPh": "A healthy and concrete behavior",
    "schemaEx.dialogue.name": "Inner dialogue",
    "schemaEx.dialogue.desc": "Have your schema side and healthy side dialogue to challenge automatic beliefs.",
    "schemaEx.dialogue.schemaSide": "Schema side (what it says)",
    "schemaEx.dialogue.schemaSidePh": "Let the schema express itself fully...",
    "schemaEx.dialogue.healthySide": "Healthy side (Healthy Adult response)",
    "schemaEx.dialogue.healthySidePh": "Respond with empathy and firmness...",
    "schemaEx.dialogue.schemaReply": "Schema (reply)",
    "schemaEx.dialogue.schemaReplyPh": "What does the schema reply?",
    "schemaEx.dialogue.healthyFinal": "Healthy side (final word)",
    "schemaEx.dialogue.healthyFinalPh": "Conclude with a balanced perspective",
    "schemaEx.letter.name": "Letter to the Vulnerable Child",
    "schemaEx.letter.desc": "Write to your inner child as an act of inner reparenting.",
    "schemaEx.letter.letter": "Your letter to the inner child",
    "schemaEx.letter.letterPh": "Dear little [name]...\nAcknowledge their suffering, validate their emotions, reassure them...",
    "schemaEx.letter.response": "Child's response (optional)",
    "schemaEx.letter.responsePh": "What would the child reply?",
    "schemaEx.imagery.name": "Imagery and reparenting",
    "schemaEx.imagery.desc": "Access a childhood memory and transform it by bringing a reparative experience.",
    "schemaEx.imagery.scene": "Childhood scene (describe the image)",
    "schemaEx.imagery.scenePh": "The place, the people, what you feel in your child body...",
    "schemaEx.imagery.intervention": "Healthy Adult intervention",
    "schemaEx.imagery.interventionPh": "How does your adult self intervene to protect the child?",
    "schemaEx.imagery.transformed": "Transformed scene",
    "schemaEx.imagery.transformedPh": "How does the scene change when the child's need is met?",
    "schemaEx.imagery.feeling": "Feeling after the exercise",
    "schemaEx.imagery.feelingPh": "What did you feel when you opened your eyes?",

    // TCD Exercises Page
    "tcdEx.pageTitle": "Exercises: DBT (Dialectical Behavior Therapy)",
    "tcdEx.pageSubtitle": "Emotional regulation and distress tolerance skills",
    "tcdEx.chain.name": "Chain analysis",
    "tcdEx.chain.desc": "Break down the complete sequence between a trigger and a problematic behavior.",
    "tcdEx.chain.behavior": "Problematic behavior",
    "tcdEx.chain.behaviorPh": "Describe precisely the behavior to analyze",
    "tcdEx.chain.trigger": "Triggering event",
    "tcdEx.chain.triggerPh": "Who, what, when, where?",
    "tcdEx.chain.vulnerability": "Vulnerability factors",
    "tcdEx.chain.vulnerabilityPh": "Lack of sleep, hunger, stress, illness, conflict...",
    "tcdEx.chain.chain": "Chain links (thoughts, emotions, sensations, actions)",
    "tcdEx.chain.chainPh": "Detail each step between the trigger and the behavior...",
    "tcdEx.chain.consequences": "Consequences (short-term and long-term)",
    "tcdEx.chain.consequencesPh": "Short-term: relief? Long-term: worsening?",
    "tcdEx.chain.skills": "Alternative DBT skills",
    "tcdEx.chain.skillsPh": "At which links could you have used a skill? Which one?",
    "tcdEx.stop.name": "STOP and TIP",
    "tcdEx.stop.desc": "Crisis techniques to get through intense pain without making the situation worse.",
    "tcdEx.stop.crisis": "Crisis situation",
    "tcdEx.stop.crisisPh": "Describe the situation and emotional intensity (0-10)",
    "tcdEx.stop.steps": "Applying STOP",
    "tcdEx.stop.stepsPh": "S (Stop) — T (Take a step back) — O (Observe) — P (Proceed mindfully)",
    "tcdEx.stop.tip": "TIP technique used",
    "tcdEx.stop.tipPh": "Temperature (cold water), Intense exercise, Paced breathing, Progressive muscle relaxation...",
    "tcdEx.stop.outcome": "Outcome",
    "tcdEx.stop.outcomePh": "How do you feel after? Did the intensity decrease?",
    "tcdEx.acceptance.name": "Radical acceptance and half-smile",
    "tcdEx.acceptance.desc": "Practice complete acceptance of reality and associated body techniques.",
    "tcdEx.acceptance.situation": "Situation to accept",
    "tcdEx.acceptance.situationPh": "What reality are you struggling to accept?",
    "tcdEx.acceptance.signs": "Signs of non-acceptance",
    "tcdEx.acceptance.signsPh": "Bitterness, rumination, body tension, desire to deny...",
    "tcdEx.acceptance.steps": "Acceptance steps practiced",
    "tcdEx.acceptance.stepsPh": "Observe the struggle, acknowledge reality, identify causes, accept with the body...",
    "tcdEx.acceptance.body": "Body practice",
    "tcdEx.acceptance.bodyPh": "Half-smile, willing hands — describe your experience",
    "tcdEx.acceptance.result": "Effect felt",
    "tcdEx.acceptance.resultPh": "How do you feel after the practice?",
    "tcdEx.facts.name": "Check the facts and opposite action",
    "tcdEx.facts.desc": "Evaluate if your emotion matches the facts and, if not, act opposite.",
    "tcdEx.facts.emotion": "Emotion felt and intensity",
    "tcdEx.facts.emotionPh": "E.g.: Anger (8/10)",
    "tcdEx.facts.triggerFacts": "Objective facts of the situation",
    "tcdEx.facts.triggerFactsPh": "What actually happened? (without interpretation)",
    "tcdEx.facts.interpretations": "My interpretations",
    "tcdEx.facts.interpretationsPh": "What assumptions did I make? Are they based on facts?",
    "tcdEx.facts.matchesFacts": "Does the emotion match the facts?",
    "tcdEx.facts.matchesFactsPh": "Yes (problem solving) or No (opposite action)",
    "tcdEx.facts.oppositeAction": "Opposite action taken",
    "tcdEx.facts.oppositeActionPh": "What did you do opposite to the urge? GOING ALL THE WAY",
    "tcdEx.dear.name": "DEAR MAN",
    "tcdEx.dear.desc": "Effectiveness guidelines for the objective: getting what you want.",
    "tcdEx.dear.context": "Situation and objective",
    "tcdEx.dear.contextPh": "Describe the situation and what you want to achieve",
    "tcdEx.dear.describe": "D - Describe (the facts)",
    "tcdEx.dear.describePh": "E.g.: 'You told me you would be home for dinner but you arrived at 11pm.'",
    "tcdEx.dear.describeHint": "Describe the current SITUATION (if necessary). Stick to the facts. Tell the person exactly what you are reacting to.",
    "tcdEx.dear.express": "E - Express (feelings and opinions)",
    "tcdEx.dear.expressPh": "E.g.: 'When you come home so late, I worry about you.'",
    "tcdEx.dear.expressHint": "Express your FEELINGS and OPINIONS about the situation. Don't assume the person knows how you feel. Use 'I want' rather than 'you should'.",
    "tcdEx.dear.assert": "A - Assert (clear request or refusal)",
    "tcdEx.dear.assertPh": "E.g.: 'I would really like you to call me if you're going to be late.'",
    "tcdEx.dear.assertHint": "Assert yourself by ASKING or REFUSING clearly. Don't assume others will guess. Remember that others can't read your mind.",
    "tcdEx.dear.reinforce": "R - Reinforce (positive consequences)",
    "tcdEx.dear.reinforcePh": "E.g.: 'I would be so relieved and much easier to live with if you did.'",
    "tcdEx.dear.reinforceHint": "Reinforce (reward) the person in advance by presenting the positive effects of getting what you want. If necessary, specify the negative consequences. Remember to reward the desired behavior after it happens.",
    "tcdEx.dear.mindful": "M - Stay Mindful",
    "tcdEx.dear.mindfulPh": "How did you stay on track? Did you use the 'broken record' technique?",
    "tcdEx.dear.mindfulHint": "Stay focused ON YOUR OBJECTIVES. Maintain your position. Don't get distracted. Don't go off topic. Broken record: Keep asking, refusing, or expressing your opinion, over and over. Ignore attacks and diversions.",
    "tcdEx.dear.confident": "A - Appear Confident",
    "tcdEx.dear.confidentPh": "How did you show confidence? (tone, eye contact, posture)",
    "tcdEx.dear.confidentHint": "Appear competent and EFFECTIVE. Use a confident tone and attitude; establish adequate eye contact. Don't stammer, don't whisper, don't look at the floor, don't back away. Don't say 'I'm not sure'.",
    "tcdEx.dear.negotiate": "N - Negotiate (alternatives)",
    "tcdEx.dear.negotiatePh": "E.g.: 'What if you texted me when you think you'll be late?'",
    "tcdEx.dear.negotiateHint": "Be willing to GIVE TO GET. Propose and ask for other solutions. Reduce your request. Say no, but propose an alternative to solve the problem differently.",
    "tcdEx.dear.debrief": "Debriefing after the interaction (optional)",
    "tcdEx.dear.debriefPh": "What worked well? What to improve?",

    // GIVE Exercise
    "tcdEx.give.name": "GIVE",
    "tcdEx.give.desc": "Effectiveness guidelines for the relationship: maintaining the relationship.",
    "tcdEx.give.context": "Relational situation",
    "tcdEx.give.contextPh": "Describe the situation and the relationship you want to preserve",
    "tcdEx.give.gentle": "G - Be Gentle",
    "tcdEx.give.gentlePh": "How were you pleasant and respectful? What did you avoid?",
    "tcdEx.give.gentleHint": "BE PLEASANT and respectful. No attacks: don't attack verbally or physically. No threats: describe consequences calmly and without exaggeration. No judgments: no lectures, no 'you should'. No disrespect: don't smirk, don't roll your eyes.",
    "tcdEx.give.interest": "I - Show Interest",
    "tcdEx.give.interestPh": "How did you show interest in the other person?",
    "tcdEx.give.interestHint": "LISTEN and SHOW INTEREST in the other person and their point of view. Face the person, maintain eye contact. Don't interrupt and don't speak for the other. Be patient.",
    "tcdEx.give.validate": "V - Validate",
    "tcdEx.give.validatePh": "How did you show understanding of the other's feelings and thoughts?",
    "tcdEx.give.validateHint": "Through your WORDS and ACTIONS, show that you understand the person's feelings and thoughts about the situation. Put yourself in their shoes. 'I realize this is hard for you', 'I see you're busy'.",
    "tcdEx.give.easy": "E - Use an Easy manner",
    "tcdEx.give.easyPh": "How did you use humor or put the person at ease?",
    "tcdEx.give.easyHint": "Use humor. SMILE. Put the person at ease. Don't take yourself too seriously. Prefer the soft approach over the hard approach. Be diplomatic.",
    "tcdEx.give.debrief": "Debriefing (optional)",
    "tcdEx.give.debriefPh": "How was the relationship impacted? What did you learn?",

    // FAST Exercise
    "tcdEx.fast.name": "FAST",
    "tcdEx.fast.desc": "Effectiveness guidelines for self-respect: maintaining self-respect.",
    "tcdEx.fast.context": "Situation at stake",
    "tcdEx.fast.contextPh": "Describe the situation where your self-respect is at stake",
    "tcdEx.fast.fair": "F - Be Fair",
    "tcdEx.fast.fairPh": "How were you honest with yourself and the other person?",
    "tcdEx.fast.fairHint": "Be HONEST with YOU and with THE OTHER person. Remember to VALIDATE YOUR OWN feelings and desires, as well as those of the other person.",
    "tcdEx.fast.apologies": "A - No unnecessary Apologies",
    "tcdEx.fast.apologiesPh": "Did you over-apologize? What should you not apologize for?",
    "tcdEx.fast.apologiesHint": "Don't apologize excessively. Don't apologize for being alive or making a request. Don't apologize for having an opinion or disagreeing. Don't LOOK ASHAMED. Don't invalidate what is valid.",
    "tcdEx.fast.values": "S - Stick to your values",
    "tcdEx.fast.valuesPh": "What values did you stand up for in this situation?",
    "tcdEx.fast.valuesHint": "Stay true to YOUR VALUES. Don't sacrifice your values or integrity for reasons that are not VERY important. Be clear about your morality and defend your position.",
    "tcdEx.fast.truthful": "T - Be Truthful",
    "tcdEx.fast.truthfulPh": "How were you honest without exaggerating?",
    "tcdEx.fast.truthfulHint": "Don't lie. Don't present yourself as helpless when you're not. Don't exaggerate and don't make up excuses.",
    "tcdEx.fast.debrief": "Debriefing (optional)",
    "tcdEx.fast.debriefPh": "How did you feel afterwards? Is your self-respect preserved?",

    // Dissociation Exercises Page
    "dissocEx.pageTitle": "Exercises: Dissociation & Trauma",
    "dissocEx.pageSubtitle": "Grounding, stabilization and parts work techniques",
    "dissocEx.grounding.name": "Grounding and return to present",
    "dissocEx.grounding.desc": "5-4-3-2-1 technique to reconnect with the present moment during dissociation.",
    "dissocEx.grounding.see5": "5 things I see (in detail)",
    "dissocEx.grounding.see5Ph": "Describe 5 visible things with details (color, shape, size...)",
    "dissocEx.grounding.hear4": "4 things I hear",
    "dissocEx.grounding.hear4Ph": "Describe 4 sounds you perceive right now",
    "dissocEx.grounding.touch3": "3 things I touch",
    "dissocEx.grounding.touch3Ph": "Describe 3 tactile sensations (texture, temperature...)",
    "dissocEx.grounding.smell2": "2 things I smell",
    "dissocEx.grounding.smell2Ph": "Describe 2 perceived smells",
    "dissocEx.grounding.taste1": "1 thing I taste",
    "dissocEx.grounding.taste1Ph": "Describe 1 taste",
    "dissocEx.grounding.orientation": "Time orientation",
    "dissocEx.grounding.orientationPh": "My name, the date, the time, the place, and: 'Right now, I am safe'",
    "dissocEx.grounding.effectiveness": "Effectiveness (0-10)",
    "dissocEx.grounding.effectivenessPh": "How effective was the grounding? Did sensations return?",
    "dissocEx.safe.name": "Inner safe place",
    "dissocEx.safe.desc": "Create an inner refuge of safety and calm, activable in times of distress.",
    "dissocEx.safe.visual": "What I see in my safe place",
    "dissocEx.safe.visualPh": "Colors, light, shapes, space...",
    "dissocEx.safe.sounds": "What I hear",
    "dissocEx.safe.soundsPh": "Nature sounds, music, silence...",
    "dissocEx.safe.touch": "What I touch and feel in my body",
    "dissocEx.safe.touchPh": "Textures, temperature, warmth, lightness, relaxation...",
    "dissocEx.safe.smells": "What I smell (odors)",
    "dissocEx.safe.smellsPh": "Smells associated with this place",
    "dissocEx.safe.keyword": "Keyword or associated gesture",
    "dissocEx.safe.keywordPh": "A word or gesture that activates the feeling of safety",
    "dissocEx.safe.body": "Body sensation of safety",
    "dissocEx.safe.bodyPh": "Where in your body do you feel safety?",
    "dissocEx.parts.name": "Working with parts (IFS)",
    "dissocEx.parts.desc": "Identify and communicate with a protective or wounded part of your psyche.",
    "dissocEx.parts.identified": "Identified part",
    "dissocEx.parts.identifiedPh": "Describe the part (e.g.: 'the numbing part', 'the inner critic')",
    "dissocEx.parts.role": "Its protective role",
    "dissocEx.parts.rolePh": "What is this part trying to protect me from?",
    "dissocEx.parts.communication": "Communication with the part",
    "dissocEx.parts.communicationPh": "What does it say? What is it afraid of? What does it need?",
    "dissocEx.parts.reassurance": "Reassurance from the Self",
    "dissocEx.parts.reassurancePh": "What your Self (calm essence) says to this part...",
    "dissocEx.parts.newRole": "New proposed role (optional)",
    "dissocEx.parts.newRolePh": "Would it accept a different, less extreme role?",
    "dissocEx.window.name": "Window of tolerance",
    "dissocEx.window.desc": "Evaluate your position in the window of tolerance and practice regulation.",
    "dissocEx.window.state": "Current position",
    "dissocEx.window.statePh": "Hyperarousal (anxiety, panic, anger) / Within the window (present, balanced) / Hypoarousal (numbness, emptiness)",
    "dissocEx.window.signs": "Observed body signs",
    "dissocEx.window.signsPh": "Racing heart, tense muscles, racing thoughts OR numbness, fatigue, disconnection...",
    "dissocEx.window.trigger": "What took me out of the window",
    "dissocEx.window.triggerPh": "Event, thought, memory, interaction...",
    "dissocEx.window.strategy": "Strategy used to return to the window",
    "dissocEx.window.strategyPh": "Hyperarousal: breathing, grounding, safe place / Hypoarousal: movement, sensory stimulation, walking",
    "dissocEx.window.result": "Result",
    "dissocEx.window.resultPh": "Did I return to the window? How long did it take?",

    // Shared exercise form labels
    "exerciseForm.save": "Save",
    "exerciseForm.back": "Back",
    "exerciseForm.history": "History",
    "exerciseForm.sessions": "session(s)",
    "exerciseForm.fillOne": "Please fill in at least one field",
    "exerciseForm.saved": "Exercise saved",
    "exerciseForm.saveError": "Error saving exercise",

    // Psychoeducation Module UI
    "psychoed.notFound": "Module not found.",
    "psychoed.back": "Back",
    "psychoed.progress": "Progress",
    "psychoed.chapters": "chapters",
    "psychoed.chapter": "Chapter",
    "psychoed.quizChild": "Quiz!",
    "psychoed.quizAdult": "Reflection question",
    "psychoed.correctChild": "Well done! ",
    "psychoed.correctAdult": "Correct! ",
    "psychoed.wrongChild": "Not quite! ",
    "psychoed.wrongAdult": "Not exactly. ",
    "psychoed.prev": "Previous",
    "psychoed.next": "Next",
    "psychoed.saving": "Saving...",
    "psychoed.complete": "Complete the module!",
    "psychoed.completed": "Module completed! Bravo!",
    "psychoed.saveError": "Error saving",
    "psychoed.historyTitle": "History",

    // Auth extras
    "auth.autoLogout": "You have been logged out for security after a period of inactivity.",
    "auth.quote": "The greatest glory is not in never falling, but in rising every time we fall.",

    // Profile extras
    "profile.manageSubscription": "Manage subscription",

    // Pricing
    "pricing.chooseAPlan": "Choose the plan that fits your needs",
    "pricing.coveredByTherapist": "Covered by your therapist",
    "pricing.recommended": "Recommended",
    "pricing.alreadySubscribed": "Already subscribed",
    "pricing.patientNote": "Patients treated by a therapist with an active subscription benefit from free access.",

    // Subscription extras
    "subscription.verifying": "Verifying payment...",
    "subscription.success": "Payment successful!",
    "subscription.successMessage": "Your subscription is now active. Enjoy all features.",
    "subscription.failedMessage": "Payment was not successful. Please try again.",
    "subscription.retry": "Try again",

    // Trial banner
    "trialBanner.trialExpired": "Trial expired",
    "trialBanner.trialExpiredMessage": "Your free trial has expired",
    "trialBanner.subscribePrompt": "Subscribe to regain access to all features.",
    "trialBanner.dontLoseAccess": "Switch to a subscription so you don't lose access.",

    // Consent
    "consent.dataProtection": "Data Protection",
    "consent.message": "By using Kintsugi CBT, you accept our privacy policy and the processing of your health data in accordance with GDPR.",

    // Patient exercises extras
    "patientExercises.thoughts": "Thoughts:",
    "patientExercises.balancedThought": "Balanced thought:",
    "patientExercises.problemLabel": "Problem:",
    "patientExercises.difficultyLabel": "Difficulty:",
    "patientExercises.rewardLabel": "Reward:",
    "patientExercises.assignedCount": "{count} assigned exercise(s)",
    "patientExercises.standaloneCount": "{count} standalone entry(ies)",
    "patientExercises.generating": "Generating...",
    "patientExercises.moreEntries": "... and {count} more entry(ies)",

    // Meditation instructions
    "meditation.breathing.inst1": "Sit comfortably with your back straight",
    "meditation.breathing.inst2": "Gently close your eyes",
    "meditation.breathing.inst3": "Slowly breathe in through your nose, counting to 4",
    "meditation.breathing.inst4": "Hold your breath for 4 seconds",
    "meditation.breathing.inst5": "Slowly exhale through your mouth, counting to 6",
    "meditation.breathing.inst6": "Repeat this cycle for 5 minutes",
    "meditation.bodyScan.inst1": "Lie down comfortably",
    "meditation.bodyScan.inst2": "Close your eyes and breathe deeply",
    "meditation.bodyScan.inst3": "Bring attention to your feet, notice the sensations",
    "meditation.bodyScan.inst4": "Gradually move up: ankles, calves, knees...",
    "meditation.bodyScan.inst5": "Continue to the top of your head",
    "meditation.bodyScan.inst6": "Finish by becoming aware of your entire body",
    "meditation.gratitudeMed.inst1": "Make yourself comfortable",
    "meditation.gratitudeMed.inst2": "Take three deep breaths",
    "meditation.gratitudeMed.inst3": "Think of three things you are grateful for",
    "meditation.gratitudeMed.inst4": "Feel the gratitude in your heart",
    "meditation.gratitudeMed.inst5": "Visualize these positive moments",
    "meditation.gratitudeMed.inst6": "End with an inner smile",
    "meditation.innerCalm.inst1": "Find a quiet place",
    "meditation.innerCalm.inst2": "Sit in a comfortable position",
    "meditation.innerCalm.inst3": "Focus on your natural breathing",
    "meditation.innerCalm.inst4": "Let your thoughts pass like clouds",
    "meditation.innerCalm.inst5": "Don't judge, just observe",
    "meditation.innerCalm.inst6": "Gently return to your breathing if you get lost",
    "meditation.sleepPrep.inst1": "Lie down in your bed",
    "meditation.sleepPrep.inst2": "Close your eyes and relax your jaw",
    "meditation.sleepPrep.inst3": "Release all tension from your shoulders",
    "meditation.sleepPrep.inst4": "Imagine a soft light surrounding you",
    "meditation.sleepPrep.inst5": "Each breath relaxes you more",
    "meditation.sleepPrep.inst6": "Let yourself drift into sleep",

    // Defusion instructions
    "defusion.leaves.inst1": "Close your eyes and imagine a peaceful stream",
    "defusion.leaves.inst2": "See leaves gently floating on the water",
    "defusion.leaves.inst3": "When a thought arrives, place it on a leaf",
    "defusion.leaves.inst4": "Watch the leaf slowly drift away",
    "defusion.leaves.inst5": "Don't hold back the leaves, let them go",
    "defusion.leaves.inst6": "Continue for 5-10 minutes",
    "defusion.silly.inst1": "Identify a thought that bothers you",
    "defusion.silly.inst2": "Repeat it out loud in a cartoon voice",
    "defusion.silly.inst3": "Try different voices: robot, singing, very slow...",
    "defusion.silly.inst4": "Notice how it changes your relationship with the thought",
    "defusion.silly.inst5": "The thought is still there, but it has less power",
    "defusion.prefix.inst1": "Take a negative thought, for example 'I'm useless'",
    "defusion.prefix.inst2": "Add the prefix: 'I have the thought that I'm useless'",
    "defusion.prefix.inst3": "Then: 'I notice that I have the thought that I'm useless'",
    "defusion.prefix.inst4": "Notice how this creates distance",
    "defusion.prefix.inst5": "You are not your thoughts, you are observing them",
    "defusion.clouds.inst1": "Imagine your mind as an infinite blue sky",
    "defusion.clouds.inst2": "Thoughts are clouds crossing this sky",
    "defusion.clouds.inst3": "Some clouds are dark, others are light",
    "defusion.clouds.inst4": "You are the sky, not the clouds",
    "defusion.clouds.inst5": "The clouds pass, the sky is always there",
    "defusion.thankMind.inst1": "When a negative thought arises",
    "defusion.thankMind.inst2": "Say internally: 'Thank you, mind, for this thought'",
    "defusion.thankMind.inst3": "Acknowledge that your mind is trying to protect you",
    "defusion.thankMind.inst4": "Then gently bring your attention back to the present",
    "defusion.thankMind.inst5": "This technique reduces the struggle against thoughts",

    // Self-compassion content
    "compassion.letter.prompt1": "Describe a difficult situation you are experiencing",
    "compassion.letter.prompt2": "What would you say to a dear friend in this situation?",
    "compassion.letter.prompt3": "Write yourself those same words of comfort",
    "compassion.touch.step1": "Place a hand on your heart",
    "compassion.touch.step2": "Feel the warmth of your hand",
    "compassion.touch.step3": "Breathe deeply",
    "compassion.touch.step4": "Say to yourself: 'I'm going through a difficult time'",
    "compassion.touch.step5": "Then: 'I deserve compassion'",
    "compassion.touch.step6": "Stay like this for a few minutes",
    "compassion.phrase1": "May I be safe",
    "compassion.phrase2": "May I be happy",
    "compassion.phrase3": "May I be healthy",
    "compassion.phrase4": "May I live with ease",
    "compassion.phrase5": "I deserve love and compassion",
    "compassion.phrase6": "I accept myself as I am",

    // Assertiveness content
    "assertiveness.passive.description": "Avoiding conflicts, not expressing your needs",
    "assertiveness.passive.examples": "I don't say anything,It's not a big deal,Whatever you want",
    "assertiveness.passive.consequence": "Frustration, resentment, lack of self-respect",
    "assertiveness.aggressive.description": "Imposing your needs without regard for others",
    "assertiveness.aggressive.examples": "You have to do this!,It's always the same with you,You never understand anything",
    "assertiveness.aggressive.consequence": "Conflicts, isolation, guilt",
    "assertiveness.passiveAggressive.description": "Indirectly expressing hostility",
    "assertiveness.passiveAggressive.examples": "Sure, as usual...,No no, everything is fine (sigh),Do whatever you want",
    "assertiveness.passiveAggressive.consequence": "Misunderstandings, tensions, superficial relationships",
    "assertiveness.assertive.description": "Expressing your needs while respecting others",
    "assertiveness.assertive.examples": "I feel... when you...,I would need...,I understand your point of view, and I think...",
    "assertiveness.assertive.consequence": "Healthy relationships, mutual respect, self-confidence",
    "assertiveness.examplesLabel": "Examples:",
    "assertiveness.descPlaceholderD": "When you interrupted me during the meeting...",
    "assertiveness.descPlaceholderE": "I felt frustrated and not heard...",
    "assertiveness.descPlaceholderS": "I would like to finish my sentences before you respond...",
    "assertiveness.descPlaceholderC": "This would allow us to have more constructive exchanges...",

    // Maslow needs content
    "needs.physiological.description": "Food, water, sleep, health, shelter",
    "needs.physiological.q1": "Do you get enough sleep?",
    "needs.physiological.q2": "Do you eat a balanced diet?",
    "needs.physiological.q3": "Do you have stable, comfortable housing?",
    "needs.physiological.q4": "Is your physical health satisfactory?",
    "needs.safety.description": "Financial security, employment, health, property",
    "needs.safety.q1": "Do you feel financially secure?",
    "needs.safety.q2": "Is your employment stable?",
    "needs.safety.q3": "Do you feel physically safe?",
    "needs.safety.q4": "Do you have a sense of stability in your life?",
    "needs.belonging.description": "Love, friendship, intimacy, family",
    "needs.belonging.q1": "Do you have satisfying friendships?",
    "needs.belonging.q2": "Do you feel loved and supported?",
    "needs.belonging.q3": "Are you part of a group or community?",
    "needs.belonging.q4": "Are your intimate relationships fulfilling?",
    "needs.esteem.description": "Respect, recognition, self-confidence",
    "needs.esteem.q1": "Do you feel respected by others?",
    "needs.esteem.q2": "Are you proud of your accomplishments?",
    "needs.esteem.q3": "Do you have confidence in your abilities?",
    "needs.esteem.q4": "Do you feel valued in your work?",
    "needs.selfActualization.description": "Creativity, fulfillment, realizing your potential",
    "needs.selfActualization.q1": "Do you pursue your passions and interests?",
    "needs.selfActualization.q2": "Do you feel you are achieving your potential?",
    "needs.selfActualization.q3": "Do you fully use your talents?",
    "needs.selfActualization.q4": "Do you contribute to something greater than yourself?",

    // Maslow pyramid examples
    "needs.physiological.examples": "Restful sleep / Rest / Calm / Less stimulation",
    "needs.safety.examples": "Predictability / Stability / Financial security / Material",
    "needs.belonging.examples": "Recognition / Validation / Affection / Emotional support",
    "needs.esteem.examples": "Self-esteem / Stimulation / Novelty / Meaning / Understanding",
    "needs.selfActualization.examples": "Being heard / Understood / Choice / Decision / Time for myself",

    // Pyramid card
    "needs.pyramidTitle": "Maslow's Pyramid of Needs",
    "needs.pyramidHint": "Click a level to explore its needs",
    "needs.evaluateSection": "Rate the satisfaction of each dimension (1 to 10)",

    // Specific needs table
    "needs.tableTitle": "Table: Exploration of fundamental needs",
    "needs.tableHint": "Add your specific needs in this category. You can add as many as needed.",
    "needs.col.specific": "Specific need",
    "needs.col.specific.placeholder": "e.g., Sleep 8h per night",
    "needs.col.nourished": "Is this need met?",
    "needs.col.response": "How could I better respond to it?",
    "needs.col.response.placeholder": "Concrete actions…",
    "needs.col.priority": "Priority",
    "needs.col.deadline": "Deadline",
    "needs.nourished.yes": "Yes",
    "needs.nourished.no": "No",
    "needs.nourished.partial": "A little",
    "needs.priority.high": "High",
    "needs.priority.medium": "Medium",
    "needs.priority.low": "Low",
    "needs.addRow": "Add a need",

    // Value domains
    "values.domain.family": "Family",
    "values.domain.family.desc": "Family relationships, parenting, being a good parent/child/sibling",
    "values.domain.relationships": "Intimate Relationships",
    "values.domain.relationships.desc": "Romantic relationships, intimacy, emotional connection",
    "values.domain.friends": "Friendship",
    "values.domain.friends.desc": "Friendships, social relationships, belonging to a group",
    "values.domain.work": "Work/Career",
    "values.domain.work.desc": "Professional achievement, contribution, development",
    "values.domain.education": "Education/Learning",
    "values.domain.education.desc": "Curiosity, personal development, knowledge acquisition",
    "values.domain.leisure": "Leisure",
    "values.domain.leisure.desc": "Recreational activities, hobbies, pleasures",
    "values.domain.spirituality": "Spirituality",
    "values.domain.spirituality.desc": "Spiritual connection, meaning of life, transcendence",
    "values.domain.community": "Community/Citizenship",
    "values.domain.community.desc": "Social engagement, volunteering, contributing to society",
    "values.domain.health": "Health/Wellbeing",
    "values.domain.health.desc": "Physical and mental health, life balance",
    "values.domain.environment": "Environment/Nature",
    "values.domain.environment.desc": "Connection to nature, ecology, environmental respect",

    // Quality category examples
    "qualities.interpersonal.ex": "Empathetic,Good listener,Generous,Loyal,Kind",
    "qualities.intellectual.ex": "Curious,Creative,Analytical,Insightful,Thoughtful",
    "qualities.emotional.ex": "Calm,Resilient,Optimistic,Patient,Sensitive",
    "qualities.moral.ex": "Honest,Principled,Fair,Responsible,Humble",
    "qualities.practical.ex": "Organized,Reliable,Persevering,Resourceful,Efficient",

    // Capability area examples
    "capabilities.professional.ex": "Project management,Communication,Leadership,Problem solving",
    "capabilities.social.ex": "Active listening,Negotiation,Teamwork,Networking",
    "capabilities.personal.ex": "Time management,Adaptability,Learning,Organization",
    "capabilities.creative.ex": "Innovation,Design,Writing,Artistic",
    "capabilities.physicalSkills.ex": "Sports,Dance,DIY,Cooking",

    // Reward ideas
    "reinforcement.reward1": "Take a relaxation break",
    "reinforcement.reward2": "Listen to my favorite music",
    "reinforcement.reward3": "Go for a walk",
    "reinforcement.reward4": "Call a friend",
    "reinforcement.reward5": "Have a nice meal",
    "reinforcement.reward6": "Watch an episode of my show",
    "reinforcement.reward7": "Read a few pages of a book",
    "reinforcement.reward8": "Do a creative activity",
    "reinforcement.describeAccomplishment": "Describe your accomplishment",
    "reinforcement.reflectionPlaceholder": "What did you learn about yourself?",

    // Pomodoro extras
    "pomodoro.min": "min",

    // Cognitive Restructuring Page
    "exercise.cognitive_restructuring": "Cognitive Restructuring",
    "exercise.cognitive_restructuring.desc": "Identify and modify your thought patterns",
    "cogRestructuring.title": "Cognitive Restructuring",
    "cogRestructuring.pageSubtitle": "Identify your cognitive distortions and develop more balanced alternative thoughts",
    "cogRestructuring.newEntry": "New restructuring",
    "cogRestructuring.history": "History",
    "cogRestructuring.step.situation": "Situation",
    "cogRestructuring.step.situationDesc": "Describe the situation that triggered your negative thoughts",
    "cogRestructuring.step.thought": "Automatic Thought",
    "cogRestructuring.step.thoughtDesc": "What thought came to you spontaneously?",
    "cogRestructuring.step.emotions": "Emotions Felt",
    "cogRestructuring.step.emotionsDesc": "Identify the emotions associated with this thought",
    "cogRestructuring.step.distortions": "Cognitive Distortions",
    "cogRestructuring.step.distortionsDesc": "Identify the reasoning biases present in your thought",
    "cogRestructuring.step.evidence": "Examining Evidence",
    "cogRestructuring.step.evidenceDesc": "Objectively analyze the evidence for and against your thought",
    "cogRestructuring.step.alternative": "Alternative Thought",
    "cogRestructuring.step.alternativeDesc": "Formulate a more balanced and realistic thought",
    "cogRestructuring.situationPlaceholder": "E.g.: During a meeting, my colleague didn't reply to my message...",
    "cogRestructuring.thoughtPlaceholder": "E.g.: He's ignoring me, nobody respects me...",
    "cogRestructuring.emotionIntensityBefore": "Emotional intensity before restructuring",
    "cogRestructuring.emotionIntensityAfter": "Emotional intensity after restructuring",
    "cogRestructuring.evidenceFor": "Evidence supporting this thought",
    "cogRestructuring.evidenceForPlaceholder": "What objective facts support this thought?",
    "cogRestructuring.evidenceAgainst": "Evidence against this thought",
    "cogRestructuring.evidenceAgainstPlaceholder": "What objective facts contradict this thought?",
    "cogRestructuring.alternativePlaceholder": "E.g.: He might be busy, I don't have enough information to conclude...",
    "cogRestructuring.actionPlan": "Action Plan",
    "cogRestructuring.actionPlanPlaceholder": "What can you concretely do following this reflection?",
    "cogRestructuring.saved": "Cognitive restructuring saved!",
    "cogRestructuring.fillRequired": "Please fill in the situation and automatic thought",
    "cogRestructuring.noEntries": "No entries yet",
    "cogRestructuring.startRestructuring": "Start by identifying a negative automatic thought",
    "cogRestructuring.low": "Low",
    "cogRestructuring.high": "Intense",
    // Cognitive Distortions
    "distortion.allOrNothing": "All-or-Nothing Thinking",
    "distortion.allOrNothing.desc": "Seeing things in black or white with no middle ground",
    "distortion.catastrophizing": "Catastrophizing",
    "distortion.catastrophizing.desc": "Imagining the worst possible scenario",
    "distortion.overgeneralization": "Overgeneralization",
    "distortion.overgeneralization.desc": "Drawing a general conclusion from a single event",
    "distortion.mindReading": "Mind Reading",
    "distortion.mindReading.desc": "Assuming what others think without evidence",
    "distortion.fortuneTelling": "Fortune Telling",
    "distortion.fortuneTelling.desc": "Negatively predicting the future without evidence",
    "distortion.magnification": "Magnification / Minimization",
    "distortion.magnification.desc": "Exaggerating negatives or minimizing positives",
    "distortion.emotionalReasoning": "Emotional Reasoning",
    "distortion.emotionalReasoning.desc": "Believing that feelings reflect reality",
    "distortion.shouldStatements": "Should Statements",
    "distortion.shouldStatements.desc": "Imposing rigid and unrealistic rules on yourself",
    "distortion.labeling": "Labeling",
    "distortion.labeling.desc": "Defining yourself by a single negative event",
    "distortion.personalization": "Personalization",
    "distortion.personalization.desc": "Feeling responsible for things outside your control",

    // Patient progress extras
    "progress.averageMoodValue": "{value}/10",
    "progress.noReports": "No session reports for this patient",
    "progress.noReportsHint": "Generate a session report from the Reports tab",
    "progress.sessionReportsTitle": "Session Reports",

    // Session Report fields
    "cr.agenda_objectifs": "Agenda / Objectives",
    "cr.ameliorations_progres": "Improvements / Progress",
    "cr.analyse_chaine": "Chain Analysis",
    "cr.analyse_chaine_abc": "ABC Analysis",
    "cr.analyse_chaine_sorc": "SORC Analysis",
    "cr.analyse_tcc": "CBT Analysis",
    "cr.anamnese": "Anamnesis",
    "cr.echelles_administrees": "Scales Administered",
    "cr.editHint": "All filled fields are editable above.",
    "cr.evaluation_dsm5": "DSM-5 Assessment",
    "cr.evaluations_standardisees": "Standardized Assessments",
    "cr.exercices": "Exercises",
    "cr.experiences_comportementales": "Behavioral Experiments",
    "cr.fields": "fields",
    "cr.humeur_symptomes": "Mood / Symptoms",
    "cr.hypothese_clinique_generale": "General Clinical Hypothesis",
    "cr.hypothese_diagnostic_principal": "Primary Diagnosis",
    "cr.hypotheses_diagnostics_differentiels": "Differential Diagnoses",
    "cr.informations_generales": "General Information",
    "cr.initial": "Initial Interview",
    "cr.interventions": "Interventions",
    "cr.notes_libres": "Free Notes",
    "cr.nouveaux_exercices": "New Exercises",
    "cr.objectifs_smart": "SMART Goals",
    "cr.objectifs_therapeutiques": "Therapeutic Goals",
    "cr.pensees_automatiques": "Automatic Thoughts",
    "cr.plan_prochaine_seance": "Next Session Plan",
    "cr.problematiques": "Issues",
    "cr.processus_cibles": "Target Processes",
    "cr.protocole_therapeutique": "Therapeutic Protocol",
    "cr.recommandations_suivi": "Follow-up Recommendations",
    "cr.revue_exercices": "Exercise Review",
    "cr.revue_semaine": "Week Review",
    "cr.saveError": "Error saving report",
    "cr.saved": "Report saved",
    "cr.situation_pensees_emotions": "Situation-Thoughts-Emotions",
    "cr.strategies_therapeutiques": "Therapeutic Strategies",
    "cr.suivi": "Follow-up Session",

    // Pomodoro
    "pomodoro.addTask": "Add task",
    "pomodoro.allDone": "Day complete!",
    "pomodoro.dayPlannerSubtitle": "Plan and execute your pomodori for the day",
    "pomodoro.deferTask": "Defer",
    "pomodoro.deferred": "Deferred",
    "pomodoro.effectiveWork": "of effective work",
    "pomodoro.longBreak": "Long break (min)",
    "pomodoro.longBreakHint": "Long break triggers automatically after 4 pomodori",
    "pomodoro.longBreakLabel": "Long break",
    "pomodoro.maxReached": "Maximum reached (8h of effective work)",
    "pomodoro.newPlan": "New plan",
    "pomodoro.objectivePh": "Objective: what do you want to accomplish?",
    "pomodoro.planCreated": "Plan created!",
    "pomodoro.planTasks": "Tasks for the day",
    "pomodoro.resume": "Resume",
    "pomodoro.reviewPh": "What did you accomplish? How did it go?",
    "pomodoro.reviewTitle": "Pomodoro review",
    "pomodoro.settings": "Settings",
    "pomodoro.shortBreak": "Short break (min)",
    "pomodoro.skipBreak": "Skip break",
    "pomodoro.startBreak": "Start break",
    "pomodoro.startDay": "Start the day",
    "pomodoro.submitReview": "Submit and continue",
    "pomodoro.taskDeferred": "Task deferred",
    "pomodoro.taskList": "Task list",
    "pomodoro.workDuration": "Work (min)",

  },
  es: esTranslations,
  ca: caTranslations,
  it: itTranslations,
  pt: ptTranslations,
  nl: nlTranslations,
};

export { translations };

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => {
    const saved = localStorage.getItem("app_locale");
    return LANGUAGES.some((l) => l.id === saved) ? saved : "fr";
  });
  const setLocale = (next) => { if (LANGUAGES.some((l) => l.id === next)) setLocaleState(next); };

  useEffect(() => {
    localStorage.setItem("app_locale", locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const t = (key, paramsOrDefault = {}) => {
    if (!key) {
      return typeof paramsOrDefault === 'string' ? paramsOrDefault : '';
    }
    let text = translations[locale]?.[key] || translations["en"]?.[key] || translations["fr"]?.[key];
    if (!text) {
      return typeof paramsOrDefault === 'string' ? paramsOrDefault : key;
    }
    if (typeof paramsOrDefault === 'object' && paramsOrDefault !== null) {
      Object.entries(paramsOrDefault).forEach(([k, v]) => {
        text = text.replace(`{${k}}`, v);
      });
    }
    return text;
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used within I18nProvider");
  return context;
};
